import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Button,
  Modal,
  TextContainer,
  TextField,
  Stack,
  ResourceList,
  Card,
  ResourceItem,
  ButtonGroup,
  Select,
  FormLayout,
  Banner,
  Checkbox,
  ProgressBar,
  Popover,
  ActionList,
  Heading,
  Subheading,
  TextStyle,
  EmptyState,
  Icon,
  List,
  Collapsible,
  Tooltip,
  Badge,
  Text,
  RadioButton,
  ChoiceList
} from '@shopify/polaris';
import { DeleteMajor, EditMajor, SearchMinor, DuplicateMinor, QuestionMarkMajor, QuestionMarkMinor } from '@shopify/polaris-icons';
import { useTemplateContext } from '../../contexts/TemplateContext';
import { useAuth } from '../../contexts/AuthContext';
import { useShop } from '../../contexts/ShopContext';
import { useTemplate } from '../../contexts/TemplateContext';
import { PersistentResultsSidebar } from '../templates/PersistentResultsSidebar';
import { recordTemplateUsage } from '../../services/metricsService';
import { hasEnoughCredits } from '../../services/creditService';
import { TemplateSelector, TemplateHelp, WorkflowList, TemplateEditor, TemplateList, ImageTemplateExecutor, TemplateVariablesHelp } from '../templates';
import { getAvailableAIModels, getAIModelById } from '../../services/aiModelService';
import { processWithAIProvider } from '../../services/aiProviderService';
import { getOpenAIApiKey } from '../../services/apiKeyService';
import { getTemplateById, saveTemplateOrder, getTemplateOrder } from '../../services/templateService';
import { WorkflowServiceV2 } from '../../services/workflowServiceV2';
import { resolveImagePlaceholders } from '../../services/imagePlaceholderService';
import { compressImageForAnalysis } from '../../services/imageCompressionService';

export default function TemplateTools({ onSaveTemplate, onTemplateUpdate, onLoadTemplate, disabled, selectedProducts, handleProductChange, handleOptionValueChange, handleVariantChange, metafieldChangeHandler, onExecutionStatusChange, onExecutionError }) {

  // Helper function to normalize template progress messages
  const normalizeTemplateMessage = (message, index = null, total = null) => {
    // Get the appropriate values to use, with fallbacks
    const safeCurrentIndex = index !== null ? index : currentTemplateIndex;
    // Use provided total, then currentWorkflowTemplateCount if it's set, or fall back to selectedTemplates length
    const safeTotalCount = total !== null ? total :
      (currentWorkflowTemplateCount > 0 ? currentWorkflowTemplateCount : selectedTemplates.length);

    if (!message) {
      return `Template ${safeCurrentIndex + 1}/${safeTotalCount}: Processing...`;
    }

    // Don't normalize messages if this is a workflow progress message from the workflow service
    // We want to preserve the original template numbers in these cases
    if (message.includes('Processing template') && message.includes(' of ')) {
      console.log(`Message from workflow service, preserving original template numbers: "${message}"`);
      return message;
    }

    // Also preserve Product X/Y - Template Z/W messages from workflow context
    // Use case-insensitive regex to match both 'Template' and 'template' and with/without colon
    if (message.match(/Product \d+\/\d+ - [Tt]emplate \d+\/\d+:?/)) {
      console.log(`Product-template message from workflow context, preserving numbers: "${message}"`);
      return message;
    }

    // Otherwise, replace any existing template counting with our local state values (multiple patterns)
    let normalizedMessage = message
      // Pattern 1: "Template 1/3:"
      .replace(
        /Template (\d+)\/(\d+):/gi,
        `Template ${safeCurrentIndex + 1}/${safeTotalCount}:`
      )
      // Pattern 2: "template 1/3"
      .replace(
        /template (\d+)\/(\d+)/gi,
        `template ${safeCurrentIndex + 1}/${safeTotalCount}`
      );

    console.log(`Message normalization: "${message}" → "${normalizedMessage}" (index=${safeCurrentIndex}, total=${safeTotalCount})`);
    return normalizedMessage;
  };

  // Template type options - now supporting both Product and Image Analyzing templates
  const templateTypes = [
    { label: 'Product', value: 'product' },
    { label: 'Image Analyzing', value: 'image' },
    { label: 'Tool Execution', value: 'tool' }
    // Variant type temporarily disabled
    // { label: 'Variant', value: 'variant' }
  ];

  const [isOpen, setIsOpen] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showCreateWorkflowModal, setShowCreateWorkflowModal] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [showTemplateVariablesHelp, setShowTemplateVariablesHelp] = useState(false);
  const [error, setError] = useState('');
  const [originalName, setOriginalName] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [workflowSearchValue, setWorkflowSearchValue] = useState('');
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [isExecuting, setIsExecuting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [skuPrefix, setSkuPrefix] = useState('');
  const [skuSuffix, setSkuSuffix] = useState('');
  const [handlePrefix, setHandlePrefix] = useState('');
  const [handleSuffix, setHandleSuffix] = useState('');
  const [workflowName, setWorkflowName] = useState('');
  const [workflowExecutionStatus, setWorkflowExecutionStatus] = useState(null);
  const [currentWorkflowTemplateCount, setCurrentWorkflowTemplateCount] = useState(0);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [editingTemplate, setEditingTemplate] = useState({
    id: '',
    name: '',
    type: 'product',
    prompt: '',
    target: 'description',
    metafield: {
      namespace: '',
      key: ''
    },
    userId: '',
    inputFields: [],
    outputFields: [],
    inputMetafields: [],
    outputMetafields: [],
    modelId: '',
    namePoolId: ''
  });
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
  const [showDeleteWorkflowModal, setShowDeleteWorkflowModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [updatedProducts, setUpdatedProducts] = useState({});
  const [availableAIModels, setAvailableAIModels] = useState([]);
  const [loadingAIModels, setLoadingAIModels] = useState(false);
  const [creditCost, setCreditCost] = useState(0);
  const [availableNamePools, setAvailableNamePools] = useState([]);

  // State for AI Model Help modal
  const [showAIModelHelpModal, setShowAIModelHelpModal] = useState(false);

  // State for Credits Error modal
  const [showCreditsErrorModal, setShowCreditsErrorModal] = useState(false);
  const [creditsErrorDetails, setCreditsErrorDetails] = useState(null);

  // State for Stop Markers (Debug Mode)
  const [stopMarkers, setStopMarkers] = useState([]);
  const [currentExecutionId, setCurrentExecutionId] = useState(null);
  const [isPaused, setIsPaused] = useState(false);

  const { user } = useAuth();
  const { currentShop } = useShop();
  const {
    templates,
    workflows,
    loading,
    addTemplate,
    updateTemplate,
    deleteTemplate,
    addWorkflow,
    deleteWorkflow,
    updateWorkflow,
    executeTemplates,
    executeTemplatesDirectly,
    executeWorkflow,
    openaiSettings,
    getCurrentProductOptions,
    productEdits,
    editHistory,
    products
  } = useTemplateContext();

  useEffect(() => {
    const loadAIModels = async () => {
      setLoadingAIModels(true);
      try {
        const models = await getAvailableAIModels();
        setAvailableAIModels(models);
      } catch (err) {
        console.error('Error loading AI models:', err);
      } finally {
        setLoadingAIModels(false);
      }
    };

    const loadNamePools = async () => {
      if (user?.uid) {
        try {
          const { getNamePoolsByUserId } = await import('../../services/namePoolService');
          const pools = await getNamePoolsByUserId(user.uid);
          setAvailableNamePools(pools);
        } catch (err) {
          console.error('Error loading name pools:', err);
        }
      }
    };

    loadAIModels();
    loadNamePools();
  }, [user?.uid]);

  // Ensure a default selection for image templates
  useEffect(() => {
    if (editingTemplate.type === 'image') {
      // If no option is selected (neither body_html in outputFields nor sidebarOnly is true)
      if (!editingTemplate.outputFields?.includes('body_html') && !editingTemplate.sidebarOnly) {
        console.log('Setting default output mode for image template to Sidebar Only');
        setEditingTemplate(prev => ({
          ...prev,
          sidebarOnly: true,
          outputFields: []
        }));
      }
    }
  }, [editingTemplate.type, editingTemplate.outputFields, editingTemplate.sidebarOnly]);

  // Initialize prefix and suffix values when editing a template - only once when the template ID changes
  useEffect(() => {
    if (editingTemplate.id) {
      if (editingTemplate.prompt && editingTemplate.prompt.startsWith('tool:generate_skus')) {
        console.log('Raw prompt:', editingTemplate.prompt);
        const promptParams = editingTemplate.prompt.split('|').slice(1).join('|');
        console.log('Prompt params:', promptParams);

        const prefixMatch = promptParams.match(/prefix=([^|]*)/);
        const suffixMatch = promptParams.match(/suffix=([^|]*)/);

        console.log('Regex matches:', { prefixMatch, suffixMatch });

        const prefix = prefixMatch ? prefixMatch[1] : '';
        const suffix = suffixMatch ? suffixMatch[1] : '';

        console.log('Extracted values:', { prefix, suffix });

        setSkuPrefix(prefix);
        setSkuSuffix(suffix);

        // Make sure the template prompt includes both prefix and suffix
        if (suffix && !editingTemplate.prompt.includes('suffix=')) {
          console.log('Fixing template prompt to include suffix');
          setEditingTemplate(prev => ({
            ...prev,
            prompt: `tool:generate_skus|prefix=${prefix}|suffix=${suffix}`
          }));
        }

        console.log('Initialized SKU prefix and suffix once:', { prefix, suffix });
      }

      if (editingTemplate.prompt && editingTemplate.prompt.startsWith('tool:handle_prefix_suffix')) {
        console.log('Raw handle prompt:', editingTemplate.prompt);
        const promptParams = editingTemplate.prompt.split('|').slice(1).join('|');
        console.log('Handle prompt params:', promptParams);

        const prefixMatch = promptParams.match(/prefix=([^|]*)/);
        const suffixMatch = promptParams.match(/suffix=([^|]*)/);

        console.log('Handle regex matches:', { prefixMatch, suffixMatch });

        const prefix = prefixMatch ? prefixMatch[1] : '';
        const suffix = suffixMatch ? suffixMatch[1] : '';

        console.log('Extracted handle values:', { prefix, suffix });

        setHandlePrefix(prefix);
        setHandleSuffix(suffix);

        // Make sure the template prompt includes both prefix and suffix
        if (suffix && !editingTemplate.prompt.includes('suffix=')) {
          console.log('Fixing handle template prompt to include suffix');
          setEditingTemplate(prev => ({
            ...prev,
            prompt: `tool:handle_prefix_suffix|prefix=${prefix}|suffix=${suffix}`
          }));
        }

        console.log('Initialized handle prefix and suffix once:', { prefix, suffix });
      }
    }
  }, [editingTemplate.id]); // Only run when template ID changes

  // Add event listeners for external control
  useEffect(() => {
    const handleOpenTemplateManager = () => {
      setIsOpen(true);
    };

    const handleOpenWorkflowManager = () => {
      // Load template order from Firebase before opening the modal
      loadTemplateOrder();
      setShowWorkflowModal(true);
    };

    // Add event listeners if the component has an ID
    const element = document.getElementById('template-tools-ref');
    if (element) {
      element.addEventListener('open-template-manager', handleOpenTemplateManager);
      element.addEventListener('open-workflow-manager', handleOpenWorkflowManager);

      return () => {
        element.removeEventListener('open-template-manager', handleOpenTemplateManager);
        element.removeEventListener('open-workflow-manager', handleOpenWorkflowManager);
      };
    }
  }, []);

  // Notify parent component about execution status changes
  useEffect(() => {
    if (onExecutionStatusChange) {
      onExecutionStatusChange(isExecuting, workflowExecutionStatus);
    }
  }, [isExecuting, workflowExecutionStatus, onExecutionStatusChange]);

  const targetOptions = [
    { label: 'Product Description', value: 'description' },
    { label: 'Product Title', value: 'title' },
    { label: 'Metafield', value: 'metafield' }
  ];

  const examplePrompts = [
    {
      label: 'Select an example prompt',
      value: ''
    },
    {
      label: 'Title Optimizer',
      value: 'Generate a compelling and SEO-optimized title for our product "{title}", ensuring it incorporates "{metafield.seo.keywords}" and is structured to rank effectively on Google and Amazon search platforms.\n\n' +
        'Instructions:\n\n' +
        'To use this prompt, the following placeholders should be replaced dynamically with the actual product information:\n\n' +
        '- {title} → The existing product title.\n' +
        '- {description} → A short summary of the product to understand its core features.\n' +
        '- {metafield.seo.keywords} → The relevant SEO keywords stored in the metafield that should be included for better ranking.\n\n' +
        'Example Prompt in Action:\n' +
        'Generate an SEO-friendly title for our "{title}" using the following information:\n\n' +
        '- Product Details: {description}\n' +
        '- Target Keywords: {metafield.seo.keywords}\n\n' +
        'The final title should be clear, attractive, and structured for maximum visibility on Google and Amazon.'
    },
    {
      label: 'Description Optimizer',
      value: 'Craft a compelling product summary for "{title}" that is engaging, SEO-optimized, and structured for Shopify compatibility. Incorporate relevant details from the existing description and enhance it with target keywords from SEO metafields.\n\n' +
        'We structure the description like this:\n\n' +
        '1. {title}\n' +
        '2. Overview of the product: {description}\n' +
        '3. Key Features\n' +
        '- {metafield.seo.keywords} (incorporate naturally within the content)\n' +
        '- Highlight distinctive attributes and benefits.\n' +
        '- Emphasize why it stands out from competitors.\n\n' +
        '4. Reason to choose this product: Explain why this product is a must-have, using persuasive language that drives conversions.\n\n' +
        '### Important Guidelines:\n' +
        '- Use bold text for important words with `<strong>text</strong>` in HTML.\n' +
        '- Structure the description using proper HTML headers (`<h2>`, `<h3>`, etc.) for Shopify compatibility.\n' +
        '- Do not use `*` or `**` for bold text. Always use HTML formatting (`<strong></strong>`).\n' +
        '- Do not include technical specifications—only descriptive and persuasive content.\n' +
        '- Convert sizes correctly for the U.S. market where applicable.\n\n' +
        'Ensure the final output is engaging, structured, and optimized for both user readability and search engine rankings.'
    },
    {
      label: 'Product Specifications Extractor',
      value: 'Extract only the relevant product specifications from the description of "{title}". Do not include any additional text, headlines, or marketing content.\n\n' +
        '### Specification Formatting Guidelines:\n' +
        '- Only include key product details such as material, dimensions, weight, care instructions, and package contents.\n' +
        '- Format each specification with a **bold label** followed by the information (e.g., `<strong>Material:</strong> 100% Cotton`).\n' +
        '- Use **US units first**, followed by metric units in parentheses where applicable (e.g., `<strong>Dimensions:</strong> 12 x 8 x 6 inches (30.5 x 20.3 x 15.2 cm)`).\n' +
        '- Separate each specification with a `<br>` tag to maintain readability.\n\n' +
        '### Unwanted Words or Sections to Prevent:\n' +
        'Do not include or reference any of the following in the specifications:\n' +
        '- Shipping (e.g., “Free shipping included”)\n' +
        '- Payment (e.g., “Payment details available on request”)\n' +
        '- Warranty (e.g., “Includes a 2-year warranty”)\n' +
        '- Dropshipping\n' +
        '- China, Hong Kong\n' +
        '- Free (e.g., “Free returns”)\n' +
        '- Customer service (e.g., “Contact our customer service for more details”)\n' +
        '- Return (e.g., “Eligible for returns within 30 days”)\n' +
        '- Contact (e.g., “Contact us for further details”)\n\n' +
        'Use the following product details as the source for extracting specifications:\n' +
        '- **Product Description:** {description}\n' +
        '- **SEO Keywords for Context:** {metafield.seo.keywords}\n\n' +
        'Return only the cleaned and formatted product specifications as structured HTML output.'
    },
    {
      label: 'SEO Description Generator',
      value: 'Generate an SEO-optimized product description using the following information: {body_html}. The description should be compelling, highlight key features, and include relevant keywords.'
    },
    {
      label: 'Clothing Image Analyze',
      value: 'Analyze the clothing item shown in the image and create a detailed product description based on visible attributes only.\n\n### Key Attributes to Identify:\n- **Garment Type:** Identify the specific type (e.g., hoodie, dress, jacket, t-shirt)\n- **Material/Texture:** Describe visible fabric qualities (e.g., denim, knit, cotton, silk)\n- **Fit & Cut:** Note the silhouette and fit (e.g., slim fit, oversized, cropped, relaxed)\n- **Design Features:** List all visible elements (e.g., buttons, zippers, hood, pockets, collar type)\n- **Sleeve & Neck Style:** Specify the style (e.g., v-neck, crew neck, turtleneck, long sleeve)\n- **Pattern/Graphics:** Describe any visible designs (e.g., stripes, floral, minimalist print)\n- **Gender Target:** Only if visually clear (e.g., menswear, womenswear, unisex)\n\n### Selling Points:\nSuggest 2-3 key benefits or selling points based on the visible style and features (e.g., "perfect for layering," "versatile everyday piece," "eye-catching statement item").\n\n### Important Guidelines:\n- Focus ONLY on what is clearly visible in the image\n- Do NOT speculate about non-visible features\n- Do NOT mention specific brand names\n- Format output as structured content with clear sections\n- For image templates, respond with valid JSON format matching the requested output fields'
    }
  ];

  // State for template ordering
  const [templateOrder, setTemplateOrder] = useState({});
  const [isOrderLoaded, setIsOrderLoaded] = useState(false);
  // State for ordered templates that will be passed to TemplateList
  const [orderedTemplates, setOrderedTemplates] = useState([]);

  // CRITICAL: Credits error handler function
  const handleCreditsError = (errorResult) => {
    if (errorResult?.error === 'INSUFFICIENT_CREDITS' && errorResult?.details) {
      console.log('🚫 Credits error detected:', errorResult.details);

      // Set the error details for the modal
      setCreditsErrorDetails(errorResult.details);

      // Show the credits error modal
      setShowCreditsErrorModal(true);

      // Reset execution state
      setIsExecuting(false);
      setProgress(0);
      setWorkflowExecutionStatus(null);

      // Clear any existing error messages since we're showing the modal
      setError('');

      return true; // Indicates that this was a credits error
    }
    return false; // Not a credits error
  };

  // CRITICAL: Wrapper function for safe template execution with credits check
  const executeTemplatesSafely = async (templateIds, products, onProgress = null) => {
    try {
      console.log('🔒 Safe template execution started:', { templateIds, productCount: products?.length });

      const result = await executeTemplates(templateIds, products, onProgress);

      // Check if result contains a credits error
      if (result?.error === 'INSUFFICIENT_CREDITS') {
        handleCreditsError(result);
        return null; // Don't proceed with execution
      }

      if (result?.error && result.error !== 'INSUFFICIENT_CREDITS') {
        console.error('Template execution error:', result.error);
        setError(result.error);
        setIsExecuting(false);
        setWorkflowExecutionStatus(null);

        // Call error callback if provided
        if (onExecutionError) {
          onExecutionError({
            message: result.error,
            errors: [{ template: 'Template Execution', message: result.error }],
            successCount: 0,
            totalCount: selectedTemplates.length
          });
        }

        return null;
      }

      console.log('✅ Safe template execution completed successfully');
      return result;

    } catch (error) {
      console.error('Safe template execution failed:', error);
      setError(error.message || 'Template execution failed');
      setIsExecuting(false);
      setWorkflowExecutionStatus(null);

      // Call error callback if provided
      if (onExecutionError) {
        onExecutionError({
          message: error.message || 'Template execution failed',
          errors: [{ template: 'Template Execution', message: error.message || 'Unknown error' }],
          successCount: 0,
          totalCount: selectedTemplates.length
        });
      }

      return null;
    }
  };

  // CRITICAL: Wrapper function for safe direct template execution with credits check
  const executeTemplatesDirectlySafely = async (templateIds, products, onProgress = null) => {
    try {
      console.log('🔒 Safe direct template execution started:', { templateIds, productCount: products?.length });

      const result = await executeTemplatesDirectly(templateIds, products, onProgress);

      // Check if result contains a credits error
      if (result?.error === 'INSUFFICIENT_CREDITS') {
        handleCreditsError(result);
        return null; // Don't proceed with execution
      }

      if (result?.error && result.error !== 'INSUFFICIENT_CREDITS') {
        console.error('Direct template execution error:', result.error);
        setError(result.error);
        setIsExecuting(false);
        setWorkflowExecutionStatus(null);
        return null;
      }

      console.log('✅ Safe direct template execution completed successfully');
      return result;

    } catch (error) {
      console.error('Safe direct template execution failed:', error);
      setError(error.message || 'Direct template execution failed');
      setIsExecuting(false);
      setWorkflowExecutionStatus(null);
      return null;
    }
  };

  // CRITICAL: Wrapper function for safe workflow execution with credits check
  const executeWorkflowSafely = async (workflow, products, onProgress = null) => {
    try {
      console.log('🔒 Safe workflow execution started:', { workflowId: workflow?.id, productCount: products?.length });

      const result = await executeWorkflow(workflow, products, onProgress);

      // Check if result contains a credits error
      if (result?.error === 'INSUFFICIENT_CREDITS') {
        handleCreditsError(result);
        return null; // Don't proceed with execution
      }

      if (result?.error && result.error !== 'INSUFFICIENT_CREDITS') {
        console.error('Workflow execution error:', result.error);
        setError(result.error);
        setIsExecuting(false);
        setWorkflowExecutionStatus(null);

        // Call error callback if provided
        if (onExecutionError) {
          onExecutionError({
            message: result.error,
            errors: [{ template: 'Workflow Execution', message: result.error }],
            successCount: 0,
            totalCount: selectedTemplates.length
          });
        }

        return null;
      }

      console.log('✅ Safe workflow execution completed successfully');
      return result;

    } catch (error) {
      console.error('Safe workflow execution failed:', error);
      setError(error.message || 'Workflow execution failed');
      setIsExecuting(false);
      setWorkflowExecutionStatus(null);

      // Call error callback if provided
      if (onExecutionError) {
        onExecutionError({
          message: error.message || 'Workflow execution failed',
          errors: [{ template: 'Workflow Execution', message: error.message || 'Unknown error' }],
          successCount: 0,
          totalCount: selectedTemplates.length
        });
      }

      return null;
    }
  };

  // Update ordered templates whenever templates or template order changes
  useEffect(() => {
    if (templates.length > 0 && isOrderLoaded) {
      // Create a copy of templates that we can sort
      const templatesWithOrder = templates.map(template => ({
        ...template,
        // Assign an order value from templateOrder or a high default
        orderValue: templateOrder[template.id] !== undefined ?
          Number(templateOrder[template.id]) : 999999
      }));

      // Sort by the order value
      const sorted = [...templatesWithOrder].sort((a, b) => a.orderValue - b.orderValue);

      // Remove the temporary orderValue property
      const cleanSorted = sorted.map(({ orderValue, ...rest }) => rest);

      // Update the ordered templates state
      setOrderedTemplates(cleanSorted);
    } else {
      // If no order is loaded yet, just use templates as is
      setOrderedTemplates(templates);
    }
  }, [templates, templateOrder, isOrderLoaded]);

  // Function to load template order from Firestore
  const loadTemplateOrder = useCallback(async () => {
    if (user?.uid && templates.length > 0) {
      try {
        // Load template order from Firestore
        const savedOrder = await getTemplateOrder(user.uid);

        // If there's a saved order, use it
        if (savedOrder && Object.keys(savedOrder).length > 0) {
          console.log('Loaded template order from Firestore:', savedOrder);
          setTemplateOrder(savedOrder);
        } else {
          // Otherwise, create an initial order based on current templates
          const initialOrder = {};
          templates.forEach((template, index) => {
            initialOrder[template.id] = index;
          });
          setTemplateOrder(initialOrder);
          console.log('Created initial template order:', initialOrder);
        }
        setIsOrderLoaded(true);
      } catch (error) {
        console.error('Error loading template order:', error);
        // Fall back to default order
        const initialOrder = {};
        templates.forEach((template, index) => {
          initialOrder[template.id] = index;
        });
        setTemplateOrder(initialOrder);
        setIsOrderLoaded(true);
      }
    }
  }, [user, templates, setTemplateOrder, setIsOrderLoaded]);

  // Load template order from Firestore when user or templates change
  useEffect(() => {
    if (!isOrderLoaded) {
      loadTemplateOrder();
    }
  }, [user, templates, isOrderLoaded, loadTemplateOrder]);

  // Add any new templates to the order that might not be there
  useEffect(() => {
    if (isOrderLoaded && templates.length > 0) {
      const updatedOrder = { ...templateOrder };
      let hasChanges = false;

      templates.forEach((template, index) => {
        if (updatedOrder[template.id] === undefined) {
          // Assign a high index to new templates so they appear at the end
          updatedOrder[template.id] = 1000 + index;
          hasChanges = true;
        }
      });

      if (hasChanges) {
        setTemplateOrder(updatedOrder);
      }
    }
  }, [templates, templateOrder, isOrderLoaded]);

  // Filter templates based on search
  const filteredTemplates = useMemo(() => {
    if (!orderedTemplates || orderedTemplates.length === 0) return [];

    // Filter by search term if one exists
    if (searchValue) {
      const lowerSearchValue = searchValue.toLowerCase();
      return orderedTemplates.filter(template =>
        template.name.toLowerCase().includes(lowerSearchValue)
      );
    }

    // If no search term, return all ordered templates
    return orderedTemplates;
  }, [orderedTemplates, searchValue]);

  const filteredWorkflows = useMemo(() => {
    return workflows.filter(workflow =>
      workflow.name.toLowerCase().includes(workflowSearchValue.toLowerCase())
    );
  }, [workflows, workflowSearchValue]);

  const handleTemplateSelect = useCallback((templateId) => {
    setSelectedTemplates(prev => {
      const isSelected = prev.includes(templateId);
      return isSelected
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId];
    });
  }, []);

  // Handle template reordering - receives the fully reordered array from TemplateList
  const handleReorderTemplates = async (reorderedTemplates) => {
    try {
      console.log('handleReorderTemplates called with reordered array:', 
        reorderedTemplates.map((t, i) => `${i}: ${t.name} (${t.id})`));

      if (!reorderedTemplates || reorderedTemplates.length === 0) {
        console.error('No templates to reorder');
        return;
      }

      // Update the ordered templates state immediately for UI responsiveness
      setOrderedTemplates(reorderedTemplates);

      // Create a new order object with spaced values (multiples of 100)
      const newOrder = {};
      reorderedTemplates.forEach((template, index) => {
        newOrder[template.id] = (index + 1) * 100;
      });

      // Update the template order state
      setTemplateOrder(newOrder);

      console.log('New template order:', newOrder);

      // Save the new order to Firebase
      if (user) {
        await saveTemplateOrder(user.uid, newOrder);
        console.log('Template order saved to Firestore');
      }

    } catch (error) {
      console.error('Error reordering templates:', error);
    }
  };

  const handleSaveWorkflow = async () => {
    console.log('🚀 TemplateTools handleSaveWorkflow called with:', {
      workflowName,
      selectedTemplates,
      templateOrder,
      templatesAvailable: templates.length
    });

    if (!workflowName.trim() || selectedTemplates.length === 0) {
      setError('Please provide a workflow name and select at least one template');
      return;
    }

    try {
      console.log('🔍 Starting Image Template validation...');

      // Validate Image Template execution order
      const selectedTemplateObjects = selectedTemplates.map(templateId =>
        templates.find(t => t.id === templateId)
      ).filter(Boolean);

      const imageTemplates = selectedTemplateObjects.filter(t => t.type === 'image');

      if (imageTemplates.length > 0) {
        console.log('📋 Found Image Templates:', imageTemplates.map(t => ({ id: t.id, name: t.name })));

        // Check WORKFLOW ORDER (selectedTemplates array position) - NOT global templateOrder!
        // Check if any image template is not at the beginning of the workflow
        const imageTemplatePositions = imageTemplates.map(imageTemplate => {
          const position = selectedTemplates.findIndex(templateId => templateId === imageTemplate.id);
          return { template: imageTemplate, position };
        });

        const nonFirstImageTemplates = imageTemplatePositions.filter(item => item.position > 0);

        console.log('🔍 WORKFLOW ORDER Validation (Save):', {
          selectedTemplates,
          selectedTemplateObjects: selectedTemplates.map((templateId, index) => {
            const template = templates.find(t => t.id === templateId);
            return template ? {
              name: template.name,
              type: template.type,
              workflowPosition: index + 1
            } : null;
          }).filter(Boolean),
          imageTemplatePositions,
          nonFirstImageTemplates
        });

        if (nonFirstImageTemplates.length > 0) {
          const problemNames = nonFirstImageTemplates.map(item =>
            `${item.template.name} (position ${item.position + 1})`
          ).join(', ');

          setError(
            `⚠️ Workflow Order Issue: Image Templates (${problemNames}) ` +
            `are positioned to execute AFTER other templates in this workflow. Image Templates must be placed ` +
            `at the BEGINNING of the workflow for technical reasons. Please move them to the start of your template selection.`
          );
          return;
        }
      }

      console.log('✅ Image Template validation passed, proceeding with save...');

      const workflowData = {
        name: workflowName.trim(),
        templates: selectedTemplates,
        stopMarkers: stopMarkers // Save stop markers with workflow
      };

      if (editingWorkflow) {
        await updateWorkflow(editingWorkflow.id, workflowData);
      } else {
        await addWorkflow(workflowData);
      }

      setWorkflowName('');
      setSelectedTemplates([]);
      setStopMarkers([]); // Reset stop markers
      setShowCreateWorkflowModal(false);
      setEditingWorkflow(null);
      setError('');
    } catch (err) {
      console.error('Error saving workflow:', err);
      setError(err.message || 'Failed to save workflow');
    }
  };

  const handleCreateWorkflow = async () => {
    // Load template order from Firebase before opening the modal
    await loadTemplateOrder();
    // ... (rest of the code remains the same)

    setEditingWorkflow(null);
    setWorkflowName('');
    setSelectedTemplates([]);
    setStopMarkers([]); // Reset stop markers
    setShowCreateWorkflowModal(true);
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setWorkflowName(workflow.name);
    setSelectedTemplates(workflow.templates || []);
    setStopMarkers(workflow.stopMarkers || []); // Restore stop markers
    setShowCreateWorkflowModal(true);
  };

  // Utility function to strip HTML tags
  const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  const processTemplateResults = useCallback(async (templateResults, executedTemplates = null) => {
    if (!templateResults) return;

    console.log('=== Starting processTemplateResults ===')
    console.log('📋 Template results structure:');
    console.log('- Total products in results:', Object.keys(templateResults || {}).length);
    console.log('- Product IDs in results:', Object.keys(templateResults || {}));
    console.log('Template results:', JSON.stringify(templateResults, null, 2));
    console.log('Executed templates:', executedTemplates);

    // Debug first product specifically
    const firstProductKey = Object.keys(templateResults || {})[0];
    if (firstProductKey) {
      console.log(`🔍 FIRST PRODUCT DEBUG (${firstProductKey}):`, {
        productId: firstProductKey,
        hasData: !!templateResults[firstProductKey],
        dataKeys: templateResults[firstProductKey] ? Object.keys(templateResults[firstProductKey]) : [],
        dataStructure: typeof templateResults[firstProductKey]
      });
    } else {
      console.warn('⚠️ No products found in template results!');
    }

    // Check if this is a SKU template by looking at the template results
    let isSKUTemplate = false;
    console.log('=== SKU Template Detection Debug ===');

    // Simple detection: if the results contain variant_skus or sku fields, it's a SKU template
    Object.values(templateResults || {}).forEach(productResult => {
      if (productResult && typeof productResult === 'object') {
        Object.values(productResult).forEach(templateResult => {
          if (templateResult && typeof templateResult === 'object') {
            // Check for SKU-related fields in the result
            if (templateResult.variant_skus ||
              templateResult.sku !== undefined ||
              (templateResult.variants && Object.values(templateResult.variants).some(v => v.sku !== undefined))) {
              isSKUTemplate = true;
              console.log('✅ SKU template detected from result structure');
            }
          }
        });
      }
    });

    console.log('Final isSKUTemplate result:', isSKUTemplate);

    if (isSKUTemplate) {
      console.log('✅ Detected SKU template execution, will filter non-SKU fields to preserve prices');
    } else {
      console.log('❌ No SKU template detected, will process all fields normally');
    }

    // First, collect all updates for each product
    const productUpdates = {};

    Object.entries(templateResults || {}).forEach(([productId, productResult]) => {
      if (!productResult) return;
      console.log(`\n=== Processing product ${productId} ===`);

      // Get the product
      const product = selectedProducts.find(p => p.id && p.id.toString() === productId);
      if (!product) {
        console.log(`Product ${productId} not found in selected products`);

        // Try to find by index if productId is "unknown"
        if (productId === "unknown" && Object.keys(productResult).length > 0) {
          console.log('Trying to process "unknown" product ID as the first selected product');

          // Use the first product as a fallback
          if (selectedProducts.length > 0) {
            const firstProduct = selectedProducts[0];
            console.log(`Using first product ${firstProduct.id} for unknown product ID`);

            // Process all fields for this product
            Object.entries(productResult).forEach(([field, value]) => {
              if (field !== 'error' && value !== undefined && value !== null) {
                console.log(`Processing field ${field} with value:`, value);
                handleProductChange(firstProduct.id, field, value);
              }
            });
          }
        }
        return;
      }

      // Initialize updates collection for this product
      if (!productUpdates[productId]) {
        productUpdates[productId] = {
          metafields: []
        };
      }

      // Check if productResult is a nested object with templateIds as keys
      const isNestedResult = Object.values(productResult).some(value =>
        value && typeof value === 'object' && !Array.isArray(value)
      );

      // If nested structure, flatten the results first
      let flattenedResult = {};
      if (isNestedResult) {
        console.log('Detected nested template results structure, flattening...');
        // Combine all template results for this product
        // CRITICAL FIX: Only merge fields that have actual values to prevent overwrites
        Object.values(productResult).forEach(templateResult => {
          if (templateResult && typeof templateResult === 'object') {
            // Only merge fields that have meaningful values (not null, undefined, or empty string)
            Object.entries(templateResult).forEach(([key, value]) => {
              if (value !== null && value !== undefined && value !== '') {
                // Only overwrite if we don't already have a value, or if the new value is more substantial
                if (!flattenedResult.hasOwnProperty(key) ||
                  flattenedResult[key] === null ||
                  flattenedResult[key] === undefined ||
                  flattenedResult[key] === '') {
                  flattenedResult[key] = value;
                  console.log(`Merged field ${key} with value:`, typeof value === 'string' ? value.substring(0, 100) + '...' : value);
                } else {
                  console.log(`Skipped overwriting existing field ${key} - keeping existing value`);
                }
              } else {
                console.log(`Skipped empty/null field ${key} to prevent overwriting existing values`);
              }
            });
          }
        });
        console.log('Flattened result:', flattenedResult);
      } else {
        flattenedResult = productResult;
      }

      // Process special 'title' field first to ensure it gets updated
      if (flattenedResult.title !== undefined && flattenedResult.title !== null) {
        console.log(`🏷️ Processing title field for product ${productId}:`, {
          productId: productId,
          newTitle: flattenedResult.title,
          titleLength: flattenedResult.title?.length || 0,
          hasHandleProductChange: typeof handleProductChange === 'function'
        });

        productUpdates[productId].title = flattenedResult.title;

        // Immediately update the product state for UI
        if (handleProductChange) {
          console.log(`📞 Calling handleProductChange for title update: ${productId}`);
          handleProductChange(productId, 'title', flattenedResult.title);
          console.log(`✅ Title update completed for: ${productId}`);
        } else {
          console.warn(`⚠️ handleProductChange not available for title update: ${productId}`);
        }
      } else {
        console.log(`❌ No title field found in results for product ${productId}:`, {
          flattenedResultKeys: Object.keys(flattenedResult),
          titleValue: flattenedResult.title,
          titleType: typeof flattenedResult.title
        });
      }

      // Process special 'alt_text' field to map it to image_alt_text
      if (flattenedResult.alt_text !== undefined && flattenedResult.alt_text !== null) {
        console.log(`Processing alt_text field with value: ${flattenedResult.alt_text}`);
        productUpdates[productId].image_alt_text = flattenedResult.alt_text;

        // Immediately update the product state for UI with the correct field name
        handleProductChange(productId, 'image_alt_text', flattenedResult.alt_text);
      }

      // Process special 'body_html' field to map to descriptionHtml
      // Use the template's insert mode from executionMetadata
      if (flattenedResult.body_html !== undefined && flattenedResult.body_html !== null) {
        let finalHtml = flattenedResult.body_html;

        console.log(`Processing body_html field with raw value:`, finalHtml?.substring(0, 100) + '...');

        // Resolve placeholders {{IMAGE_N}} using actual product images
        const productData = selectedProducts.find(p => p.id === productId);
        if (productData && finalHtml && typeof finalHtml === 'string' && finalHtml.includes('{{IMAGE_')) {
          try {
            console.log(`🖼️ Resolving image placeholders for product ${productId}...`);
            let imageUrls = [];

            // Extract image URLs from product data
            if (productData.images?.edges) {
              imageUrls = productData.images.edges.map(edge => edge.node.url || edge.node.originalSrc).filter(Boolean);
            } else if (Array.isArray(productData.images)) {
              imageUrls = productData.images.map(img => img.src || img.url).filter(Boolean);
            }

            if (imageUrls.length > 0) {
              // Only resolve if we actually found images
              finalHtml = resolveImagePlaceholders(finalHtml, imageUrls, {
                analysisResults: productData._imageAnalysis // Add analysis context if available
              });
              console.log(`✅ Image placeholders resolved successfully`);
            } else {
              console.warn(`⚠️ No images found for product ${productId} - placeholders will remain unresolved`);
            }
          } catch (err) {
            console.error(`❌ Error resolving image placeholders for product ${productId}:`, err);
          }
        }

        // Get the template's insert mode from executionMetadata
        const templateId = flattenedResult.executionMetadata?.templateId;
        let insertMode = 'exchange'; // Default

        if (templateId) {
          // Find the template to get its outputModes
          const template = templates.find(t => t.id === templateId);
          if (template && template.outputModes && template.outputModes.body_html) {
            insertMode = template.outputModes.body_html;
            console.log(`📋 Found template insert mode for body_html: ${insertMode}`);
          }
        }

        productUpdates[productId].descriptionHtml = finalHtml;

        // Update the product state for UI with the CORRECT insert mode
        handleProductChange(productId, 'descriptionHtml', finalHtml, insertMode);
        console.log(`✅ Applied body_html to descriptionHtml with insert mode: ${insertMode}`);

        // Remove from flattenedResult to prevent double processing
        delete flattenedResult.body_html;
      }

      // Process special 'option1_values' field to update option values
      if (flattenedResult.option1_values !== undefined && flattenedResult.option1_values !== null) {
        console.log(`Processing option1_values field with value:`, flattenedResult.option1_values);

        // Parse comma-separated string into array if needed
        let valuesArray;
        if (Array.isArray(flattenedResult.option1_values)) {
          valuesArray = flattenedResult.option1_values;
        } else if (typeof flattenedResult.option1_values === 'string') {
          // Split comma-separated string and trim whitespace
          valuesArray = flattenedResult.option1_values.split(',').map(v => v.trim()).filter(v => v.length > 0);
        } else {
          valuesArray = [flattenedResult.option1_values];
        }

        console.log(`Parsed option1_values into array:`, valuesArray);

        // Update each value individually using the correct field handler
        valuesArray.forEach((value, index) => {
          handleProductChange(productId, `option1_value_${index}`, value);
        });
      }

      // Process special 'option2_values' field to update option values
      if (flattenedResult.option2_values !== undefined && flattenedResult.option2_values !== null) {
        console.log(`Processing option2_values field with value:`, flattenedResult.option2_values);

        // Parse comma-separated string into array if needed
        let valuesArray;
        if (Array.isArray(flattenedResult.option2_values)) {
          valuesArray = flattenedResult.option2_values;
        } else if (typeof flattenedResult.option2_values === 'string') {
          // Split comma-separated string and trim whitespace
          valuesArray = flattenedResult.option2_values.split(',').map(v => v.trim()).filter(v => v.length > 0);
        } else {
          valuesArray = [flattenedResult.option2_values];
        }

        console.log(`Parsed option2_values into array:`, valuesArray);

        // Update each value individually using the correct field handler
        valuesArray.forEach((value, index) => {
          handleProductChange(productId, `option2_value_${index}`, value);
        });
      }

      // Process special 'option3_values' field to update option values
      if (flattenedResult.option3_values !== undefined && flattenedResult.option3_values !== null) {
        console.log(`Processing option3_values field with value:`, flattenedResult.option3_values);

        // Parse comma-separated string into array if needed
        let valuesArray;
        if (Array.isArray(flattenedResult.option3_values)) {
          valuesArray = flattenedResult.option3_values;
        } else if (typeof flattenedResult.option3_values === 'string') {
          // Split comma-separated string and trim whitespace
          valuesArray = flattenedResult.option3_values.split(',').map(v => v.trim()).filter(v => v.length > 0);
        } else {
          valuesArray = [flattenedResult.option3_values];
        }

        console.log(`Parsed option3_values into array:`, valuesArray);

        // Update each value individually using the correct field handler
        valuesArray.forEach((value, index) => {
          handleProductChange(productId, `option3_value_${index}`, value);
        });
      }

      // Process all fields from the flattened result
      Object.entries(flattenedResult).forEach(([field, value]) => {
        if (field === 'title' || field === 'error') return; // Skip title (already processed) and error

        if (field === 'metafields' && typeof value === 'object') {
          // Process metafields object
          console.log('Processing metafields object:', value);

          // Iterate through all keys in the metafields object
          Object.entries(value).forEach(([metafieldKey, metaValue]) => {
            if (metafieldKey.includes('.')) {
              // Handle dot notation keys like "custom.product_specifications"
              const [namespace, key] = metafieldKey.split('.');
              if (metaValue !== undefined && metaValue !== null) {
                console.log(`Processing metafield ${namespace}.${key} with value:`, metaValue);

                // Add to the metafields collection for batch update
                productUpdates[productId].metafields.push({
                  namespace,
                  key,
                  value: metaValue
                });

                // Also update UI immediately
                if (metafieldChangeHandler) {
                  // Use metafieldChangeHandler if available (directly updates the metafield)
                  metafieldChangeHandler(productId, `${namespace}.${key}`, metaValue);
                } else {
                  // Fallback to handleProductChange
                  handleProductChange(productId, `metafields.${namespace}.${key}`, metaValue);
                }
              }
            } else if (typeof metaValue === 'object') {
              // Handle nested structure: { namespace: { key: value } }
              const namespace = metafieldKey;
              Object.entries(metaValue).forEach(([key, nestedValue]) => {
                if (nestedValue !== undefined && nestedValue !== null) {
                  console.log(`Processing metafield ${namespace}.${key} with value:`, nestedValue);

                  // Add to the metafields collection for batch update
                  productUpdates[productId].metafields.push({
                    namespace,
                    key,
                    value: nestedValue
                  });

                  // Also update UI immediately
                  if (metafieldChangeHandler) {
                    // Use metafieldChangeHandler if available
                    metafieldChangeHandler(productId, `${namespace}.${key}`, nestedValue);
                  } else {
                    // Fallback to handleProductChange
                    handleProductChange(productId, `metafields.${namespace}.${key}`, nestedValue);
                  }
                }
              });
            }
          });
        } else if (field.includes('.') && !field.startsWith('variants')) {
          // Handle dot notation for metafields (e.g., "namespace.key")
          const [namespace, key] = field.split('.');
          if (value !== undefined && value !== null) {
            console.log(`Processing dot notation metafield ${namespace}.${key} with value:`, value);

            // Add to the metafields collection for batch update
            productUpdates[productId].metafields.push({
              namespace,
              key,
              value
            });

            // Also update UI immediately
            if (metafieldChangeHandler) {
              // Use metafieldChangeHandler if available
              metafieldChangeHandler(productId, `${namespace}.${key}`, value);
            } else {
              // Fallback to handleProductChange
              handleProductChange(productId, `metafields.${namespace}.${key}`, value);
            }
          }
        } else if (value !== undefined && value !== null) {
          // Process regular fields
          console.log(`Processing field ${field} with value:`, value);

          // If this is a SKU template, only process SKU-related fields
          if (isSKUTemplate) {
            if (field === 'variants' || field.toLowerCase().includes('sku') || field.includes('variants.sku') || field === 'variant_skus') {
              console.log(`SKU template: Including field ${field}`);

              if (field === 'variants' && Array.isArray(value) && handleVariantChange) {
                console.log(`Processing variants array using handleVariantChange`);
                value.forEach(variant => {
                  if (variant && variant.id) {
                    // Normalize variant ID to full GID format for consistency with UI lookups
                    let variantId = variant.id.toString();
                    if (!variantId.startsWith('gid://')) {
                      variantId = `gid://shopify/ProductVariant/${variantId}`;
                    }
                    // Update any returned variant fields (sku, price, compare_at_price, inventory_quantity)
                    Object.keys(variant).forEach(variantField => {
                      if (variantField !== 'id' && variant[variantField] !== undefined) {
                        handleVariantChange(productId, variantId, variantField, variant[variantField]);
                      }
                    });
                  }
                });
              } else if (field === 'variant_skus' && typeof value === 'object' && handleVariantChange) {
                // Legacy support for variant_skus
                console.log(`Processing variant_skus specifically using handleVariantChange`);
                Object.entries(value).forEach(([variantId, variantData]) => {
                  if (variantData && variantData.sku !== undefined) {
                    handleVariantChange(productId, variantId, 'sku', variantData.sku);
                  }
                });
              } else {
                handleProductChange(productId, field, value);
              }
            } else {
              console.log(`SKU template: Skipping non-SKU field ${field} to preserve existing value`);
            }
          } else {
            // Normal template processing - update all fields
            if (field === 'variants' && Array.isArray(value) && handleVariantChange) {
              console.log(`Processing variants array using handleVariantChange`);
              value.forEach(variant => {
                if (variant && variant.id) {
                  // Normalize variant ID to full GID format for consistency with UI lookups
                  let variantId = variant.id.toString();
                  if (!variantId.startsWith('gid://')) {
                    variantId = `gid://shopify/ProductVariant/${variantId}`;
                  }
                  // Update any returned variant fields
                  Object.keys(variant).forEach(variantField => {
                    if (variantField !== 'id' && variant[variantField] !== undefined) {
                      handleVariantChange(productId, variantId, variantField, variant[variantField]);
                    }
                  });
                }
              });
            } else if (field === 'variant_skus' && typeof value === 'object' && handleVariantChange) {
              // Legacy support
              console.log(`Processing variant_skus specifically using handleVariantChange`);
              Object.entries(value).forEach(([variantId, variantData]) => {
                if (variantData && variantData.sku !== undefined) {
                  handleVariantChange(productId, variantId, 'sku', variantData.sku);
                }
              });
            } else {
              handleProductChange(productId, field, value);
            }
          }
        }
      });
    });

    // Apply all updates to products
    console.log('Final product updates:', productUpdates);

    // Update UI to reflect changes
    setUpdatedProducts(prev => {
      const updated = { ...prev };
      Object.keys(productUpdates).forEach(productId => {
        updated[productId] = true;
      });
      return updated;
    });

    console.log('=== Completed processTemplateResults ===');
  }, [selectedProducts, handleProductChange, handleVariantChange, metafieldChangeHandler]);

  const handleExecute = async () => {
    if (selectedTemplates.length === 0) {
      setError('Please select at least one template');
      return;
    }

    setIsExecuting(true);
    setError('');

    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);

      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }

      console.log('Starting template execution with templates:', selectedTemplates);
      const templateResults = await executeTemplates(selectedTemplates, selectedProducts, (progress) => {
        setProgressSafe(progress);
      });

      console.log('Template execution completed:', templateResults);

      if (!templateResults || Object.keys(templateResults).length === 0) {
        throw new Error('No results returned from template execution');
      }

      await processTemplateResults(templateResults, selectedTemplates);

      setIsExecuting(false);
      setProgressSafe(0);
      setIsOpen(false);

    } catch (err) {
      console.error('Error executing templates:', err);
      setError(err.message || 'Failed to execute templates');
      setIsExecuting(false);
      setProgressSafe(0);
    }
  };

  const handleExecuteWorkflow = async () => {
    if (!selectedProducts || selectedProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    if (selectedTemplates.length === 0) {
      setError('Please select at least one template');
      return;
    }

    // Validate Image Template positioning before execution
    const imageTemplates = selectedTemplates.filter(templateId => {
      const template = templates.find(t => t.id === templateId);
      return template && template.type === 'image';
    });

    if (imageTemplates.length > 0) {
      // Check if any image template is not at the beginning
      const firstImageTemplateIndex = selectedTemplates.findIndex(templateId => {
        const template = templates.find(t => t.id === templateId);
        return template && template.type === 'image';
      });

      if (firstImageTemplateIndex > 0) {
        const imageTemplate = templates.find(t => t.id === imageTemplates[0]);
        setError(
          `⚠️ Image Analyzer Template "${imageTemplate?.name}" must be placed at the beginning of the workflow. ` +
          `Image Templates are processed first for technical reasons. Please reorder your templates or create a workflow with Image Templates at the start.`
        );
        return;
      }
    }

    setIsExecuting(true);
    setError('');

    // Initialize workflow status and template tracking
    setCurrentWorkflowTemplateCount(selectedTemplates.length);
    setCurrentTemplateIndex(0);
    setWorkflowExecutionStatus({
      status: 'starting',
      message: `Starting workflow with ${selectedTemplates.length} template(s)...`,
      progress: 0
    });

    try {
      console.log('🚀 DEBUG: About to call executeTemplatesDirectlySafely with:', selectedTemplates);
      console.log('🚀 DEBUG: executeTemplatesDirectlySafely function exists:', typeof executeTemplatesDirectlySafely);

      // Execute templates directly without creating a workflow - using safe wrapper for credits check
      const result = await executeTemplatesDirectlySafely(
        selectedTemplates,
        selectedProducts, // Pass the full product objects
        (status) => {
          console.log('🔄 Image Template Progress Update:', {
            message: status.message,
            progress: status.progress,
            selectedTemplatesLength: selectedTemplates.length,
            currentTemplateIndex: currentTemplateIndex,
            currentWorkflowTemplateCount: currentWorkflowTemplateCount
          });

          // Create consistent status message for image template
          let correctedStatus = { ...status };

          // Use immediate values instead of state (which might not be updated yet)
          const immediateTemplateCount = selectedTemplates.length;
          const immediateTemplateIndex = 0; // Image template is always first

          correctedStatus.message = normalizeTemplateMessage(
            status.message,
            immediateTemplateIndex,
            immediateTemplateCount
          );

          console.log(`Image template progress: using immediate values (index=${immediateTemplateIndex}, total=${immediateTemplateCount})`);

          // Update template index for image template (first template)
          setCurrentTemplateIndex(immediateTemplateIndex);

          // Update progress and status using safe progress setter
          setProgressSafe(status.progress || 0);
          setWorkflowExecutionStatus(correctedStatus);

          // Process results if available
          if (status.results && Object.keys(status.results).length > 0) {
            processTemplateResults(status.results, selectedTemplates);
          }

          // If workflow is complete, reset the executing state
          if (status.status === 'completed') {
            setIsExecuting(false);
            setProgressSafe(0);

            // Clear the status after a delay
            setTimeout(() => {
              setWorkflowExecutionStatus(null);
            }, 2000);
          }
        }
      );

      // Check if the result indicates we need to show the image template executor
      if (result && result.requiresImageSelection) {
        console.log('Image template detected:', result.imageTemplate);
        console.log('Template position:', result.templatePosition);

        // Process previous template results if they exist (respecting workflow order)
        if (result.previousTemplatesResults && Object.keys(result.previousTemplatesResults).length > 0) {
          console.log('Processing results from templates before the image template:', {
            resultCount: Object.keys(result.previousTemplatesResults).length
          });
          // For previous template results, we need to determine which templates were executed
          // Since we don't have the exact template objects, we'll pass null to disable SKU filtering
          // This maintains backward compatibility while being safe
          processTemplateResults(result.previousTemplatesResults, null);
        }

        // Store remaining templates to process after the image template
        if (result.remainingTemplateIds && result.remainingTemplateIds.length > 0) {
          console.log('Storing remaining templates to process after image template:', {
            count: result.remainingTemplateIds.length,
            templateIds: result.remainingTemplateIds
          });
          setRemainingTemplateIds(result.remainingTemplateIds);
        }

        // Check if this is an image template with auto-process mode
        const analysisMode = result.imageTemplate.imageAnalysisMode;
        const isLegacyFeatured = Boolean(result.imageTemplate.useFeaturedImage) && !analysisMode;
        const isFeaturedMode = analysisMode === 'featured' || isLegacyFeatured;
        const isAnalyzeAllMode = analysisMode === 'all';

        if (result.imageTemplate && isFeaturedMode) {
          console.log('Auto-processing image template with featured mode:', result.imageTemplate.name);
          // Process the template directly without showing the modal

          const templatePosition = result.templatePosition || { current: 0, total: selectedTemplates.length };
          const currentTemplateNumber = templatePosition.current + 1;
          const totalTemplates = templatePosition.total;

          console.log(`Processing image template ${currentTemplateNumber} of ${totalTemplates}`);

          processImageTemplateDirectly(
            result.imageTemplate,
            result.remainingTemplateIds,
            currentTemplateNumber,
            totalTemplates
          );
          return;
        }

        if (result.imageTemplate && isAnalyzeAllMode) {
          // 'all' mode — process directly without any modal, just like featured
          console.log('Auto-processing image template with Analyze All mode:', result.imageTemplate.name);
          const templatePosition = result.templatePosition || { current: 0, total: selectedTemplates.length };
          const currentTemplateNumber = templatePosition.current + 1;
          const totalTemplates = templatePosition.total;
          processImageTemplateDirectly(
            result.imageTemplate,
            result.remainingTemplateIds,
            currentTemplateNumber,
            totalTemplates
          );
          return;
        }

        // For manual image templates, show the image selection UI
        console.log('Showing image selection UI for manual mode template');
        setCurrentImageTemplate(result.imageTemplate);
        setRemainingAfterImage(result.remainingTemplateIds || []);
        setShowImageTemplateExecutor(true);
        setWorkflowExecutionStatus({
          status: 'waiting_for_image_selection',
          message: 'Please select images for the image template to continue...'
        });
        return;
      }

      // If there was an error with image templates, show it
      if (result && result.imageTemplates && result.error) {
        setError(result.error);
        setIsExecuting(false);
        return;
      }

      // Normal execution flow continues
      const executionId = result;
      console.log('Template execution started with ID:', executionId);
    } catch (err) {
      console.error('Error executing workflow:', err);
      setError(err.message || 'Failed to execute workflow');
      setIsExecuting(false);
      setProgressSafe(0); // Use setProgressSafe instead of setProgress
      // Clear the status
      setWorkflowExecutionStatus(null);
    }
  };

  const handleDeleteClick = useCallback((item, type) => {
    setItemToDelete(item);
    if (type === 'template') {
      setShowDeleteTemplateModal(true);
    } else {
      setShowDeleteWorkflowModal(true);
    }
  }, []);

  const handleConfirmDelete = useCallback(async (type) => {
    try {
      if (type === 'template') {
        await deleteTemplate(itemToDelete.id);
        setShowDeleteTemplateModal(false);
      } else {
        await deleteWorkflow(itemToDelete.id);
        setShowDeleteWorkflowModal(false);
      }
      setItemToDelete(null);
    } catch (err) {
      console.error(`Error deleting ${type}:`, err);
      setError(`Failed to delete ${type}`);
    }
  }, [deleteTemplate, deleteWorkflow, itemToDelete]);

  const handleDelete = useCallback(async (templateId) => {
    handleDeleteClick({ id: templateId }, 'template');
  }, [handleDeleteClick]);

  const handleEdit = useCallback((template) => {
    console.log('Editing template with data:', template);
    // Make sure we preserve all existing data when editing
    setEditingTemplate({
      id: template.id,
      name: template.name,
      type: template.type,
      prompt: template.prompt,
      target: template.target,
      metafield: template.metafield,
      userId: template.userId, // Preserve the userId
      inputFields: template.inputFields || [],
      outputFields: template.outputFields || [],
      inputMetafields: template.inputMetafields || [],
      outputMetafields: template.outputMetafields || [],
      modelId: template.modelId || '',
      namePoolId: template.namePoolId || '',
      // Add image template specific properties
      sidebarOnly: template.sidebarOnly,
      useFeaturedImage: template.useFeaturedImage,
      imageAnalysisMode: template.imageAnalysisMode,
      enableImagePlaceholders: template.enableImagePlaceholders,
      outputModes: template.outputModes || {},
      outputMetafieldModes: template.outputMetafieldModes || {}
    });
    setOriginalName(template.name);
    setShowSaveModal(true);
  }, []);

  const handleSave = useCallback(async () => {
    if (!editingTemplate.name.trim()) {
      setError('Template name is required');
      return;
    }

    if (!editingTemplate.prompt.trim()) {
      setError('Template prompt is required');
      return;
    }

    try {
      // Get the selected model information
      const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);

      // Calculate the credit cost
      const outputFieldCount = (editingTemplate.outputFields?.length || 0);
      const outputMetafieldsCount = (editingTemplate.outputMetafields?.length || 0);
      const totalOutputs = outputFieldCount + outputMetafieldsCount;
      const effectiveOutputCount = Math.max(totalOutputs, 1);
      const costPerOutput = selectedModel?.creditCostPerOutput || 1;

      console.log('Saving template with model info:', {
        modelId: selectedModel?.id || '',
        modelName: selectedModel?.displayName,
        costPerOutput,
        outputFields: outputFieldCount,
        outputMetafields: outputMetafieldsCount,
        totalOutputs,
        effectiveOutputCount,
        // Log image template specific properties
        useFeaturedImage: editingTemplate.useFeaturedImage,
        sidebarOnly: editingTemplate.sidebarOnly,
        outputModes: editingTemplate.outputModes,
        outputMetafieldModes: editingTemplate.outputMetafieldModes
      });

      // Create a clean template data object to ensure all properties are properly included
      const templateData = {
        // Basic template properties
        name: editingTemplate.name.trim(),
        prompt: editingTemplate.prompt.trim(),
        type: editingTemplate.type, // Preserve the template type selected in the UI
        target: editingTemplate.target,
        // Only include metafield if target is 'metafield' and both namespace and key exist
        metafield: editingTemplate.target === 'metafield' &&
          editingTemplate.metafield?.namespace &&
          editingTemplate.metafield?.key ?
          editingTemplate.metafield : null,
        // Ensure arrays are initialized
        inputFields: editingTemplate.inputFields || [],
        outputFields: editingTemplate.outputFields || [],
        inputMetafields: editingTemplate.inputMetafields || [],
        outputMetafields: editingTemplate.outputMetafields || [],
        // Save Name Pool link
        namePoolId: editingTemplate.namePoolId || '',
        // Save model information
        modelId: selectedModel?.id || editingTemplate.modelId || '',
        creditCostPerOutput: costPerOutput,
        // Image template specific properties - explicitly set Boolean values
        useFeaturedImage: editingTemplate.useFeaturedImage === true,
        imageAnalysisMode: editingTemplate.imageAnalysisMode || (editingTemplate.useFeaturedImage ? 'featured' : 'manual'),
        enableImagePlaceholders: editingTemplate.enableImagePlaceholders === true,
        sidebarOnly: editingTemplate.sidebarOnly === true,
        // Ensure objects are properly initialized
        outputModes: { ...(editingTemplate.outputModes || {}) },
        outputMetafieldModes: { ...(editingTemplate.outputMetafieldModes || {}) }
      };

      let savedTemplate;
      if (editingTemplate.id) {
        // Update existing template
        savedTemplate = await updateTemplate(editingTemplate.id, templateData);
      } else {
        // Create new template
        savedTemplate = await addTemplate(templateData);
      }

      // Call parent handler with the saved template
      if (savedTemplate) {
        onSaveTemplate(savedTemplate);
      }

      // Reset and close
      setEditingTemplate({
        id: '',
        name: '',
        type: 'product',
        prompt: '',
        target: 'description',
        metafield: { namespace: '', key: '' },
        userId: '',
        inputFields: [],
        outputFields: [],
        inputMetafields: [],
        outputMetafields: [],
        modelId: '',
        namePoolId: ''
      });
      setOriginalName('');
      setShowSaveModal(false);
      setError('');
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    }
  }, [editingTemplate, updateTemplate, addTemplate, onSaveTemplate, availableAIModels]);

  const resetForm = useCallback(() => {
    setEditingTemplate({
      id: '',
      name: '',
      type: 'product',
      prompt: '',
      target: 'description',
      metafield: {
        namespace: '',
        key: ''
      },
      userId: '',
      inputFields: [],
      outputFields: [],
      inputMetafields: [],
      outputMetafields: [],
      modelId: '',
      namePoolId: ''
    });
    setOriginalName('');
    setError('');
  }, []);

  const handleFieldChange = (fieldName, checked, type) => {
    setEditingTemplate(prev => ({
      ...prev,
      [type === 'input' ? 'inputFields' : 'outputFields']: checked
        ? [...(prev[type === 'input' ? 'inputFields' : 'outputFields'] || []), fieldName]
        : (prev[type === 'input' ? 'inputFields' : 'outputFields'] || []).filter(f => f !== fieldName)
    }));
  };

  const handleMetafieldChange = (index, field, value, type) => {
    const newMetafields = [...(editingTemplate[type === 'input' ? 'inputMetafields' : 'outputMetafields'] || [])];
    newMetafields[index] = { ...newMetafields[index], [field]: value };
    setEditingTemplate(prev => ({
      ...prev,
      [type === 'input' ? 'inputMetafields' : 'outputMetafields']: newMetafields
    }));
  };

  const handleAddMetafield = (type) => {
    const newMetafields = [...(editingTemplate[type === 'input' ? 'inputMetafields' : 'outputMetafields'] || []), { namespace: '', key: '' }];
    setEditingTemplate(prev => ({
      ...prev,
      [type === 'input' ? 'inputMetafields' : 'outputMetafields']: newMetafields
    }));
  };

  const handleRemoveMetafield = (index, type) => {
    const newMetafields = [...(editingTemplate[type === 'input' ? 'inputMetafields' : 'outputMetafields'] || [])];
    newMetafields.splice(index, 1);
    setEditingTemplate(prev => ({
      ...prev,
      [type === 'input' ? 'inputMetafields' : 'outputMetafields']: newMetafields
    }));
  };

  const handleDuplicateTemplate = async (template) => {
    try {
      const duplicateTemplate = {
        ...template,
        id: '', // Clear ID so a new one will be generated
        name: `${template.name} (Copy)`,
      };
      delete duplicateTemplate.id; // Ensure ID is removed

      const savedTemplate = await addTemplate(duplicateTemplate);
      if (savedTemplate) {
        onSaveTemplate(savedTemplate);
      }
    } catch (err) {
      console.error('Error duplicating template:', err);
      setError(err.message || 'Failed to duplicate template');
    }
  };

  const handleDuplicateWorkflow = async (workflow) => {
    try {
      const duplicateWorkflow = {
        name: `${workflow.name} (Copy)`,
        templates: [...workflow.templates]
      };

      await addWorkflow(duplicateWorkflow);
    } catch (err) {
      console.error('Error duplicating workflow:', err);
      setError(err.message || 'Failed to duplicate workflow');
    }
  };

  const getAvailableFields = (type) => {
    const productFields = [
      { label: 'Title', value: 'title' },
      { label: 'Description', value: 'body_html' },
      { label: 'Product Type', value: 'product_type' },
      { label: 'Tags', value: 'tags' },
      { label: 'Alt Text', value: 'alt_text' }
    ];

    const variantFields = [
      { label: 'SKU', value: 'variants.sku' },
      { label: 'Price', value: 'variants.price' },
      { label: 'Compare at Price', value: 'variants.compare_at_price' },
      { label: 'Inventory Quantity', value: 'variants.inventory_quantity' }
    ];

    // Check if this is a SKU/variant template
    const isSKUTemplate = editingTemplate.prompt?.toLowerCase().includes('sku');

    return isSKUTemplate ? [...productFields, ...variantFields] : productFields;
  };

  const handleExecuteWorkflowFromList = async (workflow) => {
    if (!workflow || !workflow.templates || workflow.templates.length === 0) {
      setError('Selected workflow has no templates');
      return;
    }

    if (!selectedProducts || selectedProducts.length === 0) {
      setError('Please select at least one product to apply the workflow');
      return;
    }

    // Set the selected templates to match the workflow
    setSelectedTemplates(workflow.templates);

    // Close the workflow modal
    setShowWorkflowModal(false);

    // Execute the workflow
    setIsExecuting(true);
    setError('');
    setWorkflowExecutionStatus(null);
    setIsPaused(false);

    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);

      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }

      console.log('Starting workflow execution from list:', workflow.id, 'with templates:', workflow.templates);

      // Use stop markers from workflow if available, otherwise use local state
      const workflowStopMarkers = workflow.stopMarkers || stopMarkers || [];
      console.log('Stop markers:', workflowStopMarkers);

      // Execute the workflow using the V2 service with stop markers
      const result = await executeWorkflow(
        workflow,
        selectedProducts,
        (status) => {
          // Handle template completion - process results immediately with insert mode
          if (status.status === 'template_complete') {
            console.log('Processing template completion results:', status.templateId);
            if (status.results && Object.keys(status.results).length > 0) {
              processTemplateResults(status.results, selectedTemplates);
            }
            return; // Don't update progress/status for template completion
          }

          // Update progress and status
          setProgress(status.progress || 0);
          setWorkflowExecutionStatus(status);

          // Check if workflow is paused
          if (status.isPaused || status.status === 'paused') {
            setIsPaused(true);
          } else {
            setIsPaused(false);
          }

          // Reset state when completed
          if (status.status === 'completed') {
            setIsExecuting(false);
            setProgress(0);
            setIsPaused(false);
            setCurrentExecutionId(null);
          }
        },
        null, // templateIds (use workflow's templates)
        { stopMarkers: workflowStopMarkers } // Pass stop markers in options
      );

      // Store execution ID for resume functionality
      const executionId = result?.executionId;
      setCurrentExecutionId(executionId);

      console.log('Workflow execution started with ID:', executionId);
    } catch (err) {
      console.error('Error executing workflow from list:', err);
      setError(err.message || 'Failed to execute workflow');
      setIsExecuting(false);
      setProgressSafe(0);
      setIsPaused(false);
      setCurrentExecutionId(null);
      // Clear the status
      setWorkflowExecutionStatus(null);
    }
  };

  // Handler to resume a paused workflow
  const handleResumeWorkflow = async () => {
    if (!currentExecutionId || !user) {
      console.error('Cannot resume: missing execution ID or user');
      return;
    }

    try {
      console.log('Resuming workflow execution:', currentExecutionId);
      await WorkflowServiceV2.resumeExecution(user.uid, currentExecutionId);
      setIsPaused(false);
      console.log('Workflow resumed successfully');
    } catch (err) {
      console.error('Error resuming workflow:', err);
      setError(err.message || 'Failed to resume workflow');
    }
  };

  // Handler to toggle stop marker for a template
  const handleToggleStopMarker = (templateIndex) => {
    setStopMarkers(prev => {
      if (prev.includes(templateIndex)) {
        return prev.filter(i => i !== templateIndex);
      } else {
        return [...prev, templateIndex].sort((a, b) => a - b);
      }
    });
  };

  // State for image template execution
  // Using selectedProducts from props instead of local state
  const [showImageTemplateExecutor, setShowImageTemplateExecutor] = useState(false);
  const [currentImageTemplate, setCurrentImageTemplate] = useState(null);
  const [remainingTemplateIds, setRemainingTemplateIds] = useState([]);
  const [remainingAfterImage, setRemainingAfterImage] = useState([]);

  // State for persistent results sidebar
  const [persistentResults, setPersistentResults] = useState([]);
  const [sidebarVisible, setSidebarVisible] = useState(false);

  // Listen for persistent results from headless workflow pipeline
  // The templateExecutionServiceV2.js dispatches 'persistentResultsUpdated' events
  // ONLY for templates with sidebarOnly enabled, allowing the sidebar to show real-time progress
  useEffect(() => {
    const handlePersistentResults = (event) => {
      const results = event.detail?.results || window._persistentResults || [];
      if (results.length > 0) {
        console.log(`📊 PersistentResults updated from headless pipeline: ${results.length} entries`);
        setPersistentResults([...results]);
        setSidebarVisible(true);
      }
    };
    
    window.addEventListener('persistentResultsUpdated', handlePersistentResults);
    
    return () => {
      window.removeEventListener('persistentResultsUpdated', handlePersistentResults);
    };
  }, []);

  // Fix for progress display - ensure progress never exceeds 100%
  const setProgressSafe = (value) => {
    // Ensure progress is a number and capped at 100%
    const safeValue = typeof value === 'number' ? Math.min(value, 100) : 0;
    setProgress(safeValue);
  };

  // Helper function to format objects for human-readable description
  const formatObjectForDescription = (obj) => {
    if (!obj || typeof obj !== 'object') {
      return String(obj);
    }

    // Handle arrays specially
    if (Array.isArray(obj)) {
      const items = obj.map(item => {
        if (typeof item === 'object' && item !== null) {
          return formatObjectForDescription(item);
        }
        return String(item);
      });
      return '<ul>' + items.map(item => `<li>${item}</li>`).join('') + '</ul>';
    }

    // Format object as a list of key-value pairs
    const parts = [];
    for (const [key, value] of Object.entries(obj)) {
      // Skip empty values
      if (value === null || value === undefined || value === '') {
        continue;
      }

      let formattedValue;
      if (typeof value === 'object') {
        formattedValue = formatObjectForDescription(value);
      } else {
        formattedValue = String(value);
      }

      // Format the key-value pair
      parts.push(`<strong>${key}:</strong> ${formattedValue}`);
    }

    return parts.join('<br>');
  };

  // Direct image template processing without showing any modal
  const processImageTemplateDirectly = async (template, remainingIds = [], currentTemplateNumber = 1, totalTemplates = 1) => {
    try {
      // Template is passed directly to avoid redundant database calls
      if (!template) {
        throw new Error('Template not found');
      }

      console.log('DIRECT PROCESSING - Template details:', {
        id: template.id,
        name: template.name,
        type: template.type,
        useFeaturedImage: template.useFeaturedImage
      });

      // Create a notification to show that processing is happening
      setIsExecuting(true);
      setProgressSafe(0); // Use setProgressSafe instead of setProgress
      setWorkflowExecutionStatus({
        status: 'processing',
        message: `Template ${currentTemplateNumber}/${totalTemplates}: Processing image template '${template.name}' automatically... 0%`
      });

      // Check if user has enough credits
      const hasCredits = await hasEnoughCredits(user?.uid, selectedProducts.length);
      if (!hasCredits) {
        throw new Error(`Not enough credits to process template for ${selectedProducts.length} products.`);
      }

      // Get the AI model for this template
      const model = await getAIModelById(template.modelId);
      if (!model) {
        throw new Error('AI model not found for this template');
      }

      // Debug logging to see model properties
      if (model) {
        console.log('=== MAX IMAGES DEBUG (TemplateTools) ===');
        console.log('Selected Model Debug:', {
          id: model.id,
          displayName: model.displayName,
          type: model.type,
          modelType: model.modelType,
          maxImages: model.maxImages,
          isVision: model.type === 'vision' || model.displayName?.toLowerCase().includes('vision'),
          allProperties: model
        });
        console.log('Raw maxImages value:', model.maxImages);
        console.log('Type of maxImages:', typeof model.maxImages);
        console.log('All model keys:', Object.keys(model));
        console.log('=== END MAX IMAGES DEBUG ===');
      }

      // Process each product
      const results = [];
      let processedCount = 0;

      console.log(`🔄 PROCESSING ${selectedProducts.length} PRODUCTS for image template:`, selectedProducts.map(p => p.title));

      for (let i = 0; i < selectedProducts.length; i++) {
        const product = selectedProducts[i];

        console.log(`📦 Processing product ${i + 1}/${selectedProducts.length}:`, {
          productId: product.id,
          productTitle: product.title,
          productIndex: i
        });

        // Update progress - cap at 100%
        const progressPercent = Math.min(((i + 1) / selectedProducts.length) * 100, 100);

        // Calculate workflow progress for image template (first 25% of workflow)
        const workflowProgress = (progressPercent / totalTemplates);
        setProgressSafe(workflowProgress);

        console.log(`Auto-select image template progress: ${progressPercent}% (workflow: ${workflowProgress}%)`);
        setWorkflowExecutionStatus({
          status: 'processing',
          message: `Template ${currentTemplateNumber}/${totalTemplates}: Processing image template '${template.name}'... ${Math.round(progressPercent)}%`
        });

        // ===== ANALYZE ALL MODE: process every image per product =====
        if (template.imageAnalysisMode === 'all') {
          const allImages = product.images || [];
          if (allImages.length === 0) {
            console.warn(`No images found for product ${product.id}, skipping`);
            continue;
          }

          console.log(`📸 ANALYZE ALL: Processing ${allImages.length} images for "${product.title}"`);
          const imageAnalysisResults = [];

          for (let j = 0; j < allImages.length; j++) {
            const img = allImages[j];
            const imageUrl = img.src || img.url;

            try {
              console.log(`  Compressing image ${j + 1}/${allImages.length} before AI analysis...`);
              // Compress image before sending to AI (1024px max, JPEG 85%)
              const compressedImageUrl = await compressImageForAnalysis(imageUrl, 1024);

              // Use the template's custom prompt — user defines WHAT to analyze
              // System prompt enforces the OUTPUT FORMAT (JSON with required fields)
              let userPrompt = template.prompt || 'Analyze this product image in detail. Describe what you see.';

              // Replace placeholders in the user's prompt with product data
              Object.entries(product).forEach(([key, value]) => {
                if (typeof value === 'string') {
                  userPrompt = userPrompt.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
                }
              });

              // Build Vision API messages
              // IMPORTANT: We wrap the user's custom prompt inside the JSON structure
              // so the AI always returns JSON with suggestedAltText, regardless of prompt format
              const analysisMessages = [
                {
                  role: 'system',
                  content: `You are an AI assistant that analyzes product images for e-commerce.

YOUR RESPONSE MUST BE A VALID JSON OBJECT. No markdown, no code blocks, no plain text. ONLY JSON.

Required JSON format:
{
  "description": "Your full detailed analysis goes here as a single plain text string",
  "category": "one of: hero, detail, lifestyle, variant, scale, texture",
  "suggestedAltText": "A short, unique, descriptive alt text for THIS image (max 125 chars)",
  "bestPosition": "one of: top, middle, bottom, gallery"
}

RULES FOR "description":
- Follow the user's analysis prompt and put ALL your analysis text into this field
- Write as a single plain text string, be specific and detailed
- Do NOT use nested JSON or markdown formatting inside this field

RULES FOR "suggestedAltText":
- Max 125 characters
- Describe what is VISIBLE in the image: product type, color, material, angle, key features
- Must be UNIQUE per image — never use generic text like "Product - Image 1"
- Good example: "Brown faux leather tote bag with gold hardware, front view"
- Bad example: "Large Capacity Women Handbag - Image 3"

RULES FOR "category":
- "hero": Primary full-product shot with clean background (max 1 per product)
- "detail": Close-up, feature focus, or alternative angle
- "lifestyle": Product in real-world use or styled setting
- "variant": Same product in a DIFFERENT color or style
- "scale": Shows product size relative to hand/body/ruler
- "texture": Close-up of material/fabric/surface

RULES FOR "bestPosition":
- "top": Single best overview image
- "middle": Detail shots, features — between text sections
- "bottom": Variants, size charts, extra views
- "gallery": Supplementary or similar angle images

You are analyzing one image from a SET. Most images are NOT "hero". Focus on what makes THIS image different.`
                },
                {
                  role: 'user',
                  content: [
                    { type: 'image_url', image_url: { url: compressedImageUrl, detail: 'high' } },
                    {
                      type: 'text', text: `Product: ${product.title || 'Unknown'} (Image ${j + 1} of ${allImages.length})

Analyze this image and respond as JSON. Put your analysis into the "description" field.

Analysis instructions: ${userPrompt}

RESPOND WITH JSON ONLY. No markdown code blocks.` }
                  ]
                }
              ];

              let jsonResponse;

              if (model.provider === 'openai') {
                const apiKey = await getOpenAIApiKey(user?.uid);
                if (!apiKey) throw new Error('OpenAI API key is required');

                const response = await fetch('https://api.openai.com/v1/chat/completions', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${apiKey}` },
                  body: JSON.stringify({
                    model: model.internalName || 'gpt-4.1-mini',
                    messages: analysisMessages,
                    temperature: 0.5,
                    max_tokens: 1000,
                    response_format: { type: 'json_object' }
                  })
                });
                if (!response.ok) throw new Error(`OpenAI API failed: ${response.status}`);
                jsonResponse = await response.json();
              } else if (model.provider === 'gemini') {
                const { generateContent } = await import('../../services/aiProviderService');
                const modelIdentifier = model.id || model.modelId || model.internalName;
                jsonResponse = await generateContent(modelIdentifier, {
                  messages: analysisMessages,
                  temperature: 0.5,
                  max_tokens: 1000,
                  images: [compressedImageUrl],
                  modelType: 'vision'
                });
              } else {
                throw new Error(`Unsupported provider: ${model.provider}`);
              }

              const rawContent = jsonResponse?.choices?.[0]?.message?.content || '';
              let parsed = {};

              // Clean markdown blocks and parse JSON
              try {
                const cleaned = rawContent.replace(/```json\s*/gi, '').replace(/```\s*/g, '').trim();
                parsed = JSON.parse(cleaned);
              } catch {
                try {
                  const jsonMatch = rawContent.match(/\{[\s\S]*\}/);
                  if (jsonMatch) {
                    parsed = JSON.parse(jsonMatch[0]);
                  }
                } catch {
                  // Complete fallback — use raw text as description
                  parsed = {};
                }
              }

              // Build a clean description from parsed data
              let cleanDescription = '';
              if (parsed.description && typeof parsed.description === 'string' && !parsed.description.startsWith('{')) {
                cleanDescription = parsed.description;
              } else {
                // Synthesize description from whatever fields exist
                const skipKeys = ['category', 'suggestedAltText', 'suggestedalttext', 'bestPosition', 'bestposition', 'confidence', 'alt_text'];
                const parts = [];
                for (const [key, val] of Object.entries(parsed)) {
                  if (skipKeys.includes(key)) continue;
                  if (typeof val === 'string' && val.length > 0) {
                    parts.push(`${key}: ${val}`);
                  }
                }
                cleanDescription = parts.length > 0 ? parts.join('. ') : rawContent.replace(/```json|```/g, '').substring(0, 300).trim();
              }

              imageAnalysisResults.push({
                imageIndex: j + 1,
                imageUrl: imageUrl,
                imageId: img.id,
                description: cleanDescription,
                category: parsed.category || 'detail',
                suggestedAltText: parsed.suggestedAltText || parsed.suggestedalttext || parsed.alt_text || parsed.suggested_alt_text || (cleanDescription ? cleanDescription.substring(0, 125) : `${product.title || 'Product'} - Image ${j + 1}`),
                bestPosition: parsed.bestPosition || parsed.bestposition || 'middle',
                confidence: parsed.confidence || 0.8
              });

              console.log(`  ✓ Image ${j + 1}/${allImages.length} analyzed: [${parsed.category || 'detail'}]`);

            } catch (imgError) {
              console.error(`  ✗ Error analyzing image ${j + 1}:`, imgError.message);
              imageAnalysisResults.push({
                imageIndex: j + 1,
                imageUrl: imageUrl,
                imageId: img.id,
                description: 'Analysis failed',
                category: 'detail',
                suggestedAltText: `${product.title || 'Product'} - image ${j + 1} analysis failed`,
                bestPosition: 'gallery',
                confidence: 0,
                error: imgError.message
              });
            }

            // Update progress within product
            const imageProgress = ((i * allImages.length + j + 1) / (selectedProducts.length * allImages.length)) * 100;
            setProgressSafe(Math.min(imageProgress / totalTemplates, 100));
            setWorkflowExecutionStatus({
              status: 'processing',
              message: `Template ${currentTemplateNumber}/${totalTemplates}: Analyzing image ${j + 1}/${allImages.length} of "${product.title}"... ${Math.round(imageProgress)}%`
            });
          }

          // Format combined analysis for description
          let analysisSummary = `<h3>Image Analysis Results (${imageAnalysisResults.length} images)</h3>\n`;
          imageAnalysisResults.forEach(analysis => {
            analysisSummary += `<p><strong>Image ${analysis.imageIndex} [${analysis.category}]:</strong> ${analysis.description}</p>\n`;
            analysisSummary += `<p><em>Alt text:</em> ${analysis.suggestedAltText}</p>\n`;
            analysisSummary += `<p><em>Best position:</em> ${analysis.bestPosition}</p>\n<hr>\n`;
          });

          // Pass analysis results to parent
          if (handleProductChange) {
            // Store _imageAnalysis on the product object directly for downstream workflow templates
            const productRef = selectedProducts.find(p => p.id === product.id);
            if (productRef) {
              productRef._imageAnalysis = imageAnalysisResults;
              console.log(`Stored _imageAnalysis directly on product object for "${product.title}"`);
            }

            // Write the analysis summary to the description if not sidebar-only
            if (!template.sidebarOnly) {
              const insertMode = template.outputModes?.body_html || 'add';
              handleProductChange(product.id, 'descriptionHtml', analysisSummary, insertMode);
              console.log(`✅ Wrote analysis summary to description for "${product.title}" (mode: ${insertMode})`);
            } else {
              console.log(`ℹ️ Template is sidebar-only, not writing to description for "${product.title}"`);
            }
            console.log(`✅ Passed ${imageAnalysisResults.length} image analysis results for "${product.title}"`);
          }

          results.push({
            id: product.id,
            productName: product.title || `Product ${i + 1}`,
            templateName: template.name,
            imageUrl: allImages[0]?.src || allImages[0]?.url,
            status: 'success',
            fields: { description: analysisSummary },
            insertModes: {},
            imageAnalysis: imageAnalysisResults
          });
          processedCount++;
          continue; // Skip the featured-image processing below
        }
        // ===== END ANALYZE ALL MODE =====

        // Find the featured image for this product (Featured / Manual mode)
        const featuredImage = product.images?.find(img => img.position === 1) || product.images?.[0];
        if (!featuredImage) {
          console.warn(`No featured image found for product ${product.id}, skipping`);
          continue;
        }

        // Format the image for processing
        const formattedImage = {
          id: featuredImage.id,
          url: featuredImage.src || featuredImage.url,
          alt: featuredImage.alt || `Featured image for ${product.title || 'product'}`,
          position: featuredImage.position || 0
        };

        // Compress featured image before sending to AI (1024px max, JPEG 85%)
        const compressedFeaturedUrl = await compressImageForAnalysis(formattedImage.url, 1024);

        // Process the image with the AI provider - using direct API call like in ImageTemplateExecutor
        let basePrompt = template.prompt || 'We need all informations from the product image which is relevant for the product description.';

        // Replace any placeholders in the prompt with product data
        Object.entries(product).forEach(([key, value]) => {
          if (typeof value === 'string') {
            basePrompt = basePrompt.replace(new RegExp(`\{${key}\}`, 'g'), value);
          }
        });

        // Get all outputs (fields and metafields) for structured response
        const allOutputs = [...(template.outputFields || [])];

        // Add metafields to allOutputs, ensuring they're in string format
        if (template.outputMetafields && template.outputMetafields.length > 0) {
          template.outputMetafields.forEach(metafieldItem => {
            // Convert object format to string format if needed
            let metafieldId = '';

            if (typeof metafieldItem === 'string') {
              metafieldId = metafieldItem;
            } else if (typeof metafieldItem === 'object' && metafieldItem !== null) {
              if (metafieldItem.namespace && metafieldItem.key) {
                metafieldId = `metafield.${metafieldItem.namespace}.${metafieldItem.key}`;
                console.log(`Converted metafield object to string format: ${metafieldId}`);
              } else {
                console.log(`Invalid metafield object format:`, metafieldItem);
                return; // Skip this item
              }
            }

            if (metafieldId && !allOutputs.includes(metafieldId)) {
              allOutputs.push(metafieldId);
            }
          });
        }

        // Format the output fields for the prompt
        const formattedOutputs = allOutputs.map(field => {
          // For metafields, use a more readable format
          if (typeof field === 'string' && field.startsWith('metafield.')) {
            const parts = field.replace('metafield.', '').split('.');
            if (parts.length === 2) {
              return `${parts[0]}.${parts[1]}`;
            }
          }
          return field;
        });

        // Log the formatted outputs for debugging
        console.log('Formatted output fields for prompt:', formattedOutputs);

        // Add instructions for structured output
        // Use the actual field names in the example to avoid confusion
        const exampleField = formattedOutputs[0] || 'field_name';

        // Create a clear, explicit prompt with properly formatted field names
        let fieldsList = '';
        formattedOutputs.forEach(field => {
          fieldsList += `- "${field}"
`;
        });

        // Combine the user's original prompt with very explicit formatting instructions
        // This approach ensures we get a single string value for the metafield
        const processedPrompt = `${basePrompt}\n\nIMPORTANT FORMATTING INSTRUCTIONS:\nRespond with a JSON object that has EXACTLY this structure:\n{\n  \"${formattedOutputs[0]}\": \"A single plain text string with all the information combined\"\n}\n\nYour response MUST be a single text string inside the JSON value, NOT a nested object or array. All information should be combined into ONE CONTINUOUS TEXT STRING.\n\nExample of CORRECT format:\n{ \"${formattedOutputs[0]}\": \"White cropped cardigan with long bell sleeves and front tie closure, made of ribbed knit fabric. Casual style with slightly open front. Paired with high waist denim jeans with tie belt. Accessories include black shoulder bag and gold jewelry.\" }\n\nExample of INCORRECT format:\n{ \"${formattedOutputs[0]}\": { \"product_type\": \"Women's cropped cardigan\", \"color\": \"White\" } }`;

        console.log('Requesting structured JSON with fields:', formattedOutputs);

        // Make AI API call based on model provider
        let jsonResponse;

        if (model.provider === 'openai') {
          // OpenAI direct API call
          const apiKey = await getOpenAIApiKey(user?.uid);
          if (!apiKey) {
            throw new Error('OpenAI API key is required for image analysis');
          }

          // Get the model to use (default to gpt-4.1-mini if not specified)
          const modelInternalName = model?.internalName || 'gpt-4.1-mini';

          // Prepare the messages with system and user content
          const messages = [
            {
              role: 'system',
              content: 'You are an AI assistant that generates product content for an e-commerce platform based on images. Follow the user\'s prompt exactly to determine what information to extract from the image. Do not make assumptions about what information is needed unless specified in the prompt.'
            },
            {
              role: 'user',
              content: [
                {
                  type: 'image_url',
                  image_url: {
                    url: compressedFeaturedUrl,
                    detail: 'high'
                  }
                },
                {
                  type: 'text',
                  text: `Product Title: ${product.title || 'No title available'}\n\n${processedPrompt}`
                }
              ]
            }
          ];

          // Prepare the request payload
          const requestPayload = {
            model: modelInternalName,
            messages,
            temperature: 0.7,
            max_tokens: 2000
          };

          console.log('Making direct OpenAI API call with payload:', {
            model: requestPayload.model,
            messageCount: messages.length,
            imageUrl: formattedImage.url.substring(0, 50) + '...',
            prompt: processedPrompt.substring(0, 50) + (processedPrompt.length > 50 ? '...' : '')
          });

          // DETAILED LOGGING: Show exactly what text content is being sent to OpenAI Vision API
          console.log('=== OPENAI VISION API REQUEST DETAILS (TemplateTools) ===');
          console.log('Product Title:', product.title || 'No title available');
          console.log('Original Prompt:', basePrompt);
          console.log('Processed Prompt:', processedPrompt);
          console.log('Full Text Content Being Sent:', messages[1].content.find(c => c.type === 'text')?.text);
          console.log('Image Being Sent:', { url: formattedImage.url, alt: formattedImage.alt });
          console.log('Complete Messages Object:', JSON.stringify(messages, null, 2));
          console.log('Complete Request Payload:', JSON.stringify(requestPayload, null, 2));
          console.log('=== END OPENAI VISION API REQUEST DETAILS ===');

          // Make a direct API call to OpenAI
          const apiResponse = await fetch('https://api.openai.com/v1/chat/completions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${apiKey}`
            },
            body: JSON.stringify(requestPayload)
          });

          if (!apiResponse.ok) {
            const errorText = await apiResponse.text();
            console.error('OpenAI API error response:', errorText);
            throw new Error(`OpenAI API error: ${apiResponse.status}. ${errorText.substring(0, 100)}...`);
          }

          jsonResponse = await apiResponse.json();

        } else if (model.provider === 'gemini') {
          // Use centralized AI service for Gemini
          console.log('Using centralized AI service for Gemini vision');
          console.log('Selected model for debugging:', {
            id: model.id,
            modelId: model.modelId,
            internalName: model.internalName,
            provider: model.provider
          });

          // Prepare the messages for the AI service
          const messages = [
            {
              role: 'system',
              content: 'You are an AI assistant that generates product content for an e-commerce platform based on images. Follow the user\'s prompt exactly to determine what information to extract from the image. Do not make assumptions about what information is needed unless specified in the prompt.'
            },
            {
              role: 'user',
              content: `Product Title: ${product.title || 'No title available'}\n\n${processedPrompt}`
            }
          ];

          const requestData = {
            messages,
            images: [compressedFeaturedUrl],
            modelType: 'vision',
            provider: model.provider,
            templateType: 'image'
          };

          console.log('Making AI provider service call:', {
            modelId: model.id,
            provider: model.provider,
            messageCount: messages.length,
            imageCount: 1,
            imageUrls: [formattedImage.url.substring(0, 50) + '...'],
            prompt: processedPrompt.substring(0, 50) + (processedPrompt.length > 50 ? '...' : '')
          });

          // DETAILED LOGGING: Show exactly what text content is being sent to Gemini Vision API
          console.log('=== GEMINI VISION API REQUEST DETAILS (TemplateTools) ===');
          console.log('Product Title:', product.title || 'No title available');
          console.log('Original Prompt:', basePrompt);
          console.log('Processed Prompt:', processedPrompt);
          console.log('Full Text Content Being Sent:', messages[1].content);
          console.log('Image Being Sent:', { url: formattedImage.url, alt: formattedImage.alt });
          console.log('Complete Messages Object:', JSON.stringify(messages, null, 2));
          console.log('Complete Request Data:', JSON.stringify(requestData, null, 2));
          console.log('=== END GEMINI VISION API REQUEST DETAILS ===');

          // Use the centralized AI service
          const response = await processWithAIProvider({
            provider: model.provider,
            modelId: model.internalName || model.modelId,
            prompt: processedPrompt,
            messages: requestData.messages,
            images: requestData.images,
            modelType: requestData.modelType,
            templateType: requestData.templateType,
            userId: user?.uid
          });

          // Convert to OpenAI-compatible format
          jsonResponse = {
            choices: [{
              message: {
                content: response
              }
            }]
          };

        } else {
          throw new Error(`Unsupported model provider: ${model.provider}`);
        }

        // CRITICAL DEBUGGING: Log the complete API response
        console.log('COMPLETE RAW API RESPONSE JSON:', JSON.stringify(jsonResponse, null, 2));
        console.log('API response details:', {
          hasChoices: !!jsonResponse.choices,
          choicesLength: jsonResponse.choices?.length,
          firstChoice: jsonResponse.choices?.[0] ? 'Present' : 'Missing'
        });

        // Parse the response
        const rawContent = jsonResponse.choices[0].message.content;

        // CRITICAL DEBUGGING: Log the message content
        console.log('RAW MESSAGE CONTENT FROM API:', rawContent);

        // Try to parse the content as JSON if possible
        let parsedContent = rawContent;
        let isJsonResponse = false;

        // Clean up the response - remove markdown code blocks if present
        const cleanedContent = rawContent.replace(/```json|```/g, '').trim();
        console.log('Cleaned content:', cleanedContent);

        try {
          // Check if the content looks like JSON
          if (cleanedContent.trim().startsWith('{') && cleanedContent.trim().endsWith('}')) {
            parsedContent = JSON.parse(cleanedContent);
            isJsonResponse = true;
            console.log('Successfully parsed JSON response:', Object.keys(parsedContent));
          }
        } catch (error) {
          console.log('Content is not valid JSON, using as plain text:', error.message);
        }

        // Create a structured response object that maps to the requested fields
        let structuredContent = {};

        if (isJsonResponse) {
          // If we have a JSON response, use it directly
          structuredContent = parsedContent;

          // Check if the response contains [object Object] as a key
          if (structuredContent['[object Object]']) {
            console.log('Found [object Object] in response, attempting to fix');

            // Try to parse it if it's a string that looks like JSON
            const objValue = structuredContent['[object Object]'];
            if (typeof objValue === 'string' && objValue.includes('{') && objValue.includes('}')) {
              try {
                // Clean up any markdown formatting
                const cleanValue = objValue.replace(/```json|```/g, '').trim();
                const innerJson = JSON.parse(cleanValue);

                // Replace the [object Object] key with the proper field name
                delete structuredContent['[object Object]'];

                // Get the first field from the template
                const firstField = allOutputs[0];
                if (firstField) {
                  // If the inner JSON has the correct field, use that value
                  const formattedField = firstField.startsWith('metafield.') ?
                    firstField.replace('metafield.', '').replace('.', '_') : firstField;

                  if (innerJson[formattedField] || innerJson[firstField]) {
                    structuredContent[firstField] = innerJson[formattedField] || innerJson[firstField];
                  } else {
                    // Otherwise, use a stringified version of the inner JSON
                    structuredContent[firstField] = JSON.stringify(innerJson);
                  }
                }
              } catch (e) {
                console.log('Failed to parse inner JSON:', e.message);
                // Keep the original value but assign it to the correct field
                const firstField = allOutputs[0];
                if (firstField) {
                  structuredContent[firstField] = objValue;
                  delete structuredContent['[object Object]'];
                }
              }
            } else {
              // It's not JSON, just use the value with the correct field name
              const firstField = allOutputs[0];
              if (firstField) {
                structuredContent[firstField] = objValue;
                delete structuredContent['[object Object]'];
              }
            }
          }
        } else {
          // If we have a plain text response, create a structured object
          // with the raw content assigned to each requested output field
          allOutputs.forEach(field => {
            structuredContent[field] = cleanedContent;
          });

          console.log('Created structured content from plain text:', Object.keys(structuredContent));
        }

        // Create a response object that matches what processWithAIProvider would return
        const response = {
          content: structuredContent
        };

        // Format the results
        const productResult = {
          id: product.id,
          title: product.title,
          imageUrl: formattedImage.url,
          content: response.content,
          template: template.name,
          timestamp: new Date().toISOString()
        };

        results.push(productResult);

        // CRITICAL DEBUGGING: Log the response object
        console.log(`📋 Response object for product ${i + 1}/${selectedProducts.length}:`, {
          productId: product.id,
          productIndex: i,
          productTitle: product.title,
          contentType: typeof response.content,
          contentPreview: typeof response.content === 'string' ?
            response.content.substring(0, 100) :
            JSON.stringify(response.content).substring(0, 100),
          hasOutputFields: template.outputFields && template.outputFields.length > 0,
          outputFields: template.outputFields,
          hasOutputMetafields: template.outputMetafields && template.outputMetafields.length > 0
        });

        // Update the product if needed
        // CRITICAL FIX: Check for both outputFields AND outputMetafields
        if ((template.outputFields && template.outputFields.length > 0) ||
          (template.outputMetafields && template.outputMetafields.length > 0)) {
          const updates = {
            // Include the insert modes in the updates object
            insertModes: {}
          };

          // First, check for existing product content to ensure 'add' mode works correctly
          let existingContent = {};

          // For body_html, we need to make a special effort to find existing content
          if (product) {
            console.log(`Checking existing content for product ${product.id}`);

            // Try multiple ways to find the product description
            for (const field of template.outputFields || []) {
              // Special handling for body_html field
              if (field === 'body_html') {
                // Try all possible places where description might be stored
                const possibleSources = [
                  product.body_html,
                  product.descriptionHtml,
                  product.description,
                  // Check if there's an HTML description in metafields
                  product.metafields?.find(m => m.key === 'description_html')?.value,
                  // Check for SEO description
                  product.metafields?.find(m => m.key === 'description' && m.namespace === 'seo')?.value
                ];

                // Use the first non-empty source
                const foundContent = possibleSources.find(source => source && source.trim() !== '');

                if (foundContent) {
                  existingContent[field] = foundContent;
                  console.log(`Found existing content for ${field} from alternate source: ${foundContent.substring(0, 50)}...`);
                } else {
                  console.log(`No existing content found for ${field} in any source`);
                }
              }
              // Standard handling for other fields
              else if (product[field]) {
                existingContent[field] = product[field];
                console.log(`Found existing content for ${field}: ${product[field].substring(0, 50)}...`);
              } else {
                console.log(`No existing content found for ${field} in product data`);
              }
            }
          }

          // Process both standard fields and metafields from the template
          // Create a combined list of all output targets
          const allOutputs = [...(template.outputFields || [])];

          // Add metafields to the list if they exist - check BOTH metafields and outputMetafields properties
          // First check for metafields property
          if (template.metafields && template.metafields.length > 0) {
            console.log('Processing metafields from template.metafields:', template.metafields);
            template.metafields.forEach(metafield => {
              // Format metafield ID as expected by the system
              const metafieldId = `metafield.${metafield.namespace}.${metafield.key}`;
              if (!allOutputs.includes(metafieldId)) {
                allOutputs.push(metafieldId);
                console.log(`Added metafield to outputs: ${metafieldId}`);
              }
            });
          }

          // CRITICAL: Also check for outputMetafields property which is used in your template
          if (template.outputMetafields && template.outputMetafields.length > 0) {
            console.log('Processing from template.outputMetafields:', template.outputMetafields);

            // Output metafields may be objects or strings, we need to handle both formats
            template.outputMetafields.forEach(metafieldItem => {
              // Convert object format to string format if needed
              let metafieldId = metafieldItem;

              // If it's an object with namespace and key properties, convert to string format
              if (typeof metafieldItem === 'object' && metafieldItem !== null) {
                if (metafieldItem.namespace && metafieldItem.key) {
                  metafieldId = `metafield.${metafieldItem.namespace}.${metafieldItem.key}`;
                  console.log(`Converted metafield object to string format: ${metafieldId}`);
                } else {
                  console.log(`Invalid metafield object format:`, metafieldItem);
                  return; // Skip this item
                }
              }

              // Ensure the metafieldId is a string and in the correct format
              if (typeof metafieldId === 'string' && !allOutputs.includes(metafieldId)) {
                // Make sure it has the metafield. prefix
                if (!metafieldId.startsWith('metafield.')) {
                  metafieldId = `metafield.${metafieldId}`;
                }

                allOutputs.push(metafieldId);
                console.log(`Added outputMetafield to outputs: ${metafieldId}`);
              }
            });
          }

          console.log('Processing all outputs in TemplateTools:', allOutputs);

          // Process each output field
          for (const field of allOutputs) {
            if (field.startsWith('metafield.')) {
              // Get the metafield parts (namespace.key)
              const metafieldParts = field.replace('metafield.', '').split('.');
              if (metafieldParts.length === 2) {
                const [namespace, key] = metafieldParts;

                // Look for the value in response with different key formats
                const metafieldKey = `${namespace}.${key}`;

                // Simple approach: first check if content is a string or object
                let metafieldValue = '';

                if (typeof response.content === 'string') {
                  // If the entire response is a string, use it for all fields
                  console.log(`Response content is a string, using full response for ${field}`);
                  metafieldValue = response.content;
                } else {
                  // For objects, try both namespaced and direct formats
                  // This matches how text templates would handle it
                  metafieldValue = response.content[metafieldKey] || response.content[field] || '';
                  console.log(`Looking for metafield in response under keys: ${metafieldKey} or ${field}`);
                }

                // If not found, try additional formats or use the entire response
                if (!metafieldValue) {
                  // Try without the metafield. prefix
                  const shortKey = key;
                  metafieldValue = response.content[shortKey] || '';
                  console.log(`Trying simple key format: ${shortKey}`);

                  // If still not found, use the entire response as a fallback
                  if (!metafieldValue) {
                    console.log(`No content found for metafield ${field}, using full response content as fallback`);
                    metafieldValue = typeof response.content === 'object' ?
                      JSON.stringify(response.content) : response.content;
                    console.log(`Using fallback content for ${field}:`, {
                      type: typeof metafieldValue,
                      length: typeof metafieldValue === 'string' ? metafieldValue.length : 'N/A',
                      preview: typeof metafieldValue === 'string' ? metafieldValue.substring(0, 50) + '...' : metafieldValue
                    });
                  }
                }

                // Create a separate metafields object if it doesn't exist
                if (!updates.metafields) {
                  updates.metafields = {};
                }

                // Store the metafield with its full field name to preserve the insert mode
                updates.metafields[field] = metafieldValue;
                console.log(`Setting metafield ${field} in updates.metafields to: ${typeof metafieldValue === 'string' ? metafieldValue.substring(0, 50) : metafieldValue}...`);
              } else {
                console.log(`Invalid metafield format: ${field}`);
              }
            } else {
              // Standard fields are set directly from response content
              let fieldValue = response.content;

              // Special handling for compare_at_price fields to ensure Shopify compatibility
              if (field.includes('compare_at_price') || field.includes('compareAtPrice')) {
                // Normalize compare price values for Shopify compatibility
                if (fieldValue === null || fieldValue === undefined || fieldValue === '' || fieldValue === 'null') {
                  fieldValue = 0;
                  console.log(`Normalized empty compare_at_price value to 0 for field: ${field}`);
                } else if (typeof fieldValue === 'string' && fieldValue.toLowerCase() === 'empty') {
                  fieldValue = 0;
                  console.log(`Normalized 'empty' compare_at_price value to 0 for field: ${field}`);
                } else if (typeof fieldValue === 'string' && fieldValue === '0.00') {
                  fieldValue = 0;
                  console.log(`Normalized string '0.00' compare_at_price value to 0 for field: ${field}`);
                }
              }

              updates[field] = fieldValue;
            }

            // Set the insert mode for this field from the template configuration
            // Get the configured output mode or default to 'exchange'
            // For metafields, check both the regular outputModes and the specific outputMetafieldModes
            let configuredMode = 'exchange';

            if (field.startsWith('metafield.')) {
              // For metafields, first check if there's a specific metafield mode
              const metafieldKey = field.replace('metafield.', '');
              configuredMode = template.outputMetafieldModes?.[metafieldKey] || template.outputModes?.[field] || 'exchange';
              console.log(`Metafield ${field} mode lookup: outputMetafieldModes.${metafieldKey}=${template.outputMetafieldModes?.[metafieldKey]}, outputModes.${field}=${template.outputModes?.[field]}, final mode=${configuredMode}`);
            } else {
              // For standard fields, use the regular outputModes
              configuredMode = template.outputModes?.[field] || 'exchange';
            }

            // Use the configured insert mode for body_html (allow both 'add' and 'exchange')
            if (field === 'body_html') {
              updates.insertModes[field] = configuredMode;
              console.log(`Using configured '${configuredMode}' mode for body_html field in TemplateTools`);

              // Format the content with paragraph tags if needed
              if (updates[field] && typeof updates[field] === 'string' && !updates[field].includes('<p>')) {
                updates[field] = `<p>${updates[field]}</p>`;
                console.log(`Formatted body_html content with paragraph tags`);
              } else if (updates[field] && typeof updates[field] !== 'string') {
                console.log(`Warning: body_html content is not a string: ${typeof updates[field]}`);
                // Convert to string properly if it's an object
                if (typeof updates[field] === 'object' && updates[field] !== null) {
                  console.log('Converting object to formatted string for body_html');
                  // Format the object for readability
                  if (updates[field].body_html) {
                    // If the object has a body_html property, use that directly
                    updates[field] = updates[field].body_html;
                  } else {
                    // Otherwise, format the entire object for readability
                    updates[field] = formatObjectForDescription(updates[field]);
                  }
                } else {
                  // For other types, use String conversion
                  updates[field] = String(updates[field]);
                }

                // Add paragraph tags if needed
                if (!updates[field].includes('<p>')) {
                  updates[field] = `<p>${updates[field]}</p>`;
                }
              }
            } else {
              // Use the configured mode for other fields
              updates.insertModes[field] = configuredMode;
              console.log(`Using ${configuredMode} mode for ${field} field in TemplateTools`);
            }

            // CRITICAL: Pass the existing content for ALL fields that use 'add' mode
            if (updates.insertModes[field] === 'add') {
              // Initialize _originalContent if it doesn't exist
              if (!updates._originalContent) {
                updates._originalContent = {};
              }

              // For standard fields, use the existingContent object
              if (existingContent && existingContent[field]) {
                updates._originalContent[field] = existingContent[field];
                console.log(`Passing existing content directly for ${field}: ${existingContent[field].substring(0, 50)}...`);
              }
              // For metafields, we need to check the product's metafields collection
              else if (field.startsWith('metafield.')) {
                // Extract namespace and key from the field
                const metafieldParts = field.replace('metafield.', '').split('.');
                if (metafieldParts.length === 2) {
                  const [namespace, key] = metafieldParts;

                  // Look for the metafield in the product's metafields
                  if (product.metafields) {
                    const metafield = product.metafields.find(m =>
                      m.namespace === namespace && m.key === key
                    );

                    if (metafield) {
                      updates._originalContent[field] = metafield.value;
                      console.log(`Found existing metafield ${field} content: ${metafield.value ? metafield.value.toString().substring(0, 50) + '...' : 'null or undefined'}`);
                    }
                  }
                }
              }
            }
          }

          // Check if this is a SKU-only template by examining the template name and output fields
          const isSKUTemplate = template.name?.toLowerCase().includes('sku') ||
            (template.outputFields && template.outputFields.some(field =>
              field.includes('sku') || field.includes('variants.sku')
            ));

          // If this is a SKU template, filter out non-SKU fields to prevent price overwrites
          if (isSKUTemplate) {
            console.log('Detected SKU template, filtering updates to preserve prices and other fields');

            // Create filtered updates that only include SKU-related fields
            const filteredUpdates = {
              insertModes: {},
              _originalContent: updates._originalContent || {}
            };

            // Only include SKU-related fields and metafields
            Object.keys(updates).forEach(key => {
              if (key === 'insertModes' || key === '_originalContent' || key === 'metafields') {
                return; // Skip these, they're handled separately
              }

              // Only include SKU-related fields
              if (key.toLowerCase().includes('sku') || key.includes('variants.sku')) {
                filteredUpdates[key] = updates[key];
                if (updates.insertModes && updates.insertModes[key]) {
                  filteredUpdates.insertModes[key] = updates.insertModes[key];
                }
                console.log(`Including SKU field: ${key}`);
              } else {
                console.log(`Skipping non-SKU field: ${key} (preserving existing value)`);
              }
            });

            // Include SKU-related metafields
            if (updates.metafields) {
              Object.keys(updates.metafields).forEach(metafieldKey => {
                if (metafieldKey.toLowerCase().includes('sku')) {
                  if (!filteredUpdates.metafields) {
                    filteredUpdates.metafields = {};
                  }
                  filteredUpdates.metafields[metafieldKey] = updates.metafields[metafieldKey];
                  console.log(`Including SKU metafield: ${metafieldKey}`);
                } else {
                  console.log(`Skipping non-SKU metafield: ${metafieldKey}`);
                }
              });
            }

            // Log what we're updating (filtered for SKU template)
            console.log(`Updating product ${product.id} with SKU-only updates:`, {
              fields: Object.keys(filteredUpdates).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
              metafields: filteredUpdates.metafields ? Object.keys(filteredUpdates.metafields) : 'none',
              insertModes: filteredUpdates.insertModes,
              hasOriginalContent: !!filteredUpdates._originalContent
            });

            if (handleProductChange) {
              console.log(`📝 CALLING handleProductChange for SKU template - product ${i + 1}/${selectedProducts.length}:`, {
                productId: product.id,
                productIndex: i,
                productTitle: product.title,
                filteredUpdateFields: Object.keys(filteredUpdates).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
                isSKUTemplate: true
              });

              handleProductChange(product.id, 'all', filteredUpdates);

              console.log(`✅ SKU template handleProductChange completed for product ${i + 1}`);
            } else {
              console.warn(`⚠️ handleProductChange is not available for SKU template - product ${i + 1}`);
            }
          } else {
            // Log what we're updating (normal template)
            console.log(`Updating product ${product.id} with:`, {
              fields: Object.keys(updates).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
              metafields: updates.metafields ? Object.keys(updates.metafields) : 'none',
              insertModes: updates.insertModes,
              hasOriginalContent: !!updates._originalContent
            });

            if (handleProductChange) {
              // CRITICAL: Make sure metafields are properly included in the updates
              // This ensures they're processed correctly by the product field handlers
              console.log(`📝 CALLING handleProductChange for product ${i + 1}/${selectedProducts.length}:`, {
                productId: product.id,
                productIndex: i,
                productTitle: product.title,
                updateFields: Object.keys(updates).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
                hasHandleProductChange: typeof handleProductChange === 'function'
              });

              handleProductChange(product.id, 'all', updates);

              console.log(`✅ handleProductChange completed for product ${i + 1}`);
            } else {
              console.warn(`⚠️ handleProductChange is not available for product ${i + 1}`);
            }
          }
        }

        // Update progress
        processedCount++;
        const progress = Math.min(Math.round((processedCount / selectedProducts.length) * 100), 100);
        setWorkflowExecutionStatus({
          status: 'processing',
          message: `Template ${currentTemplateNumber}/${totalTemplates}: Processing image template '${template.name}': ${progress}% complete...`
        });
      }

      // Record template usage once after all products are processed
      // This prevents concurrent Firestore updates that can cause errors
      if (selectedProducts.length > 0) {
        try {
          await recordTemplateUsage(user?.uid, currentShop?.id, template.id);
          console.log('Template usage recorded successfully');
        } catch (error) {
          console.warn('Error recording template usage:', error);
          // Non-critical error, don't throw
        }
      }

      // Add results to the persistent sidebar only if sidebarOnly is true
      if (results.length > 0 && Boolean(template.sidebarOnly)) {
        console.log('Template has sidebarOnly enabled, adding results to persistent sidebar');
        setPersistentResults(prev => {
          const existingResultsMap = {};
          prev.forEach(result => {
            if (result.id) {
              existingResultsMap[result.id] = true;
            }
          });

          const uniqueNewResults = results.filter(result => {
            return !result.id || !existingResultsMap[result.id];
          });

          return [...prev, ...uniqueNewResults];
        });

        // Show the sidebar only for sidebarOnly templates
        setSidebarVisible(true);
      } else if (results.length > 0) {
        console.log('Template does not have sidebarOnly enabled, skipping persistent sidebar');
      }

      // Only update workflow status as complete if there are no remaining templates
      if (!remainingIds || remainingIds.length === 0) {
        // This is the final template, so we can mark the workflow as complete
        setIsExecuting(false);
        setProgressSafe(100); // Set to exactly 100%
        setWorkflowExecutionStatus({
          status: 'completed',
          message: `Template ${currentTemplateNumber}/${totalTemplates}: Image template '${template.name}' processed successfully!`
        });

        // Clear the status after a short delay to prevent it from staying stuck
        setTimeout(() => {
          setWorkflowExecutionStatus(null);
          setProgressSafe(0); // Reset progress to 0
          setIsExecuting(false); // Ensure execution state is reset
          console.log('Progress, execution state, and workflow status reset - no remaining templates');
        }, 1500); // Shorter timeout for better UX
      } else {
        // There are remaining templates to process, so we'll update the status but keep executing
        // Update template index - image template (index 0) is now complete, move to next
        setCurrentTemplateIndex(1); // Next template after image template

        // Calculate progress based on current template position in the workflow
        const currentProgress = Math.round((currentTemplateNumber / totalTemplates) * 100);
        setProgressSafe(currentProgress);
        setWorkflowExecutionStatus({
          status: 'processing',
          message: normalizeTemplateMessage(
            `Image template '${template.name}' completed. Processing remaining templates...`,
            1, // Next template index
            selectedTemplates.length // Use immediate value
          )
        });
        console.log('Image template completed, continuing with remaining templates');
      }

      // Ensure execution state is properly reset even if there are no more templates
      if (!remainingIds || remainingIds.length === 0) {
        console.log('No remaining templates to process, ensuring execution state is reset');
        // Double-check that execution state is reset after the timeout
        setTimeout(() => {
          if (isExecuting) {
            console.log('Execution state was still true, forcing reset');
            setIsExecuting(false);
            setProgressSafe(0);
            setWorkflowExecutionStatus(null);
          }
        }, 2000);
      }

      // Process any remaining templates in the workflow
      if (remainingIds && remainingIds.length > 0) {
        console.log('Processing remaining templates after image template:', {
          count: remainingIds.length,
          templateIds: remainingIds
        });

        // Wait a short delay to ensure UI updates properly
        setTimeout(async () => {
          try {
            // Execute the remaining templates
            // Set executing state back to true for the remaining templates
            setIsExecuting(true);

            const remainingResult = await executeTemplatesDirectly(
              remainingIds,
              selectedProducts, // Pass the full product objects
              (status) => {
                // Create intelligent progress wrapper that maintains workflow context
                console.log('🔄 Remaining Templates Progress Update:', {
                  message: status.message,
                  progress: status.progress,
                  selectedTemplatesLength: selectedTemplates.length,
                  currentTemplateNumber: currentTemplateNumber,
                  totalTemplates: totalTemplates,
                  remainingIdsLength: remainingIds.length
                });

                // Calculate correct progress based on workflow position
                const baseProgress = (currentTemplateNumber / totalTemplates) * 100;
                const remainingTemplateCount = remainingIds.length;
                const remainingProgressRange = 100 - baseProgress;

                // Scale the progress correctly for remaining templates
                const scaledProgress = status.progress ?
                  baseProgress + (status.progress * remainingProgressRange / 100) : baseProgress;

                console.log(`Progress scaling: base=${baseProgress}%, range=${remainingProgressRange}%, scaled=${scaledProgress}%`);
                setProgressSafe(scaledProgress);

                // Use immediate values for remaining templates
                const immediateTemplateCount = selectedTemplates.length;

                // Calculate current template index for remaining templates
                // Extract template number from original message (multiple patterns)
                let originalTemplateNumber = 1; // Default to first remaining template

                // Try different patterns to extract template number
                let templateMatch = status.message?.match(/Template (\d+)\/(\d+):/);
                if (!templateMatch) {
                  templateMatch = status.message?.match(/Processing template (\d+) of (\d+)/i);
                }
                if (!templateMatch) {
                  templateMatch = status.message?.match(/template (\d+)\/(\d+)/i);
                }
                if (!templateMatch) {
                  templateMatch = status.message?.match(/(\d+) of (\d+)/);
                }
                if (!templateMatch) {
                  templateMatch = status.message?.match(/(\d+)\/(\d+)/);
                }

                if (templateMatch) {
                  originalTemplateNumber = parseInt(templateMatch[1]);
                }

                // Calculate correct workflow position
                // Image template is at index 0, remaining templates start at index 1
                // If this is the 1st remaining template, it should be at workflow index 1
                // If this is the 2nd remaining template, it should be at workflow index 2, etc.
                const remainingTemplateIndex = currentTemplateNumber + originalTemplateNumber - 1;

                console.log(`📊 Template index calculation:`);
                console.log(`  - Original template number in remaining context: ${originalTemplateNumber}`);
                console.log(`  - Current template number (after image): ${currentTemplateNumber}`);
                console.log(`  - Final workflow index: ${remainingTemplateIndex}`);
                console.log(`  - Should display as: Template ${remainingTemplateIndex + 1}/${immediateTemplateCount}`);

                // Use centralized message normalization with immediate values
                const correctedMessage = normalizeTemplateMessage(
                  status.message,
                  remainingTemplateIndex,
                  immediateTemplateCount
                );

                console.log(`Remaining template progress: index=${remainingTemplateIndex}, total=${immediateTemplateCount} (immediate values)`);

                // Update current template index
                setCurrentTemplateIndex(remainingTemplateIndex);

                const updatedStatus = {
                  ...status,
                  message: correctedMessage,
                  progress: scaledProgress
                };

                console.log('Corrected remaining template status:', updatedStatus);
                setWorkflowExecutionStatus(updatedStatus);

                // Update progress bar to show workflow progress (not template progress)
                setProgressSafe(scaledProgress);

                // IMPORTANT: Only process results ONCE when workflow is completed
                // Do NOT process on every progress update - this causes multiple processing!
                if (status.status === 'completed') {
                  // Process results if available
                  if (status.results && Object.keys(status.results).length > 0) {
                    processTemplateResults(status.results, selectedTemplates);
                  }

                  setIsExecuting(false);
                  setProgressSafe(100); // Ensure we end at exactly 100%

                  // Clear the status after a delay
                  setTimeout(() => {
                    setWorkflowExecutionStatus(null);
                    setProgressSafe(0);
                  }, 1500);
                }
              }
            );

            console.log('Remaining templates processed:', {
              success: !remainingResult.error,
              result: remainingResult
            });
          } catch (remainingError) {
            console.error('Error processing remaining templates:', remainingError);
          }
        }, 1000);
      }
    } catch (error) {
      console.error('Error processing image template directly:', error);
      setWorkflowExecutionStatus({
        status: 'error',
        message: `Error processing template: ${error.message}`
      });
    }
  };

  const handleExecuteSingleTemplate = async (templateId) => {
    if (!selectedProducts || selectedProducts.length === 0) {
      setError('Please select at least one product');
      return;
    }

    try {
      // Get the template details directly from the database to ensure we have the latest data
      const template = await getTemplateById(templateId);
      if (!template) {
        setError('Template not found');
        return;
      }

      // Debug the template data
      console.log('EXECUTE TEMPLATE - Template data:', {
        id: template.id,
        name: template.name,
        type: template.type,
        useFeaturedImage: template.useFeaturedImage,
        'typeof useFeaturedImage': typeof template.useFeaturedImage,
        'Boolean(useFeaturedImage)': Boolean(template.useFeaturedImage)
      });

      // Check if this is an image template - route based on analysis mode
      const directAnalysisMode = template.imageAnalysisMode;
      const directIsLegacyFeatured = Boolean(template.useFeaturedImage) && !directAnalysisMode;
      const directIsFeatured = directAnalysisMode === 'featured' || directIsLegacyFeatured;
      const directIsAll = directAnalysisMode === 'all';

      if (template.type === 'image' && directIsFeatured) {
        // Process directly without showing any modal
        console.log('EXECUTE TEMPLATE - Featured mode, processing directly');
        processImageTemplateDirectly(template);
        return;
      }

      if (template.type === 'image' && directIsAll) {
        // Analyze All mode — process directly without any modal
        console.log('EXECUTE TEMPLATE - Analyze All mode, processing directly');
        processImageTemplateDirectly(template);
        return;
      }

      // For image templates with 'manual' mode only, show the image selection UI
      if (template.type === 'image') {
        console.log('EXECUTE TEMPLATE - Manual mode, showing image selection UI');
        setCurrentImageTemplate(template);
        setShowImageTemplateExecutor(true);
        return;
      }
    } catch (error) {
      console.error('Error in handleExecuteSingleTemplate:', error);
      setError(`Error executing template: ${error.message}`);
      return;
    }

    // For regular product templates, continue with normal execution
    setIsExecuting(true);
    setError('');
    setWorkflowExecutionStatus(null);

    try {
      // Check if user has enough credits for all selected products
      const productsToUpdate = selectedProducts.length;
      const hasCredits = await hasEnoughCredits(user.uid, productsToUpdate);

      if (!hasCredits) {
        setError(`Not enough credits. You need ${productsToUpdate} credits to update these products.`);
        setIsExecuting(false);
        return;
      }

      console.log('Executing single template directly:', templateId);

      // Execute the template directly with full product objects instead of just IDs
      const executionId = await executeTemplatesDirectly(
        [templateId],
        selectedProducts, // Pass the full product objects
        (status) => {
          // Update progress and status
          setProgressSafe(status.progress || 0);
          setWorkflowExecutionStatus(status);

          // IMPORTANT: Only process results ONCE when execution is completed
          // Do NOT process on every progress update - this causes multiple processing!
          if (status.status === 'completed') {
            // Process results if available
            if (status.results && Object.keys(status.results).length > 0) {
              processTemplateResults(status.results, selectedTemplates);
            }

            setIsExecuting(false);
            setProgressSafe(0);
          }
        }
      );

      console.log('Template execution started with ID:', executionId);
    } catch (err) {
      console.error('Error executing single template:', err);
      setError(err.message || 'Failed to execute template');
      setIsExecuting(false);
      setProgressSafe(0); // Use setProgressSafe instead of setProgress
      setWorkflowExecutionStatus(null);
    }
  };

  // Function to cancel/abort current execution
  const handleCancelExecution = () => {
    console.log('Canceling template/workflow execution');
    setIsExecuting(false);
    setProgressSafe(0);
    setWorkflowExecutionStatus(null);
    setError('Execution canceled by user');

    // Clear any ongoing operations
    // Note: This is a client-side cancellation. Server-side operations may continue.
  };

  return (
    <div>
      <Stack vertical>
        {error && (
          <Banner status="critical" onDismiss={() => setError('')}>
            <p>{error}</p>
          </Banner>
        )}

        <TemplateSelector
          initialSelectedTemplates={selectedTemplates}
          onTemplateSelect={(templates) => setSelectedTemplates(templates)}
          onExecute={handleExecuteWorkflow}
          disabled={disabled || isExecuting}
          selectedProducts={selectedProducts}
          isExecuting={isExecuting}
          progress={progress}
          workflowExecutionStatus={workflowExecutionStatus}
          onOpenTemplateManager={() => setIsOpen(true)}
          onOpenWorkflowManager={() => setShowWorkflowModal(true)}
          onOpenHelp={() => setShowHelpModal(true)}
        />
      </Stack>

      {/* Progress container outside Stack for full width */}
      {isExecuting && (
        <div className="template-tools-progress-container" style={{
          marginTop: '1rem',
          marginBottom: '2rem',
          width: '100%',
          maxWidth: 'none'
        }}>
          {workflowExecutionStatus && (
            <div className="template-tools-progress" style={{
              padding: '1rem',
              width: '100%',
              backgroundColor: '#f6f6f7',
              borderRadius: '8px',
              border: '1px solid #e1e3e5'
            }}>
              <div className="template-tools-progress-message" style={{
                marginBottom: '0.75rem',
                fontSize: '14px',
                fontWeight: '500',
                color: '#202223',
                wordWrap: 'break-word',
                whiteSpace: 'normal',
                width: '100%'
              }}>
                {/* Fix percentage in status message if needed */}
                {workflowExecutionStatus.message && workflowExecutionStatus.message.includes('%')
                  ? workflowExecutionStatus.message.replace(/\d+%/g, match => {
                    const percentage = parseInt(match, 10);
                    return percentage > 100 ? '100%' : match;
                  })
                  : workflowExecutionStatus.message
                }
              </div>
              {(isExecuting || workflowExecutionStatus) && (
                <div style={{ width: '100%' }}>
                  <ProgressBar progress={Math.min(progress, 100)} />
                  <div style={{ marginTop: '0.75rem', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                    {isPaused ? (
                      <Button
                        onClick={handleResumeWorkflow}
                        primary
                        size="medium"
                      >
                        ▶️ Continue Workflow
                      </Button>
                    ) : (
                      <Button
                        onClick={handleCancelExecution}
                        destructive
                        size="slim"
                      >
                        Cancel Execution
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Template Manager Modal */}
      <Modal
        open={isOpen}
        onClose={() => setIsOpen(false)}
        title="Template Manager"
        primaryAction={{
          content: 'Create New Template',
          onAction: () => {
            resetForm();
            setShowSaveModal(true);
          }
        }}
        secondaryActions={[
          {
            content: 'Execute Selected',
            onAction: () => {
              setIsOpen(false);
              handleExecute();
            },
            disabled: selectedTemplates.length === 0 || !selectedProducts || selectedProducts.length === 0
          },
          {
            content: 'Help',
            onAction: () => setShowHelpModal(true)
          },
          {
            content: 'Done',
            onAction: () => setIsOpen(false)
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            {loading ? (
              <TextContainer>
                <p>Loading templates...</p>
              </TextContainer>
            ) : (
              <>
                <TextField
                  placeholder="Search templates..."
                  value={searchValue}
                  onChange={setSearchValue}
                  prefix={<SearchMinor />}
                  clearButton
                  onClearButtonClick={() => setSearchValue('')}
                />

                {filteredTemplates.length === 0 && (
                  <TextContainer>
                    <p>{searchValue ? `No templates found matching "${searchValue}"` : 'No templates saved yet. Click "Create New Template" to get started.'}</p>
                  </TextContainer>
                )}

                <TemplateList
                  templates={searchValue ? filteredTemplates : orderedTemplates}
                  selectedTemplates={selectedTemplates}
                  handleTemplateSelect={handleTemplateSelect}
                  handleDelete={handleDelete}
                  handleEdit={handleEdit}
                  handleDuplicate={handleDuplicateTemplate}
                  handleExecute={handleExecuteSingleTemplate}
                  onReorder={handleReorderTemplates}
                  aiModels={availableAIModels}
                />
              </>
            )}
          </Stack>
        </Modal.Section>
      </Modal>

      {/* Workflow Manager Modal */}
      <Modal
        open={showWorkflowModal}
        onClose={() => {
          setShowWorkflowModal(false);
          setError('');
          setWorkflowSearchValue('');
        }}
        title="Workflow Manager"
        primaryAction={{
          content: 'Create New Workflow',
          onAction: handleCreateWorkflow
        }}
        secondaryActions={[
          {
            content: 'Help',
            onAction: () => setShowHelpModal(true)
          },
          {
            content: 'Done',
            onAction: () => setShowWorkflowModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            {error && (
              <Banner status="critical" onDismiss={() => setError('')}>
                <p>{error}</p>
              </Banner>
            )}

            <TextField
              label="Search workflows"
              value={workflowSearchValue}
              onChange={setWorkflowSearchValue}
              clearButton
              onClearButtonClick={() => setWorkflowSearchValue('')}
              prefix={<Icon source={SearchMinor} />}
              autoComplete="off"
            />

            <WorkflowList
              workflows={filteredWorkflows}
              selectedTemplates={selectedTemplates}
              handleEditWorkflow={handleEditWorkflow}
              handleDelete={handleDeleteClick}
              handleDuplicate={handleDuplicateWorkflow}
              executeWorkflow={handleExecuteWorkflowFromList}
              templates={templates}
              aiModels={availableAIModels}
              disabled={!selectedProducts || selectedProducts.length === 0}
            />
          </Stack>
        </Modal.Section>
      </Modal>

      {/* Create/Edit Workflow Modal */}
      <Modal
        open={showCreateWorkflowModal}
        onClose={() => {
          setShowCreateWorkflowModal(false);
          setError('');
          setWorkflowName('');
          setSelectedTemplates([]);
          setEditingWorkflow(null);
        }}
        title={editingWorkflow ? 'Edit Workflow' : 'Create Workflow'}
        primaryAction={{
          content: editingWorkflow ? 'Save Changes' : 'Create Workflow',
          onAction: handleSaveWorkflow
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowCreateWorkflowModal(false);
              setError('');
              setWorkflowName('');
              setSelectedTemplates([]);
              setEditingWorkflow(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical>
            <TextField
              label="Workflow Name"
              value={workflowName}
              onChange={setWorkflowName}
              autoComplete="off"
              error={error && !workflowName ? 'Name is required' : ''}
            />

            {/* Error Banner for workflow validation */}
            {error && workflowName && (
              <Banner status="critical">
                <p>{error}</p>
              </Banner>
            )}

            {/* Search bar for templates */}
            <div style={{ marginTop: '16px', marginBottom: '16px' }}>
              <TextField
                label="Search Templates"
                value={searchValue}
                onChange={setSearchValue}
                placeholder="Type to search for templates"
                clearButton
                onClearButtonClick={() => setSearchValue('')}
                autoComplete="off"
                prefix={<span style={{ color: '#637381' }}>🔍</span>}
                helpText="Search for templates by name to quickly find what you need"
              />
            </div>

            {/* Dynamic Image Template Guidance Banner */}
            {(() => {
              // Use WORKFLOW ORDER (selectedTemplates array position) - NOT global templateOrder!
              const selectedTemplateObjects = selectedTemplates.map(templateId =>
                templates.find(t => t.id === templateId)
              ).filter(Boolean);

              const imageTemplates = selectedTemplateObjects.filter(t => t.type === 'image');
              const hasImageTemplates = imageTemplates.length > 0;

              if (!hasImageTemplates) {
                return (
                  <Banner status="info">
                    <p>
                      <strong>📋 Tip:</strong> If you plan to use Image Analyzer Templates, they must execute first in the workflow.
                    </p>
                  </Banner>
                );
              }

              // Check WORKFLOW POSITION (selectedTemplates array index) for execution order
              const imageTemplatePositions = imageTemplates.map(imageTemplate => {
                const position = selectedTemplates.findIndex(templateId => templateId === imageTemplate.id);
                return { template: imageTemplate, position };
              });

              // Find image templates that are NOT at the beginning of the workflow
              const nonFirstImageTemplates = imageTemplatePositions.filter(item => item.position > 0);

              console.log('🔍 WORKFLOW ORDER Validation (Banner):', {
                selectedTemplates,
                selectedTemplateObjects: selectedTemplateObjects.map((t, index) => ({
                  name: t.name,
                  type: t.type,
                  workflowPosition: index + 1
                })),
                imageTemplatePositions,
                nonFirstImageTemplates
              });

              if (nonFirstImageTemplates.length > 0) {
                const problemNames = nonFirstImageTemplates.map(item =>
                  `${item.template.name} (position ${item.position + 1})`
                ).join(', ');

                return (
                  <Banner status="warning">
                    <p>
                      <strong>⚠️ Workflow Order Issue:</strong> Image Templates (<strong>{problemNames}</strong>)
                      are positioned to execute AFTER other templates in this workflow.
                      Image Templates must be placed at the BEGINNING of the workflow for technical reasons.
                    </p>
                  </Banner>
                );
              } else {
                return (
                  <Banner status="success">
                    <p>
                      <strong>✅ Perfect:</strong> Your Image Templates ({imageTemplates.map(t => t.name).join(', ')})
                      are correctly positioned at the BEGINNING of the workflow!
                    </p>
                  </Banner>
                );
              }
            })()}

            <TemplateList
              templates={templates
                .filter(template => template.name.toLowerCase().includes(searchValue.toLowerCase()))
                .sort((a, b) => {
                  // Sort templates based on the template order from Firebase
                  const aIndex = templateOrder[a.id] !== undefined ? templateOrder[a.id] : Number.MAX_SAFE_INTEGER;
                  const bIndex = templateOrder[b.id] !== undefined ? templateOrder[b.id] : Number.MAX_SAFE_INTEGER;

                  // If both templates have positions, sort by their position
                  if (aIndex !== Number.MAX_SAFE_INTEGER && bIndex !== Number.MAX_SAFE_INTEGER) {
                    return aIndex - bIndex;
                  }

                  // If only one template has a position, prioritize it
                  if (aIndex !== Number.MAX_SAFE_INTEGER) return -1;
                  if (bIndex !== Number.MAX_SAFE_INTEGER) return 1;

                  // If neither has a position, sort alphabetically by name
                  return a.name.localeCompare(b.name);
                })
              }
              selectedTemplates={selectedTemplates}
              handleTemplateSelect={handleTemplateSelect}
              handleExecute={handleExecuteSingleTemplate}
              aiModels={availableAIModels}
            />

            {/* Stop Markers Section (Debug Mode) */}
            {selectedTemplates.length > 0 && (
              <Card title="🛑 Debug Mode - Stop Markers" sectioned>
                <Stack vertical spacing="tight">
                  <Banner status="warning">
                    <p>
                      <strong>⚠️ Important:</strong> Stop markers only work when you execute this workflow
                      using the <strong>▶️ Play button</strong> in the workflow manager menu.
                      They do NOT work with the "Execute" button in the Template Selector.
                    </p>
                  </Banner>

                  <Text variant="bodyMd" as="p" color="subdued">
                    Set stop markers to pause the workflow after specific templates.
                    This allows you to review results before continuing.
                  </Text>

                  {selectedTemplates.map((templateId, index) => {
                    const template = templates.find(t => t.id === templateId);
                    if (!template) return null;

                    return (
                      <Checkbox
                        key={templateId}
                        label={`Pause after "${template.name}" (Template ${index + 1})`}
                        checked={stopMarkers.includes(index)}
                        onChange={() => handleToggleStopMarker(index)}
                      />
                    );
                  })}

                  {stopMarkers.length > 0 && (
                    <Banner status="info">
                      <p>
                        Workflow will pause after {stopMarkers.length} template(s).
                        You can review the results and click "Continue" to proceed.
                      </p>
                    </Banner>
                  )}
                </Stack>
              </Card>
            )}
          </Stack>
        </Modal.Section>
      </Modal>

      {/* Help Modal */}
      <TemplateHelp
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Template Delete Confirmation Modal */}
      <Modal
        open={showDeleteTemplateModal}
        onClose={() => {
          setShowDeleteTemplateModal(false);
          setItemToDelete(null);
        }}
        title="Delete Template"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: () => handleConfirmDelete('template')
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDeleteTemplateModal(false);
              setItemToDelete(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Are you sure you want to delete the template "{itemToDelete?.name}"? This action cannot be undone.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Workflow Delete Confirmation Modal */}
      <Modal
        open={showDeleteWorkflowModal}
        onClose={() => {
          setShowDeleteWorkflowModal(false);
          setItemToDelete(null);
        }}
        title="Delete Workflow"
        primaryAction={{
          content: 'Delete',
          destructive: true,
          onAction: () => handleConfirmDelete('workflow')
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowDeleteWorkflowModal(false);
              setItemToDelete(null);
            }
          }
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>Are you sure you want to delete the workflow "{itemToDelete?.name}"? This action cannot be undone.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Save Template Modal */}
      <Modal
        open={showSaveModal}
        onClose={() => {
          setShowSaveModal(false);
          setError('');
          resetForm();
        }}
        title={editingTemplate.id ? 'Edit Template' : 'Create Template'}
        primaryAction={{
          content: editingTemplate.id ? 'Update Template' : 'Create Template',
          onAction: handleSave
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => {
              setShowSaveModal(false);
              setError('');
              resetForm();
            }
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">

            {/* ① BASICS */}
            <Card sectioned>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>1</div>
                <div><Heading>Basics</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>Name your template and choose its type</p></div>
              </div>
              <FormLayout>
                <TextField
                  label="Template Name"
                  value={editingTemplate.name}
                  onChange={(value) => setEditingTemplate(prev => ({ ...prev, name: value }))}
                  autoComplete="off"
                  error={error && !editingTemplate.name ? 'Name is required' : ''}
                />
                <Select
                  label="Template Type"
                  options={templateTypes}
                  value={editingTemplate.type}
                  onChange={(value) => {
                    const newState = { ...editingTemplate, type: value };
                    if (value === 'image') {
                      const currentModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                      const isCurrentModelVisionCapable = currentModel?.type === 'vision';
                      if (!isCurrentModelVisionCapable) {
                        const firstVisionModel = availableAIModels.find(m => m.type === 'vision');
                        if (firstVisionModel) {
                          newState.modelId = firstVisionModel.id;
                          console.log('Automatically selected vision model:', firstVisionModel.displayName);
                        }
                      }
                    }
                    setEditingTemplate(newState);
                  }}
                  helpText={
                    editingTemplate.type === 'product' ? '📝 Uses AI to generate text content from product data' :
                      editingTemplate.type === 'image' ? '🖼️ Uses a vision model to analyze product images' :
                        editingTemplate.type === 'tool' ? '⚙️ Executes automated tools without AI credits' :
                          'Select the type of template you want to create'
                  }
                />
              </FormLayout>
            </Card>

            {/* Image Settings - only for image type */}
            {editingTemplate.type === 'image' && (
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>2</div>
                  <div><Heading>Image Settings</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>Configure how product images are selected and analyzed</p></div>
                </div>
                <Stack vertical spacing="tight">
                  <Select
                    label="Image Analysis Mode"
                    options={[
                      { label: 'Manual Selection — Select images manually during execution', value: 'manual' },
                      { label: 'Featured Image — Automatically use the first/featured image', value: 'featured' },
                      { label: 'Analyze All Images — Analyze every product image individually (1 credit per image)', value: 'all' }
                    ]}
                    value={editingTemplate.imageAnalysisMode || (editingTemplate.useFeaturedImage ? 'featured' : 'manual')}
                    onChange={(value) => {
                      console.log('Setting imageAnalysisMode to:', value);
                      setEditingTemplate(prev => ({
                        ...prev,
                        imageAnalysisMode: value,
                        useFeaturedImage: value === 'featured'
                      }));
                    }}
                    helpText={
                      editingTemplate.imageAnalysisMode === 'all' || (!editingTemplate.imageAnalysisMode && false)
                        ? '⚠️ Analyze All mode costs 1 credit per image per product.'
                        : editingTemplate.imageAnalysisMode === 'featured' || editingTemplate.useFeaturedImage
                          ? 'The featured (first) image will be used automatically.'
                          : 'You will select which images to analyze during template execution.'
                    }
                  />

                  {(editingTemplate.imageAnalysisMode === 'all') && (
                    <Banner status="warning">
                      <p><strong>Credit Cost:</strong> Analyze All mode sends each product image individually to the Vision model. A product with 5 images will use 5 credits.</p>
                    </Banner>
                  )}
                </Stack>
              </Card>
            )}

            {/* ② INPUT FIELDS - Only for product templates */}
            {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>2</div>
                  <div><Heading>Input Fields</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>What data does the AI receive as context?</p></div>
                </div>

                <FormLayout>
                  {/* Product Fields */}
                  <Card sectioned>
                    <TextContainer>
                      <Subheading>Product Fields</Subheading>
                    </TextContainer>
                    <FormLayout>
                      <Checkbox label="Title" checked={editingTemplate.inputFields?.includes('title')} onChange={(checked) => handleFieldChange('title', checked, 'input')} />
                      <Checkbox label="Description" checked={editingTemplate.inputFields?.includes('body_html')} onChange={(checked) => handleFieldChange('body_html', checked, 'input')} />
                      <Checkbox label="Product Type" checked={editingTemplate.inputFields?.includes('product_type')} onChange={(checked) => handleFieldChange('product_type', checked, 'input')} />
                      <Checkbox label="Tags" checked={editingTemplate.inputFields?.includes('tags')} onChange={(checked) => handleFieldChange('tags', checked, 'input')} />
                      <Checkbox label="Handle" checked={editingTemplate.inputFields?.includes('handle')} onChange={(checked) => handleFieldChange('handle', checked, 'input')} />
                    </FormLayout>
                  </Card>

                  {/* SEO Fields */}
                  <Card sectioned>
                    <TextContainer><Subheading>Media Fields</Subheading></TextContainer>
                    <FormLayout>
                      <Checkbox label="Product Images Alt Text" checked={editingTemplate.inputFields?.includes('alt_text')} onChange={(checked) => handleFieldChange('alt_text', checked, 'input')} />
                    </FormLayout>
                  </Card>

                  {/* Options Fields */}
                  <Card sectioned>
                    <TextContainer><Subheading>Options</Subheading></TextContainer>
                    <FormLayout>
                      <Checkbox label="Option1 Name" checked={editingTemplate.inputFields?.includes('option1_name')} onChange={(checked) => handleFieldChange('option1_name', checked, 'input')} />
                      <Checkbox label="Option1 Values" checked={editingTemplate.inputFields?.includes('option1_values')} onChange={(checked) => handleFieldChange('option1_values', checked, 'input')} />
                      <Checkbox label="Option2 Name" checked={editingTemplate.inputFields?.includes('option2_name')} onChange={(checked) => handleFieldChange('option2_name', checked, 'input')} />
                      <Checkbox label="Option2 Values" checked={editingTemplate.inputFields?.includes('option2_values')} onChange={(checked) => handleFieldChange('option2_values', checked, 'input')} />
                      <Checkbox label="Option3 Name" checked={editingTemplate.inputFields?.includes('option3_name')} onChange={(checked) => handleFieldChange('option3_name', checked, 'input')} />
                      <Checkbox label="Option3 Values" checked={editingTemplate.inputFields?.includes('option3_values')} onChange={(checked) => handleFieldChange('option3_values', checked, 'input')} />
                    </FormLayout>
                  </Card>

                  {/* Variant Fields */}
                  {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                    <Card sectioned>
                      <TextContainer><Subheading>Variant Fields</Subheading></TextContainer>
                      <FormLayout>
                        <Checkbox label="SKU" checked={editingTemplate.inputFields?.includes('sku')} onChange={(checked) => handleFieldChange('sku', checked, 'input')} />
                        <Checkbox label="Price" checked={editingTemplate.inputFields?.includes('price')} onChange={(checked) => handleFieldChange('price', checked, 'input')} />
                        <Checkbox label="Compare at Price" checked={editingTemplate.inputFields?.includes('compare_at_price')} onChange={(checked) => handleFieldChange('compare_at_price', checked, 'input')} />
                        <Checkbox label="Inventory" checked={editingTemplate.inputFields?.includes('inventory')} onChange={(checked) => handleFieldChange('inventory', checked, 'input')} />
                      </FormLayout>
                    </Card>
                  )}

                  {/* Input Metafields */}
                  <Card sectioned>
                    <TextContainer>
                      <Subheading>Input Metafields</Subheading>
                      <p>Select metafields to use as input for the template</p>
                    </TextContainer>
                    <FormLayout>
                      {(editingTemplate.inputMetafields || []).map((metafield, index) => (
                        <FormLayout.Group key={index} condensed>
                          <TextField label="Namespace" value={metafield.namespace} onChange={(value) => handleMetafieldChange(index, 'namespace', value, 'input')} autoComplete="off" />
                          <TextField label="Key" value={metafield.key} onChange={(value) => handleMetafieldChange(index, 'key', value, 'input')} autoComplete="off" />
                          <Button plain destructive onClick={() => handleRemoveMetafield(index, 'input')}>Remove</Button>
                        </FormLayout.Group>
                      ))}
                      <Button onClick={() => handleAddMetafield('input')}>Add Input Metafield</Button>
                    </FormLayout>
                  </Card>
                </FormLayout>
              </Card>
            )}

            {/* ③ OUTPUT */}
            <Card sectioned>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>{editingTemplate.type === 'tool' ? '2' : '3'}</div>
                <div><Heading>{editingTemplate.type === 'tool' ? 'Tool Execution' : 'Output'}</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>{editingTemplate.type === 'tool' ? 'Select a tool to automate product updates' : editingTemplate.type === 'image' ? 'Where should the AI output appear?' : 'What should the AI generate?'}</p></div>
              </div>

              <FormLayout>
                {/* Product template output fields */}
                {editingTemplate.type === 'product' ? (
                  <Card sectioned>
                    <TextContainer><Subheading>Product Fields</Subheading></TextContainer>
                    <FormLayout>
                      <Checkbox label="Title" checked={editingTemplate.outputFields?.includes('title')} onChange={(checked) => handleFieldChange('title', checked, 'output')} />

                      {editingTemplate.outputFields?.includes('title') && editingTemplate.type !== 'image' && (
                        <div style={{ marginLeft: '28px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e1e3e5', marginTop: '4px', marginBottom: '8px' }}>
                          <Select
                            label="🔮 Optional: Connect Title generation to a Name Pool"
                            helpText="Choose a Name Pool to generate absolutely unique, fictional product names."
                            options={[
                              { label: 'No Name Pool (Default)', value: '' },
                              ...availableNamePools.map(pool => ({ label: pool.name, value: pool.id }))
                            ]}
                            value={editingTemplate.namePoolId || ''}
                            onChange={(value) => setEditingTemplate(prev => ({ ...prev, namePoolId: value }))}
                          />
                        </div>
                      )}
                      <Checkbox label="Description" checked={editingTemplate.outputFields?.includes('body_html')} onChange={(checked) => handleFieldChange('body_html', checked, 'output')} />

                      {/* Image Placeholder Settings */}
                      {editingTemplate.outputFields?.includes('body_html') && editingTemplate.type !== 'image' && (
                        <div style={{ marginLeft: '28px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e1e3e5', marginTop: '4px', marginBottom: '8px' }}>
                          <Stack vertical spacing="tight">
                            <Checkbox
                              label="🖼️ Enable Image Placeholders in Description"
                              checked={editingTemplate.enableImagePlaceholders === true}
                              onChange={(checked) => {
                                console.log('Setting enableImagePlaceholders to:', checked);
                                setEditingTemplate(prev => ({ ...prev, enableImagePlaceholders: checked }));
                              }}
                              helpText="Allows the AI to embed product images directly into the description."
                            />
                            {editingTemplate.enableImagePlaceholders && (
                              <p style={{ fontSize: '12px', color: '#637381', margin: '4px 0 0 0' }}>
                                💎 Combine with an "Analyze All Images" template in a workflow for intelligent image placement.
                              </p>
                            )}
                          </Stack>
                        </div>
                      )}
                      <Checkbox label="Product Type" checked={editingTemplate.outputFields?.includes('product_type')} onChange={(checked) => handleFieldChange('product_type', checked, 'output')} />
                      <Checkbox label="Tags" checked={editingTemplate.outputFields?.includes('tags')} onChange={(checked) => handleFieldChange('tags', checked, 'output')} />
                      <Checkbox label="Handle" checked={editingTemplate.outputFields?.includes('handle')} onChange={(checked) => handleFieldChange('handle', checked, 'output')} />
                    </FormLayout>
                  </Card>
                ) : editingTemplate.type === 'image' ? (
                  /* Image template output */
                  <Card sectioned>
                    <TextContainer>
                      <Subheading>Image Template Output Options</Subheading>
                      <Banner status="warning">
                        <p><strong>Image Template Limitation:</strong> Image templates can only output to the Description field or display results in the sidebar.</p>
                      </Banner>
                    </TextContainer>
                    <FormLayout>
                      <Stack vertical spacing="loose">
                        <RadioButton
                          label="Add to Product Description"
                          helpText="AI-generated content will be added to the end of your product description."
                          checked={!editingTemplate.sidebarOnly && editingTemplate.outputFields?.includes('body_html')}
                          id="image-output-description"
                          name="image-output-mode"
                          onChange={() => {
                            console.log('Selecting Description output mode');
                            setEditingTemplate(prev => ({
                              ...prev,
                              sidebarOnly: false,
                              outputFields: ['body_html'],
                              outputModes: { ...(prev.outputModes || {}), 'body_html': 'add' }
                            }));
                          }}
                        />
                        <RadioButton
                          label="Sidebar Only (No Changes to Product)"
                          helpText="AI-generated content will only be displayed in the sidebar and won't modify your product."
                          id="image-output-sidebar"
                          name="image-output-mode"
                          checked={editingTemplate.sidebarOnly === true}
                          onChange={() => {
                            console.log('Selecting Sidebar-only mode');
                            setEditingTemplate(prev => ({
                              ...prev,
                              sidebarOnly: true,
                              outputFields: []
                            }));
                          }}
                        />
                      </Stack>
                    </FormLayout>
                    <div style={{ marginTop: '16px' }}>
                      <Banner status="info">
                        <p>When using Image Analyzing templates, all other output fields are automatically disabled.</p>
                      </Banner>
                    </div>
                  </Card>
                ) : (
                  /* Tool template */
                  <Card sectioned>
                    <TextContainer>
                      <Subheading>Tool Configuration</Subheading>
                      <Banner status="warning">
                        <p><strong>Tool Execution Template:</strong> Tool templates automate product tools without using AI credits.</p>
                      </Banner>
                    </TextContainer>

                    <FormLayout>
                      <Select
                        label="Select Tool"
                        options={[
                          { label: 'Select a tool...', value: '' },
                          { label: '--- Product Tools ---', value: 'header_product', disabled: true },
                          { label: 'Generate Image Alt Text', value: 'image_alt_text' },
                          { label: 'Set Vendor Name', value: 'vendor_name' },
                          { label: 'Generate SKUs', value: 'generate_skus' },
                          { label: '--- Product Handle ---', value: 'header_handle', disabled: true },
                          { label: 'Generate Handle', value: 'generate_handles' },
                          { label: 'Add Prefix/Suffix to Handle', value: 'handle_prefix_suffix' },
                          { label: 'Empty Handle', value: 'empty_handle' },
                          { label: '--- Pricing Tools ---', value: 'header_pricing', disabled: true },
                          { label: 'Update Prices', value: 'update_prices' },
                          { label: 'Set Sales Price Factor', value: 'sales_price_factor' },
                          { label: 'Round Prices', value: 'round_prices' },
                          { label: 'Set Compare-at Price Discount', value: 'compare_price_discount' },
                          { label: 'Clear Compare Prices', value: 'clear_compare_prices' },
                          { label: '--- Inventory Tools ---', value: 'header_inventory', disabled: true },
                          { label: 'Set Inventory Quantity', value: 'set_inventory' }
                        ]}
                        value={editingTemplate.prompt?.startsWith('tool:') ? editingTemplate.prompt.split(':')[1].split('|')[0] : ''}
                        onChange={(value) => {
                          let toolParams = '';
                          switch (value) {
                            case 'image_alt_text':
                              toolParams = ' - product image';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['image_alt_text'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'vendor_name':
                              toolParams = 'Your Brand Name';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['vendor'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'generate_skus':
                              toolParams = 'prefix=|suffix=';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.sku'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'generate_handles':
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['handle'], prompt: `tool:${value}|` }));
                              break;
                            case 'handle_prefix_suffix':
                              toolParams = 'prefix=|suffix=';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['handle'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'empty_handle':
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['handle'], prompt: `tool:${value}|` }));
                              break;
                            case 'update_prices':
                              toolParams = 'amount=10|type=percentage|field=price';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.price'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'sales_price_factor':
                              toolParams = '1.1';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.price'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'round_prices':
                              toolParams = '0.99';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.price'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'compare_price_discount':
                              toolParams = '20';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.compare_at_price'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            case 'clear_compare_prices':
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.compare_at_price'], prompt: `tool:${value}|` }));
                              break;
                            case 'set_inventory':
                              toolParams = 'quantity=10|ignoreZero=yes|useRange=no|quantityMin=0|quantityMax=0|trackQuantity=unchanged|continueSelling=unchanged|chargeTaxes=unchanged';
                              setEditingTemplate(prev => ({ ...prev, outputFields: ['variants.inventory_quantity'], prompt: `tool:${value}|${toolParams}` }));
                              break;
                            default:
                              setEditingTemplate(prev => ({ ...prev, outputFields: [], prompt: '' }));
                          }
                        }}
                        helpText="Select which tool you want to execute with this template"
                      />

                      {editingTemplate.prompt?.startsWith('tool:') && (
                        <>
                          {editingTemplate.prompt.startsWith('tool:image_alt_text') && (
                            <>
                              <Banner status="info">
                                <p><strong>Generate Image Alt Text</strong> - Creates SEO-friendly alt text for product images by combining the product title with a suffix.</p>
                              </Banner>
                              <TextField label="Image Alt Text Suffix" value={editingTemplate.prompt.split('|')[1] || ' - product image'} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: `tool:image_alt_text|${value}` })); }} helpText="This text will be added to the product title to create the alt text." placeholder="e.g., - product image" autoComplete="off" />
                            </>
                          )}
                          {editingTemplate.prompt.startsWith('tool:vendor_name') && (
                            <TextField label="Vendor Name" value={editingTemplate.prompt.split('|')[1] || ''} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: `tool:vendor_name|${value}` })); }} helpText="This vendor name will be set for all selected products." placeholder="e.g., Your Brand Name" autoComplete="off" />
                          )}
                          {editingTemplate.prompt.startsWith('tool:generate_skus') && (
                            <>
                              <Banner status="info"><p>Generate SKUs: <code>[prefix]-[slugified title]-[variant options]-[suffix]</code></p></Banner>
                              <FormLayout.Group>
                                <TextField label="Prefix" value={skuPrefix} onChange={(value) => { setSkuPrefix(value); setEditingTemplate(prev => ({ ...prev, prompt: `tool:generate_skus|prefix=${value}|suffix=${skuSuffix}` })); }} placeholder="e.g., WF" helpText="Added at the beginning of the SKU" autoComplete="off" />
                                <TextField label="Suffix" value={skuSuffix} onChange={(value) => { setTimeout(() => { setSkuSuffix(value); setEditingTemplate(prev => ({ ...prev, prompt: `tool:generate_skus|prefix=${skuPrefix}|suffix=${value}` })); }, 0); }} placeholder="e.g., 2025" helpText="Added at the end of the SKU" autoComplete="off" />
                              </FormLayout.Group>
                            </>
                          )}
                          {editingTemplate.prompt.startsWith('tool:handle_prefix_suffix') && (
                            <FormLayout.Group>
                              <TextField label="Prefix" value={handlePrefix} onChange={(value) => { setTimeout(() => { setHandlePrefix(value); setEditingTemplate(prev => ({ ...prev, prompt: `tool:handle_prefix_suffix|prefix=${value}|suffix=${handleSuffix}` })); }, 0); }} placeholder="e.g., new-" helpText="Added at the beginning of the handle" autoComplete="off" />
                              <TextField label="Suffix" value={handleSuffix} onChange={(value) => { setTimeout(() => { setHandleSuffix(value); setEditingTemplate(prev => ({ ...prev, prompt: `tool:handle_prefix_suffix|prefix=${handlePrefix}|suffix=${value}` })); }, 0); }} placeholder="e.g., -2023" helpText="Added at the end of the handle" autoComplete="off" />
                            </FormLayout.Group>
                          )}
                          {editingTemplate.prompt.startsWith('tool:update_prices') && (
                            <FormLayout.Group>
                              <TextField label="Amount" type="number" value={(editingTemplate.prompt.match(/amount=([^|]*)/)?.[1] || '10')} onChange={(value) => { const type = editingTemplate.prompt.match(/type=([^|]*)/)?.[1] || 'percentage'; const field = editingTemplate.prompt.match(/field=([^|]*)/)?.[1] || 'price'; setEditingTemplate(prev => ({ ...prev, prompt: `tool:update_prices|amount=${value}|type=${type}|field=${field}` })); }} helpText="Amount to adjust price by" autoComplete="off" />
                              <Select label="Type" options={[{ label: 'Increase by %', value: 'increase' }, { label: 'Decrease by %', value: 'decrease' }, { label: 'Increase by amount', value: 'increase_amount' }, { label: 'Decrease by amount', value: 'decrease_amount' }, { label: 'Set to specific amount', value: 'set' }, { label: 'Percentage (Legacy)', value: 'percentage' }, { label: 'Fixed Amount (Legacy)', value: 'fixed' }]} value={(editingTemplate.prompt.match(/type=([^|]*)/)?.[1] || 'increase')} onChange={(value) => { const amount = editingTemplate.prompt.match(/amount=([^|]*)/)?.[1] || '10'; const field = editingTemplate.prompt.match(/field=([^|]*)/)?.[1] || 'price'; setEditingTemplate(prev => ({ ...prev, prompt: `tool:update_prices|amount=${amount}|type=${value}|field=${field}` })); }} helpText="Choose how to modify prices" />
                              <Select label="Field" options={[{ label: 'Price', value: 'price' }, { label: 'Compare at Price', value: 'compare_at_price' }]} value={(editingTemplate.prompt.match(/field=([^|]*)/)?.[1] || 'price')} onChange={(value) => { const amount = editingTemplate.prompt.match(/amount=([^|]*)/)?.[1] || '10'; const type = editingTemplate.prompt.match(/type=([^|]*)/)?.[1] || 'percentage'; setEditingTemplate(prev => ({ ...prev, prompt: `tool:update_prices|amount=${amount}|type=${type}|field=${value}` })); }} helpText="Which price field to update" />
                            </FormLayout.Group>
                          )}
                          {editingTemplate.prompt.startsWith('tool:sales_price_factor') && (
                            <TextField label="Price Factor" type="number" step="0.01" value={editingTemplate.prompt.split('|')[1] || '1.1'} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: `tool:sales_price_factor|${value}` })); }} helpText="Multiply prices by this factor (e.g., 1.1 for 10% increase)" autoComplete="off" />
                          )}
                          {editingTemplate.prompt.startsWith('tool:round_prices') && (
                            <TextField label="Round to Cents Value" type="number" step="0.01" value={editingTemplate.prompt.split('|')[1] || '0.99'} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: `tool:round_prices|${value}` })); }} helpText="Round down to nearest dollar and add cents (e.g., 0.99 for $XX.99 pricing)" autoComplete="off" />
                          )}
                          {editingTemplate.prompt.startsWith('tool:compare_price_discount') && (
                            <TextField label="Discount Percentage" type="number" min="0" max="100" value={editingTemplate.prompt.split('|')[1] || '20'} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: `tool:compare_price_discount|${value}` })); }} helpText="Set the discount percentage (0-100%)" suffix="%" autoComplete="off" />
                          )}
                          {editingTemplate.prompt.startsWith('tool:set_inventory') && (() => {
                            // Helper to parse current params from prompt
                            const promptStr = editingTemplate.prompt || '';
                            const getParam = (name, fallback) => {
                              const match = promptStr.match(new RegExp(name + '=([^|]*)'));
                              return match ? match[1] : fallback;
                            };
                            const curQuantity = getParam('quantity', '10');
                            const curIgnoreZero = getParam('ignoreZero', 'yes');
                            const curUseRange = getParam('useRange', 'no');
                            const curMin = getParam('quantityMin', '0');
                            const curMax = getParam('quantityMax', '0');
                            const curTrack = getParam('trackQuantity', 'unchanged');
                            const curContinue = getParam('continueSelling', 'unchanged');
                            const curChargeTaxes = getParam('chargeTaxes', 'unchanged');
                            const buildPrompt = (overrides = {}) => {
                              const p = { quantity: curQuantity, ignoreZero: curIgnoreZero, useRange: curUseRange, quantityMin: curMin, quantityMax: curMax, trackQuantity: curTrack, continueSelling: curContinue, chargeTaxes: curChargeTaxes, ...overrides };
                              return `tool:set_inventory|quantity=${p.quantity}|ignoreZero=${p.ignoreZero}|useRange=${p.useRange}|quantityMin=${p.quantityMin}|quantityMax=${p.quantityMax}|trackQuantity=${p.trackQuantity}|continueSelling=${p.continueSelling}|chargeTaxes=${p.chargeTaxes}`;
                            };
                            return (
                              <>
                                <FormLayout.Group>
                                  <Stack vertical>
                                    {/* Range toggle */}
                                    <Checkbox
                                      label="Use random range (min – max)"
                                      checked={curUseRange === 'yes'}
                                      onChange={(checked) => {
                                        setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ useRange: checked ? 'yes' : 'no' }) }));
                                      }}
                                      helpText="When enabled, a random quantity between min and max will be chosen for each variant"
                                    />

                                    {curUseRange === 'yes' ? (
                                      <FormLayout.Group condensed>
                                        <TextField label="Min Quantity" type="number" value={curMin} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ quantityMin: value }) })); }} helpText="Minimum" autoComplete="off" />
                                        <TextField label="Max Quantity" type="number" value={curMax} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ quantityMax: value }) })); }} helpText="Maximum" autoComplete="off" />
                                      </FormLayout.Group>
                                    ) : (
                                      <TextField label="Quantity" type="number" value={curQuantity} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ quantity: value }) })); }} helpText="Set a specific inventory quantity" autoComplete="off" />
                                    )}

                                    {curUseRange === 'yes' && curMin !== '' && curMax !== '' && (
                                      <Banner status="info"><p>Each variant will get a random quantity between <strong>{curMin}</strong> and <strong>{curMax}</strong>.</p></Banner>
                                    )}

                                    <Text variant="bodyMd" as="p" fontWeight="medium">Ignore products with 0 inventory</Text>
                                    <Stack>
                                      <RadioButton label="Yes - Skip products with 0 inventory" id="ignore-zero-yes" name="ignoreZero" checked={curIgnoreZero !== 'no'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ ignoreZero: 'yes' }) })); }} />
                                    </Stack>
                                    <Stack>
                                      <RadioButton label="No - Update all products" id="ignore-zero-no" name="ignoreZero" checked={curIgnoreZero === 'no'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ ignoreZero: 'no' }) })); }} />
                                    </Stack>

                                    {/* Track Quantity */}
                                    <div style={{ marginTop: '12px', borderTop: '1px solid #e1e3e5', paddingTop: '12px' }}>
                                      <Text variant="bodyMd" as="p" fontWeight="medium">Track Quantity</Text>
                                      <Stack vertical spacing="tight">
                                        <RadioButton label="Don't change tracking" id="track-unchanged" name="trackQuantityTpl" checked={curTrack === 'unchanged'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ trackQuantity: 'unchanged' }) })); }} />
                                        <RadioButton label="Enable tracking" id="track-yes" name="trackQuantityTpl" checked={curTrack === 'yes'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ trackQuantity: 'yes' }) })); }} />
                                        <RadioButton label="Disable tracking" id="track-no" name="trackQuantityTpl" checked={curTrack === 'no'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ trackQuantity: 'no' }) })); }} />
                                      </Stack>
                                    </div>

                                    {/* Continue Selling */}
                                    <div style={{ marginTop: '12px', borderTop: '1px solid #e1e3e5', paddingTop: '12px' }}>
                                      <Text variant="bodyMd" as="p" fontWeight="medium">Continue Selling When Out of Stock</Text>
                                      <Stack vertical spacing="tight">
                                        <RadioButton label="Don't change selling policy" id="continue-unchanged" name="continueSellingTpl" checked={curContinue === 'unchanged'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ continueSelling: 'unchanged' }) })); }} />
                                        <RadioButton label="Yes - Continue selling" id="continue-yes" name="continueSellingTpl" checked={curContinue === 'yes'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ continueSelling: 'yes' }) })); }} />
                                        <RadioButton label="No - Stop selling when out of stock" id="continue-no" name="continueSellingTpl" checked={curContinue === 'no'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ continueSelling: 'no' }) })); }} />
                                      </Stack>
                                    </div>

                                    {/* Charge Taxes */}
                                    <div style={{ marginTop: '12px', borderTop: '1px solid #e1e3e5', paddingTop: '12px' }}>
                                      <Text variant="bodyMd" as="p" fontWeight="medium">Charge Taxes</Text>
                                      <Stack vertical spacing="tight">
                                        <RadioButton label="Don't change tax setting" id="tax-unchanged" name="chargeTaxesTpl" checked={curChargeTaxes === 'unchanged'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ chargeTaxes: 'unchanged' }) })); }} />
                                        <RadioButton label="Yes - Charge taxes" id="tax-yes" name="chargeTaxesTpl" checked={curChargeTaxes === 'yes'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ chargeTaxes: 'yes' }) })); }} />
                                        <RadioButton label="No - Don't charge taxes" id="tax-no" name="chargeTaxesTpl" checked={curChargeTaxes === 'no'} onChange={() => { setEditingTemplate(prev => ({ ...prev, prompt: buildPrompt({ chargeTaxes: 'no' }) })); }} />
                                      </Stack>
                                    </div>
                                  </Stack>
                                </FormLayout.Group>
                              </>
                            );
                          })()}
                          {(editingTemplate.prompt.startsWith('tool:generate_handles') || editingTemplate.prompt.startsWith('tool:empty_handle') || editingTemplate.prompt.startsWith('tool:clear_compare_prices')) && (
                            <Banner status="info"><p>{editingTemplate.prompt.startsWith('tool:empty_handle') ? 'This tool clears the product handle. When pushed to Shopify, an empty handle causes Shopify to auto-generate a new handle from the product title.' : 'This tool doesn\'t require any additional parameters.'}</p></Banner>
                          )}
                        </>
                      )}
                    </FormLayout>
                  </Card>
                )}

                {/* Output SEO Fields - product only */}
                {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                  <Card sectioned>
                    <TextContainer><Subheading>SEO Fields</Subheading></TextContainer>
                    <FormLayout>
                      <Checkbox label="Product Image Alt Text" checked={editingTemplate.outputFields?.includes('alt_text')} onChange={(checked) => handleFieldChange('alt_text', checked, 'output')} />
                    </FormLayout>
                  </Card>
                )}

                {/* Output Options Fields - product only */}
                {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                  <Card sectioned>
                    <TextContainer><Subheading>Options</Subheading></TextContainer>
                    <FormLayout>
                      <Checkbox label="Option1 Name" checked={editingTemplate.outputFields?.includes('option1_name')} onChange={(checked) => handleFieldChange('option1_name', checked, 'output')} />
                      <Checkbox label="Option1 Values" checked={editingTemplate.outputFields?.includes('option1_values')} onChange={(checked) => handleFieldChange('option1_values', checked, 'output')} />
                      <Checkbox label="Option2 Name" checked={editingTemplate.outputFields?.includes('option2_name')} onChange={(checked) => handleFieldChange('option2_name', checked, 'output')} />
                      <Checkbox label="Option2 Values" checked={editingTemplate.outputFields?.includes('option2_values')} onChange={(checked) => handleFieldChange('option2_values', checked, 'output')} />
                      <Checkbox label="Option3 Name" checked={editingTemplate.outputFields?.includes('option3_name')} onChange={(checked) => handleFieldChange('option3_name', checked, 'output')} />
                      <Checkbox label="Option3 Values" checked={editingTemplate.outputFields?.includes('option3_values')} onChange={(checked) => handleFieldChange('option3_values', checked, 'output')} />
                    </FormLayout>
                  </Card>
                )}

                {/* Output Variant Fields */}
                {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
                  <Card sectioned>
                    <TextContainer><Subheading>Variant Fields</Subheading></TextContainer>
                    <FormLayout>
                      <Checkbox label="SKU" checked={editingTemplate.outputFields?.includes('variants.sku')} onChange={(checked) => handleFieldChange('variants.sku', checked, 'output')} />
                      <Checkbox label="Price" checked={editingTemplate.outputFields?.includes('variants.price')} onChange={(checked) => handleFieldChange('variants.price', checked, 'output')} />
                      <Checkbox label="Compare at Price" checked={editingTemplate.outputFields?.includes('variants.compare_at_price')} onChange={(checked) => handleFieldChange('variants.compare_at_price', checked, 'output')} />
                      <Checkbox label="Inventory Quantity" checked={editingTemplate.outputFields?.includes('variants.inventory_quantity')} onChange={(checked) => handleFieldChange('variants.inventory_quantity', checked, 'output')} />
                    </FormLayout>
                  </Card>
                )}
              </FormLayout>
            </Card>

            {/* ④ METAFIELDS - product only */}
            {editingTemplate.type !== 'image' && editingTemplate.type !== 'tool' && (
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>4</div>
                  <div><Heading>Output Metafields</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>Custom metafields that will be updated by the template</p></div>
                </div>
                <FormLayout>
                  {(editingTemplate.outputMetafields || []).map((metafield, index) => (
                    <FormLayout.Group key={index} condensed>
                      <TextField label="Namespace" value={metafield.namespace} onChange={(value) => handleMetafieldChange(index, 'namespace', value, 'output')} autoComplete="off" helpText={index === 0 ? "Example: custom" : undefined} />
                      <TextField label="Key" value={metafield.key} onChange={(value) => handleMetafieldChange(index, 'key', value, 'output')} autoComplete="off" helpText={index === 0 ? "Example: description_extended" : undefined} />
                      {editingTemplate.type === 'image' && !editingTemplate.sidebarOnly && (
                        <Select label="Insert Mode" options={[{ label: 'Exchange', value: 'exchange' }, { label: 'Add', value: 'add' }]} value={editingTemplate.outputMetafieldModes?.[`${metafield.namespace}.${metafield.key}`] === 'add' ? 'add' : 'exchange'} onChange={(value) => { setEditingTemplate(prev => ({ ...prev, outputMetafieldModes: { ...(prev.outputMetafieldModes || {}), [`${metafield.namespace}.${metafield.key}`]: value } })); }} />
                      )}
                      <Button plain destructive onClick={() => handleRemoveMetafield(index, 'output')}>Remove</Button>
                    </FormLayout.Group>
                  ))}
                  <Button onClick={() => handleAddMetafield('output')}>Add Output Metafield</Button>
                </FormLayout>
              </Card>
            )}

            {/* ⑤ PROMPT - not for tool templates */}
            {editingTemplate.type !== 'tool' && (
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>{editingTemplate.type === 'image' ? '4' : '5'}</div>
                  <div><Heading>Prompt</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>Tell the AI how to generate the content</p></div>
                </div>
                <Stack vertical spacing="tight">
                  {editingTemplate.type !== 'image' && (
                    <Select
                      label="Quick Start — Prompt Template"
                      options={examplePrompts}
                      onChange={(value) => {
                        if (value) {
                          const isSKUTemplate = value.toLowerCase().includes('sku');
                          setEditingTemplate(prev => ({
                            ...prev,
                            prompt: value,
                            outputFields: isSKUTemplate
                              ? [...new Set([
                                ...(prev.outputFields || []),
                                'variants', 'variants.sku', 'variants.price',
                                'variants.compare_at_price', 'variants.inventory_quantity'
                              ])]
                              : prev.outputFields
                          }));
                        }
                      }}
                      value={examplePrompts.find(p => p.value === editingTemplate.prompt)?.value || ''}
                      helpText="Select a pre-defined prompt or write your own below"
                    />
                  )}

                  {editingTemplate.type === 'image' && (
                    <div style={{ padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e1e3e5', marginBottom: '8px' }}>
                      <TextContainer>
                        <Subheading>💡 Example Prompts for Image Analysis</Subheading>
                        <ul style={{ fontSize: '13px', color: '#637381', margin: '8px 0 0 16px' }}>
                          <li>Describe this product in detail and suggest a compelling title.</li>
                          <li>What are the key features visible in this product image?</li>
                          <li>Create SEO-optimized alt text for this product image.</li>
                        </ul>
                      </TextContainer>
                    </div>
                  )}

                  <TextField
                    label="Your Prompt"
                    value={editingTemplate.prompt}
                    onChange={(value) => setEditingTemplate(prev => ({ ...prev, prompt: value }))}
                    multiline={4}
                    autoComplete="off"
                    error={error && !editingTemplate.prompt ? 'Prompt is required' : ''}
                    helpText={
                      <span>
                        Use placeholders like {'{'}field_name{'}'} or {'{'}metafield.namespace.key{'}'} to reference input fields.
                        Response will be formatted as JSON with your output fields.
                        {editingTemplate.enableImagePlaceholders && (
                          <span style={{ display: 'block', marginTop: '8px', padding: '8px', backgroundColor: '#e3f1df', borderRadius: '4px', border: '1px solid #c3e6b5' }}>
                            <strong>🖼️ Image Placeholders active:</strong><br />
                            <code>{'{{IMAGE_NEXT}}'}</code> — inserts the next available image.<br />
                            <code>{'{{IMAGE_1}}'}</code>, <code>{'{{IMAGE_2}}'}</code>, ... — insert a specific image by number.
                          </span>
                        )}
                      </span>
                    }
                  />

                  {/* Dynamic placeholder hints */}
                  {editingTemplate.inputFields?.length > 0 && (
                    <div style={{ padding: '8px 12px', backgroundColor: '#EBF5FA', borderRadius: '6px', fontSize: '13px' }}>
                      <strong>📋 Available placeholders:</strong>{' '}
                      {editingTemplate.inputFields.map((field, i) => (
                        <span key={i}>
                          <code style={{ backgroundColor: '#DFE3E8', padding: '2px 6px', borderRadius: '3px', fontSize: '12px' }}>{`{${field}}`}</code>
                          {i < editingTemplate.inputFields.length - 1 ? ' ' : ''}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Name Pool Hint */}
                  {editingTemplate.outputFields?.includes('title') && editingTemplate.namePoolId && (
                    <div style={{ padding: '8px 12px', backgroundColor: '#f4f3ff', border: '1px solid #e0d7ff', borderRadius: '6px', fontSize: '13px', marginTop: '4px' }}>
                      <strong>🔮 Name Pool Active:</strong> The AI will invent a unique name.<br />
                      Use the instruction <code>{`{generated_name}`}</code> in your prompt to show the AI where exactly to place the name (e.g. <i>"Format the title as: {`{generated_name}`} | or - My Brand"</i>).
                    </div>
                  )}
                </Stack>
              </Card>
            )}

            {/* ⑥ AI MODEL - not for tool templates */}
            {editingTemplate.type !== 'tool' && (
              <Card sectioned>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>{editingTemplate.type === 'image' ? '5' : '6'}</div>
                  <div><Heading>AI Model</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>Which model should generate the content?</p></div>
                </div>

                <Stack vertical spacing="tight">
                  {/* Smart Model Suggestion */}
                  {(() => {
                    const currentModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                    let suggestion = null;
                    const promptLength = (editingTemplate.prompt || '').length;
                    const outputCount = (editingTemplate.outputFields?.length || 0);

                    if (editingTemplate.type === 'image') {
                      const visionModels = availableAIModels.filter(m => m.type === 'vision');
                      const sortedVision = [...visionModels].sort((a, b) => (a.creditCostPerOutput || 1) - (b.creditCostPerOutput || 1));
                      const cheapVision = sortedVision[0];
                      const premiumVision = sortedVision[sortedVision.length - 1];

                      // Complex prompt (>300 chars) or multiple outputs → suggest premium vision model
                      if (promptLength > 300 || outputCount > 2) {
                        if (premiumVision && premiumVision.id !== editingTemplate.modelId) {
                          suggestion = { id: premiumVision.id, name: premiumVision.displayName, reason: '👁️ Complex analysis — premium vision model recommended', credits: premiumVision.creditCostPerOutput || 1 };
                        }
                      } else {
                        // Simple/short prompt → suggest cheapest vision model
                        if (cheapVision && cheapVision.id !== editingTemplate.modelId) {
                          suggestion = { id: cheapVision.id, name: cheapVision.displayName, reason: '💰 Simple analysis — save credits with a lighter model', credits: cheapVision.creditCostPerOutput || 1 };
                        }
                      }
                    } else if (editingTemplate.outputFields?.includes('body_html')) {
                      // Description output → check prompt length for quality needs
                      if (promptLength > 400 || outputCount <= 2) {
                        const claude = availableAIModels.find(m => m.displayName?.toLowerCase().includes('sonnet'));
                        if (claude && claude.id !== editingTemplate.modelId) {
                          suggestion = { id: claude.id, name: claude.displayName, reason: '✍️ Best writing quality for descriptions', credits: claude.creditCostPerOutput || 1 };
                        }
                      }
                    } else {
                      const simpleFields = ['tags', 'handle', 'product_type', 'variants.sku', 'image_alt_text'];
                      if (outputCount > 0 && editingTemplate.outputFields?.every(f => simpleFields.includes(f))) {
                        const cheapest = availableAIModels.filter(m => m.type !== 'vision').sort((a, b) => (a.creditCostPerOutput || 1) - (b.creditCostPerOutput || 1))[0];
                        if (cheapest && cheapest.id !== editingTemplate.modelId) {
                          suggestion = { id: cheapest.id, name: cheapest.displayName, reason: '💰 Simple fields — cheapest model saves credits', credits: cheapest.creditCostPerOutput || 1 };
                        }
                      } else if (promptLength > 500 && outputCount > 3) {
                        // Very complex prompt with many outputs → suggest a premium text model
                        const premium = availableAIModels.filter(m => m.type !== 'vision').sort((a, b) => (b.creditCostPerOutput || 1) - (a.creditCostPerOutput || 1))[0];
                        if (premium && premium.id !== editingTemplate.modelId && (premium.creditCostPerOutput || 1) > (currentModel?.creditCostPerOutput || 1)) {
                          suggestion = { id: premium.id, name: premium.displayName, reason: '🧠 Complex prompt — premium model for best results', credits: premium.creditCostPerOutput || 1 };
                        }
                      }
                    }

                    return suggestion ? (
                      <div style={{ padding: '12px', backgroundColor: '#FFF3CD', borderRadius: '8px', border: '1px solid #FFE69C', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                        <div>
                          <Text variant="bodyMd"><strong>💡 Suggestion:</strong> {suggestion.reason}</Text>
                          <Text variant="bodySm" color="subdued">→ {suggestion.name} ({suggestion.credits} credits)</Text>
                        </div>
                        <Button size="slim" onClick={() => { setEditingTemplate(prev => ({ ...prev, modelId: suggestion.id })); setCreditCost(suggestion.credits); }}>
                          Use this model
                        </Button>
                      </div>
                    ) : null;
                  })()}

                  {/* Grouped Model Select */}
                  <Select
                    label="AI Model"
                    options={loadingAIModels
                      ? [{ label: 'Loading models...', value: '' }]
                      : (() => {
                        const providerOrder = [
                          { key: 'openai', label: '🔵 OpenAI' },
                          { key: 'claude', label: '🟣 Claude' },
                          { key: 'gemini', label: '🟢 Gemini' },
                          { key: 'deepseek', label: '🧠 DeepSeek' }
                        ];
                        const result = [];
                        providerOrder.forEach(({ key, label }) => {
                          const models = availableAIModels
                            .filter(m => m.provider === key)
                            .filter(m => {
                              if (editingTemplate.type === 'image') return m.type === 'vision';
                              return m.type === 'text' || !m.type;
                            })
                            .sort((a, b) => (a.creditCostPerOutput || 1) - (b.creditCostPerOutput || 1));
                          if (models.length > 0) {
                            result.push({ label: `── ${label} ──`, value: `header_${key}`, disabled: true });
                            models.forEach(m => {
                              const typeIcon = m.type === 'vision' ? '👁️' : '📝';
                              const credits = m.creditCostPerOutput || 1;
                              const defaultBadge = m.isDefault ? ' ⭐' : '';
                              result.push({ label: `${typeIcon} ${m.displayName} — ${credits} cr${defaultBadge}`, value: m.id });
                            });
                          }
                        });
                        return result.length > 0 ? result : [{ label: 'No models available', value: '' }];
                      })()
                    }
                    value={editingTemplate.modelId || ''}
                    onChange={(value) => {
                      setEditingTemplate(prev => ({ ...prev, modelId: value }));
                      const selectedModel = availableAIModels.find(m => m.id === value);
                      setCreditCost(selectedModel?.creditCostPerOutput || 1);
                    }}
                    disabled={loadingAIModels || availableAIModels.length === 0}
                    helpText={loadingAIModels ? 'Loading models...' : 'Models grouped by provider. 📝 = Text, 👁️ = Vision'}
                  />

                  {/* Credit info bar */}
                  {availableAIModels.length > 0 && (() => {
                    const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                    const aiModelCost = selectedModel?.creditCostPerOutput || 1;
                    const { calculateTemplateCredits, getDetailedCreditCalculation } = require('../../utils/templateCreditCalculator');
                    const templateData = { inputFields: editingTemplate.inputFields || [], outputFields: editingTemplate.outputFields || [], type: editingTemplate.type };
                    const totalCost = calculateTemplateCredits(templateData, aiModelCost);
                    const details = getDetailedCreditCalculation(templateData, aiModelCost);

                    return (
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', backgroundColor: '#f6f6f7', borderRadius: '6px' }}>
                        <Text variant="bodySm">
                          {selectedModel ? (
                            <>{selectedModel.type === 'vision' ? '👁️ Vision Model' : '📝 Text Model'} · {aiModelCost} credit{aiModelCost !== 1 ? 's' : ''} per field</>
                          ) : 'Select a model'}
                        </Text>
                        <Tooltip content={details.discount > 0 ? `${details.discount}% discount applied` : 'Standard pricing'}>
                          <Badge status={details.discount > 0 ? "success" : "info"}>
                            Total: {totalCost} credits {details.discount > 0 ? `(-${details.discount}%)` : ''}
                          </Badge>
                        </Tooltip>
                      </div>
                    );
                  })()}

                  {/* Vision model capability - only for vision models */}
                  {(() => {
                    const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                    const isVisionModel = selectedModel && (selectedModel.type === 'vision' || selectedModel.modelType === 'vision');
                    return isVisionModel ? (
                      <div style={{ padding: '10px 12px', backgroundColor: '#EBF5FA', borderRadius: '6px' }}>
                        <Stack spacing="tight" alignment="center">
                          <Badge status="info">👁️ Max Images: {selectedModel.maxImages || 1}</Badge>
                          <Text variant="bodySm" color="subdued">Can analyze up to {selectedModel.maxImages || 1} image{(selectedModel.maxImages || 1) > 1 ? 's' : ''} per product</Text>
                        </Stack>
                      </div>
                    ) : null;
                  })()}

                  {/* Warning: image template but non-vision model */}
                  {editingTemplate.type === 'image' && (() => {
                    const selectedModel = availableAIModels.find(m => m.id === editingTemplate.modelId);
                    return selectedModel && selectedModel.type !== 'vision' ? (
                      <Banner status="warning"><p>⚠️ Image templates require a vision model. The selected model is text-only.</p></Banner>
                    ) : null;
                  })()}

                  <Button plain onClick={() => setShowAIModelHelpModal(true)}>ℹ️ Help me choose a model</Button>
                </Stack>
              </Card>
            )}

            {/* ⑦ SUMMARY */}
            <Card sectioned>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: '#5C6AC4', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>{editingTemplate.type === 'tool' ? '3' : editingTemplate.type === 'image' ? '6' : '7'}</div>
                <div><Heading>Summary</Heading><p style={{ color: '#637381', fontSize: '13px', marginTop: '2px' }}>Review your template configuration</p></div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div style={{ padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  <Text variant="bodySm" color="subdued">Model</Text>
                  <Text variant="bodyMd">{editingTemplate.type === 'tool' ? '⚙️ No AI model (tool)' : (availableAIModels.find(m => m.id === editingTemplate.modelId)?.displayName || 'Not selected')}</Text>
                </div>
                <div style={{ padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  <Text variant="bodySm" color="subdued">Template Type</Text>
                  <Text variant="bodyMd">{editingTemplate.type === 'product' ? '📝 Product' : editingTemplate.type === 'image' ? '🖼️ Image' : '⚙️ Tool'}</Text>
                </div>
                <div style={{ padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  <Text variant="bodySm" color="subdued">Credits per Product</Text>
                  <Text variant="bodyMd">{editingTemplate.type === 'tool' ? '0 (free)' : (() => {
                    const sm = availableAIModels.find(m => m.id === editingTemplate.modelId);
                    const cost = sm?.creditCostPerOutput || 1;
                    const { calculateTemplateCredits } = require('../../utils/templateCreditCalculator');
                    return calculateTemplateCredits({ inputFields: editingTemplate.inputFields || [], outputFields: editingTemplate.outputFields || [], type: editingTemplate.type }, cost);
                  })()}</Text>
                </div>
                <div style={{ padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '6px' }}>
                  <Text variant="bodySm" color="subdued">Fields</Text>
                  <Text variant="bodyMd">{(editingTemplate.inputFields?.length || 0)} input → {(editingTemplate.outputFields?.length || 0)} output</Text>
                </div>
              </div>
            </Card>

          </Stack>
        </Modal.Section>
      </Modal>


      {/* AI Model Help Modal */}
      <Modal
        open={showAIModelHelpModal}
        onClose={() => setShowAIModelHelpModal(false)}
        title="💡 Choose the Right AI Model for Your Task"
        primaryAction={{
          content: 'Got it',
          onAction: () => setShowAIModelHelpModal(false)
        }}
      >
        <Modal.Section>
          <TextContainer>
            <Heading>🧠 What's an AI Model?</Heading>
            <p>Different AI models are better at different tasks. Some are fast and inexpensive, others are better for high-quality writing, reasoning, or image analysis.</p>
            <p>Choosing the right model helps you get better results — and use your credits more efficiently.</p>

            <Heading>⚙️ Available Models</Heading>

            {/* OpenAI Section */}
            <Heading>🔵 OpenAI Models</Heading>

            <Stack vertical spacing="loose">
              {/* GPT 5.4-Nano */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>GPT 5.4-Nano</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Simple tasks, handles, short values, bulk updates</TextStyle>
                  <p>Why use it: Fastest and cheapest OpenAI option</p>
                  <TextStyle variation="strong">Credits: 1 per request</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Great for lightweight automation and basic product data</p>
                  </div>
                </Stack>
              </Card>

              {/* GPT 5.4-Mini */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>GPT 5.4-Mini</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Titles, tags, structured text, general product content</TextStyle>
                  <p>Why use it: Best balance of quality, speed, and cost</p>
                  <TextStyle variation="strong">Credits: 2 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Recommended default for most text templates</p>
                  </div>
                </Stack>
              </Card>

              {/* GPT 5.4 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>GPT 5.4</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Premium descriptions, SEO-rich content, advanced structured output</TextStyle>
                  <p>Why use it: Strong reasoning and high-quality text generation</p>
                  <TextStyle variation="strong">Credits: 5 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Use when output quality matters most</p>
                  </div>
                </Stack>
              </Card>

              {/* GPT 5.4-Mini Vision */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>👁️</div>
                    <Heading>GPT 5.4-Mini Vision</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Image-based title generation, ALT text, simple image analysis</TextStyle>
                  <p>Why use it: Balanced vision model for everyday product image tasks</p>
                  <TextStyle variation="strong">Credits: 3 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Great for standard image-based templates</p>
                  </div>
                </Stack>
              </Card>

              {/* GPT 5.4 Vision */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>👁️</div>
                    <Heading>GPT 5.4 Vision</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: High-quality image analysis, feature extraction from product images</TextStyle>
                  <p>Why use it: Stronger visual understanding for premium image workflows</p>
                  <TextStyle variation="strong">Credits: 5 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Best for more advanced image-based product content</p>
                  </div>
                </Stack>
              </Card>

              {/* Claude Models Section */}
              <Heading>🟣 Claude Models</Heading>

              {/* Claude Haiku 4.5 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>Claude Haiku 4.5</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Short texts, translations, option values</TextStyle>
                  <p>Why use it: Fast and cost-efficient</p>
                  <TextStyle variation="strong">Credits: 1 per request</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Good for lightweight text generation</p>
                  </div>
                </Stack>
              </Card>

              {/* Claude Sonnet 4.6 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>Claude Sonnet 4.6</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Product descriptions, benefits, storytelling, persuasive text</TextStyle>
                  <p>Why use it: Excellent writing quality with natural tone</p>
                  <TextStyle variation="strong">Credits: 3 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Best choice for high-converting descriptions</p>
                  </div>
                </Stack>
              </Card>

              {/* Claude Opus 4.6 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#D32F2F', fontSize: '24px', marginRight: '8px' }}>🔴</div>
                    <Heading>Claude Opus 4.6</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Premium copy, brand voice, complex writing tasks</TextStyle>
                  <p>Why use it: Highest-quality Claude model for advanced text generation</p>
                  <TextStyle variation="strong">Credits: 6 per request</TextStyle>
                  <div style={{ color: '#D32F2F' }}>
                    <p>💡 Ideal for top-tier product pages and brand-focused content</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini Models Section */}
              <Heading>🟢 Gemini Models</Heading>

              {/* Gemini 2.5 Flash */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>Gemini 2.5 Flash</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Cheap text generation, simple product data tasks</TextStyle>
                  <p>Why use it: Very cost-efficient Gemini option</p>
                  <TextStyle variation="strong">Credits: 1 per request</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Good for lightweight text tasks at low cost</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 3 Flash */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🟢</div>
                    <Heading>Gemini 3 Flash</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Fast text generation, tags, specs, general product tasks</TextStyle>
                  <p>Why use it: Strong balance between speed and performance</p>
                  <TextStyle variation="strong">Credits: 2 per request</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Great all-round Gemini text model</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 3 Pro */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>🔷</div>
                    <Heading>Gemini 3 Pro</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Higher-quality descriptions, SEO content, more advanced generation</TextStyle>
                  <p>Why use it: Stronger reasoning and better quality than Flash</p>
                  <TextStyle variation="strong">Credits: 5 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Best Gemini text model for premium content</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 3 Flash Vision */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>👁️</div>
                    <Heading>Gemini 3 Flash Vision</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Product image analysis, ALT text, short image-based outputs</TextStyle>
                  <p>Why use it: Fast visual model with strong multimodal performance</p>
                  <TextStyle variation="strong">Credits: 3 per request</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Great for image workflows with multiple product photos</p>
                  </div>
                </Stack>
              </Card>

              {/* Gemini 3 Pro Vision */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#1976D2', fontSize: '24px', marginRight: '8px' }}>👁️</div>
                    <Heading>Gemini 3 Pro Vision</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Advanced product image understanding, premium image-based descriptions</TextStyle>
                  <p>Why use it: Best Gemini option for high-quality visual analysis</p>
                  <TextStyle variation="strong">Credits: 6 per request</TextStyle>
                  <div style={{ color: '#1976D2' }}>
                    <p>💡 Ideal for premium image-to-content generation</p>
                  </div>
                </Stack>
              </Card>

              {/* DeepSeek Models Section */}
              <Heading>🧠 DeepSeek Models</Heading>

              {/* DeepSeek-V3 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#2E8540', fontSize: '24px', marginRight: '8px' }}>🔍</div>
                    <Heading>DeepSeek-V3</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Specs, bullet points, structured information</TextStyle>
                  <p>Why use it: Clean, factual, and very cost-efficient</p>
                  <TextStyle variation="strong">Credits: 1 per request</TextStyle>
                  <div style={{ color: '#2E8540' }}>
                    <p>💡 Perfect for technical product data and budget-friendly templates</p>
                  </div>
                </Stack>
              </Card>

              {/* DeepSeek-R1 */}
              <Card sectioned>
                <Stack vertical spacing="tight">
                  <Stack alignment="center">
                    <div style={{ color: '#9C27B0', fontSize: '24px', marginRight: '8px' }}>🧠</div>
                    <Heading>DeepSeek-R1</Heading>
                  </Stack>
                  <TextStyle variation="strong">Best for: Comparisons, reasoning-heavy outputs, structured analysis</TextStyle>
                  <p>Why use it: Strong logic and advanced reasoning</p>
                  <TextStyle variation="strong">Credits: 3 per request</TextStyle>
                  <div style={{ color: '#9C27B0' }}>
                    <p>💡 Use for more analytical text generation</p>
                  </div>
                </Stack>
              </Card>
            </Stack>

            {/* Image Analysis Section */}
            <Heading>🖼️ Image Analysis</Heading>
            <p>Use vision models when your template works with product images.</p>
            <p>Available vision models in your system:</p>
            <ul>
              <li><strong>GPT 5.4-Mini Vision</strong> — 3 credits</li>
              <li><strong>GPT 5.4 Vision</strong> — 5 credits</li>
              <li><strong>Gemini 3 Flash Vision</strong> — 3 credits</li>
              <li><strong>Gemini 3 Pro Vision</strong> — 6 credits</li>
            </ul>
            <p>💡 Gemini vision models are especially useful when analyzing multiple product images.</p>

            <Heading>📌 How Credits Work</Heading>
            <p>Each template run counts as one request.</p>
            <p>Credits depend on:</p>
            <ul>
              <li>the selected model</li>
              <li>the complexity of the task</li>
              <li>whether the template uses text or images</li>
            </ul>
            <p>On average, generating core product content uses around <strong>8–10 credits per product</strong>.</p>
            <p>Advanced workflows may use more.</p>

            <Heading>📉 Want to Save Credits?</Heading>
            <p>Use lower-cost models like:</p>
            <ul>
              <li><strong>GPT 5.4-Nano</strong></li>
              <li><strong>Claude Haiku 4.5</strong></li>
              <li><strong>DeepSeek-V3</strong></li>
              <li><strong>Gemini 2.5 Flash</strong></li>
            </ul>
            <p>These are ideal for:</p>
            <ul>
              <li>option values</li>
              <li>short translations</li>
              <li>specs</li>
              <li>basic product data</li>
            </ul>

            <Heading>🎯 Not Sure Which Model to Pick?</Heading>
            <p>Start with:</p>
            <ul>
              <li>👉 <strong>GPT 5.4-Mini</strong> → best overall default</li>
              <li>👉 <strong>Claude Sonnet 4.6</strong> → best writing quality</li>
              <li>👉 <strong>Gemini 3 Flash Vision</strong> → best balanced image model</li>
            </ul>
            <p>You can choose a different model for each template anytime.</p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      {/* Image Template Executor Modal - For manual selection and Analyze All mode */}
      {showImageTemplateExecutor && currentImageTemplate && (
        currentImageTemplate.imageAnalysisMode === 'manual' ||
        currentImageTemplate.imageAnalysisMode === 'all' ||
        (!currentImageTemplate.imageAnalysisMode && !Boolean(currentImageTemplate.useFeaturedImage))
      ) && (
          <Modal
            open={showImageTemplateExecutor}
            onClose={() => setShowImageTemplateExecutor(false)}
            title={currentImageTemplate.imageAnalysisMode === 'all'
              ? `Analyze All Images: ${currentImageTemplate.name}`
              : `Select Images for Template: ${currentImageTemplate.name}`}
            large
          >
            <Modal.Section>
              <ImageTemplateExecutor
                template={currentImageTemplate}
                products={selectedProducts}
                onUpdate={(productId, updates) => {
                  // Handle product updates
                  if (handleProductChange) {
                    console.log('Manual image selection - handling product update');

                    // Handle _imageAnalysis data — store on product for downstream services
                    if (updates._imageAnalysis) {
                      console.log(`Received image analysis data for product ${productId}:`, updates._imageAnalysis.length, 'images analyzed');
                      // Store analysis results on the product object for later use
                      const product = selectedProducts.find(p => p.id === productId);
                      if (product) {
                        product._imageAnalysis = updates._imageAnalysis;
                        console.log(`Stored _imageAnalysis on product ${productId} for downstream services`);
                      }
                    }

                    // Handle the update based on the field
                    if (updates.body_html) {
                      console.log('Manual image selection - handling product update with body_html content');

                      // Get the existing product and its content
                      const product = selectedProducts.find(p => p.id === productId);

                      // CRITICAL: Use the same thorough approach as auto-process mode to find existing content
                      const existingContent = {};

                      // Try multiple ways to find the product description
                      if (product) {
                        console.log(`Manual image selection - checking existing content for product ${product.id}`);

                        // Try all possible places where description might be stored
                        const possibleSources = [
                          product.body_html,
                          product.descriptionHtml,
                          product.description,
                          // Check if there's an HTML description in metafields
                          product.metafields?.find(m => m.key === 'description_html')?.value,
                          // Check for SEO description
                          product.metafields?.find(m => m.key === 'description' && m.namespace === 'seo')?.value
                        ];

                        // Use the first non-empty source
                        const existingDescription = possibleSources.find(source => source && source.trim() !== '');
                        existingContent.body_html = existingDescription;

                        console.log(`Manual image selection - found existing description with length: ${existingContent.body_html.length}`);
                        if (existingContent.body_html) {
                          console.log(`Manual image selection - existing description starts with: ${existingContent.body_html.substring(0, 50)}...`);
                        }
                      }

                      // Create the updates object with the same structure as auto-process mode
                      const updatedFields = {
                        body_html: updates.body_html,
                        insertModes: {}
                      };

                      // For body_html, we always want to use 'add' mode regardless of configuration
                      updatedFields.insertModes.body_html = 'add';
                      console.log(`Manual image selection - setting 'add' mode for body_html field in TemplateTools`);

                      // Format the content with paragraph tags if needed
                      if (updatedFields.body_html && typeof updatedFields.body_html === 'string' && !updatedFields.body_html.includes('<p>')) {
                        updatedFields.body_html = `<p>${updatedFields.body_html}</p>`;
                        console.log(`Manual image selection - formatted body_html content with paragraph tags`);
                      } else if (updatedFields.body_html && typeof updatedFields.body_html !== 'string') {
                        console.log(`Warning: body_html content is not a string: ${typeof updatedFields.body_html}`);
                        // Convert to string properly if it's an object
                        if (typeof updatedFields.body_html === 'object' && updatedFields.body_html !== null) {
                          console.log('Converting object to formatted string for body_html');
                          // Format the object for readability
                          if (updatedFields.body_html.body_html) {
                            // If the object has a body_html property, use that directly
                            updatedFields.body_html = updatedFields.body_html.body_html;
                          } else {
                            // Otherwise, format the entire object for readability
                            updatedFields.body_html = JSON.stringify(updatedFields.body_html, null, 2);
                          }
                        } else {
                          // For other types, use String conversion
                          updatedFields.body_html = String(updatedFields.body_html);
                        }

                        // Add paragraph tags if needed
                        if (!updatedFields.body_html.includes('<p>')) {
                          updatedFields.body_html = `<p>${updatedFields.body_html}</p>`;
                        }
                      }

                      // CRITICAL: Pass the existing content for ALL fields that use 'add' mode
                      if (updatedFields.insertModes.body_html === 'add') {
                        // Initialize _originalContent if it doesn't exist
                        if (!updatedFields._originalContent) {
                          updatedFields._originalContent = {};
                        }

                        // For standard fields, use the existingContent object
                        if (existingContent && existingContent.body_html) {
                          updatedFields._originalContent.body_html = existingContent.body_html;
                          console.log(`Manual image selection - passing existing content directly for body_html: ${existingContent.body_html.substring(0, 50)}...`);
                        }
                      }

                      // Log what we're updating
                      console.log(`Manual image selection - updating product ${productId} with:`, {
                        fields: Object.keys(updatedFields).filter(k => k !== 'insertModes' && k !== '_originalContent' && k !== 'metafields'),
                        insertModes: updatedFields.insertModes,
                        hasOriginalContent: !!updatedFields._originalContent
                      });

                      // Call handleProductChange exactly like in auto-process mode
                      handleProductChange(productId, 'all', updatedFields);
                    } else {
                      // For other fields, use the standard approach
                      handleProductChange(productId, 'all', updates);
                    }
                  }

                  // Record template usage
                  recordTemplateUsage(user?.uid, currentShop?.id, currentImageTemplate.id);
                }}
                onProgress={(progressPercent) => {
                  // Update progress in the parent component - cap at 100%
                  const cappedProgress = Math.min(progressPercent, 100);
                  setProgressSafe(cappedProgress);

                  // Show processing status with progress
                  setIsExecuting(true);
                  setWorkflowExecutionStatus({
                    status: 'processing',
                    message: `Processing image template '${currentImageTemplate.name}'... ${Math.round(cappedProgress)}%`
                  });

                  // If progress is 100%, we're done processing the image template
                  if (cappedProgress >= 100) {
                    console.log('Manual image selection reached 100% - completing image template execution');

                    // Check if there are remaining templates to process
                    if (remainingAfterImage && remainingAfterImage.length > 0) {
                      console.log('Manual image selection - processing remaining templates:', remainingAfterImage);

                      // Set status to indicate we're moving to the next templates
                      setWorkflowExecutionStatus({
                        status: 'processing',
                        message: `Image template '${currentImageTemplate.name}' completed. Processing remaining templates...`
                      });

                      // Close the image template executor modal
                      setShowImageTemplateExecutor(false);

                      // Process the remaining templates after a short delay
                      setTimeout(async () => {
                        try {
                          // Execute the remaining templates
                          console.log('Executing remaining templates after image template:', remainingAfterImage);
                          const remainingResult = await executeTemplatesDirectlySafely(
                            remainingAfterImage,
                            selectedProducts, // Pass the full product objects
                            (status) => {
                              // Scale the progress from 50-100% for the remaining templates
                              const scaledProgress = status.progress ? 50 + (status.progress / 2) : 50;
                              setProgressSafe(scaledProgress);

                              // Update the status message
                              setWorkflowExecutionStatus(status);

                              // If workflow is complete, reset the executing state
                              if (status.status === 'completed') {
                                setTimeout(() => {
                                  setIsExecuting(false);
                                  setProgressSafe(0);
                                  setWorkflowExecutionStatus(null);
                                }, 1500);
                              }
                            }
                          );

                          // Check if execution was stopped due to insufficient credits
                          if (remainingResult === null) {
                            console.log('🚫 Remaining template execution stopped due to insufficient credits');
                            return; // Stop execution
                          }

                          console.log('Remaining templates processed:', remainingResult);
                        } catch (error) {
                          console.error('Error processing remaining templates:', error);
                          setWorkflowExecutionStatus({
                            status: 'error',
                            message: `Error processing remaining templates: ${error.message}`
                          });

                          // Reset execution state on error
                          setTimeout(() => {
                            setIsExecuting(false);
                            setProgressSafe(0);
                          }, 1500);
                        }
                      }, 500);
                    } else {
                      // No remaining templates, just complete the workflow
                      console.log('Manual image selection - no remaining templates, completing workflow');

                      // Set success status
                      setWorkflowExecutionStatus({
                        status: 'success',
                        message: `Image template '${currentImageTemplate.name}' processed successfully.`
                      });

                      // Reset execution state after a short delay
                      setTimeout(() => {
                        console.log('Manual image selection - execution state reset after completion');
                        setIsExecuting(false);
                        setProgressSafe(0);
                        setShowImageTemplateExecutor(false);
                      }, 1500);
                    }
                  }
                }}
                onSaveResults={(results) => {
                  console.log('Saving results to persistent sidebar:', results);
                  // Add results to the persistent sidebar
                  setPersistentResults(prev => {
                    // Create a map of existing results by ID for quick lookup
                    const existingResultsMap = {};
                    prev.forEach(result => {
                      if (result.id) {
                        existingResultsMap[result.id] = true;
                      }
                    });

                    // Filter out duplicates from new results
                    const uniqueNewResults = results.filter(result => {
                      return !result.id || !existingResultsMap[result.id];
                    });

                    console.log(`Adding ${uniqueNewResults.length} unique results to existing ${prev.length} results`);
                    return [...prev, ...uniqueNewResults];
                  });

                  // Show the sidebar
                  setSidebarVisible(true);

                  // CRITICAL: Immediately set execution state to false and progress to 100%
                  // This ensures the loading indicator completes properly
                  console.log('Manual image selection complete - immediately resetting execution state');
                  setIsExecuting(false);
                  setProgressSafe(100); // Ensure progress is complete
                  setWorkflowExecutionStatus({
                    status: 'success',
                    message: `Image template '${currentImageTemplate.name}' processed successfully.`
                  });

                  // Close the modal to ensure UI is updated
                  setShowImageTemplateExecutor(false);

                  // Clear the status after a short delay to prevent it from staying stuck
                  setTimeout(() => {
                    setWorkflowExecutionStatus(null);
                    setProgressSafe(0);
                    console.log('Progress and workflow status reset after manual image selection');
                  }, 1500); // Shorter timeout for better UX
                }}
                onClose={() => setShowImageTemplateExecutor(false)}
              />
            </Modal.Section>
          </Modal>
        )}
      {/* Persistent Results Sidebar - always rendered at the top level */}
      <PersistentResultsSidebar
        results={persistentResults}
        visible={sidebarVisible}
        onToggleVisibility={(visible) => setSidebarVisible(visible)}
        onSave={(editedResults) => {
          console.log('Saving edited results from sidebar:', editedResults);

          // Update products with edited content
          if (handleProductChange) {
            Object.entries(editedResults).forEach(([resultId, fields]) => {
              handleProductChange(resultId, 'all', fields);
            });
          }
        }}
      />

      {/* Template Help Modal */}
      <TemplateHelp
        open={showHelpModal}
        onClose={() => setShowHelpModal(false)}
        onTemplateVariablesClick={() => {
          setShowHelpModal(false);
          setShowTemplateVariablesHelp(true);
        }}
      />

      {/* Template Variables Help Modal */}
      <TemplateVariablesHelp
        open={showTemplateVariablesHelp}
        onClose={() => setShowTemplateVariablesHelp(false)}
      />

      {/* Credits Error Modal */}
      <Modal
        open={showCreditsErrorModal}
        onClose={() => setShowCreditsErrorModal(false)}
        title="❌ Not Enough Credits"
        primaryAction={{
          content: 'Buy Flex Credits',
          onAction: () => {
            setShowCreditsErrorModal(false);
            // Navigate to credits purchase page
            window.location.href = '/app/credits';
          }
        }}
        secondaryActions={[
          {
            content: 'Close',
            onAction: () => setShowCreditsErrorModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Stack vertical spacing="loose">
            {creditsErrorDetails && (
              <>
                <TextContainer>
                  <p><strong>You don't have enough credits to execute these templates.</strong></p>
                  <br />
                  <Stack distribution="fill">
                    <Stack.Item fill>
                      <Text variant="bodyMd">💳 <strong>Required Credits:</strong> {creditsErrorDetails.required}
                      </Text>
                    </Stack.Item>
                    <Stack.Item fill>
                      <Text variant="bodyMd">📊 <strong>Available Credits:</strong> {creditsErrorDetails.available}
                      </Text>
                    </Stack.Item>
                  </Stack>
                  <br />
                  <Banner status="critical">
                    <p><strong>🚫 Missing: {creditsErrorDetails.missing} credits</strong></p>
                  </Banner>
                  <br />
                  <p>You can purchase additional Flex Credits to continue using templates and workflows.</p>
                </TextContainer>
                <Banner status="info">
                  <p>💡 <strong>Pro Tip:</strong> Flex Credits never expire and work with all AI models. You only pay for what you use!</p>
                </Banner>
              </>
            )}
          </Stack>
        </Modal.Section>
      </Modal>
    </div>
  );
}
